var App = App || {};

App.classes = {
	wrappers: {},
	workflows: {},
	components: {},
	ui: {},
};
;// TODO: add getScript support
// TODO: check all interceptions where JQuery is used

/**
 * @typedef {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'|'HEAD'} HttpMethod
 *
 * @typedef {Object} AjaxRequest
 * @property {HttpMethod} [method='GET']
 * @property {string} url
 * @property {any} [body]
 * @property {Object.<string,string>} [headers]
 * @property {'auto'|'json'|'text'|'html'} [dataType='auto']
 * @property {Object.<string,any>} [params]
 * @property {AbortSignal} [signal]
 * @property {number} [timeout]
 * @property {(data:any,res:Response)=>void} [success]
 * @property {(err:any,res:Response|null)=>void} [error]
 * @property {Object} [options]
 *
 * @typedef {Object} AjaxResponse
 * @property {any} data
 * @property {Response} res
 * @property {Function} abort
 * @property {string} requestId
 */

class Ajax extends SingletonMixin() {

	/** @type {{ baseURL:string, headers:Object, credentials:RequestCredentials, timeout:number }} */
	defaults = {
		baseURL: '',
		headers: {'X-Requested-With': 'XMLHttpRequest'},
		credentials: 'same-origin',
		timeout: 0,
	}

	/** @type {{ baseURL:string, headers:Object, credentials:RequestCredentials, timeout:number }} */
	current = {...this.defaults};

	/** @type {{request:Object, response:Object, success:Object, error:Object}} */
	interceptors = {request: {}, response: {}, success: {}, error: {}};

	controllers = new Map();
	_id = 0;

	/** @param {Partial<Ajax['current']>} partial */
	setDefaults(partial) {
		this.current = {...this.current, ...partial};
	}

	useDefaults() {
		this.setDefaults(this.defaults);
	}

	/**
	 * @param {'request'|'response'|'success'|'error'} type
	 * @param {Function} fn
	 * @param {string|number} [code]
	 * @returns {string|number|undefined}
	 */
	useInterceptor(type, fn, code) {
		const key = code ?? (fn.toString().hashCode?.() ?? String(fn));
		this.interceptors[type][key] = fn;
		return key;
	}

	/**
	 * @param {'request'|'response'|'success'|'error'} type
	 * @param {Function|string|number} fnOrCode
	 */
	ejectInterceptor(type, fnOrCode) {
		let key;
		if (typeof fnOrCode === 'function') {
			const maybeHash = fnOrCode.toString().hashCode?.();
			key = typeof maybeHash !== 'undefined' ? maybeHash : String(fnOrCode);
		} else {
			key = String(fnOrCode);
		}
		delete this.interceptors[type][key];
	}

	/** @param {'request'|'response'|'success'|'error'} type
	 *  @param {Object} ctx
	 */
	runInterceptors(type, ctx) {
		const interceptors = this.interceptors[type];
		for (const interceptor of Object.values(interceptors)) {
			try {
				const result = interceptor(ctx);
				if (result?.cancel) {
					throw new DOMException(result.reason || 'Canceled by interceptor', 'AbortError');
				}
				if (result?.redirect && ctx.req) {
					ctx.req.url = result.redirect;
				}
				Object.assign(ctx, result);
			} catch (error) {
				console.error(error);
			}
		}
		return ctx;
	}


	/** @param {string} url
	 * @param {Object} [params]
	 */
	buildURL(url, params) {
		const base = this.current.baseURL && this.current.baseURL.length > 0
			? this.current.baseURL
			: (typeof location !== 'undefined' ? location.origin : undefined);

		const u = new URL(url, base);

		if (params) {
			for (const [k, v] of Object.entries(params)) {
				if (v !== null && v !== undefined) {
					u.searchParams.set(k, String(v));
				}
			}
		}

		return u.toString();
	}

	/** @param {any} body */
	json(body) {
		return {body: JSON.stringify(body), headers: {'Content-Type': 'application/json'}};
	}

	/** @param {AbortSignal[]} signals */
	static anySignal(signals) {
		const valid = signals.filter(Boolean);
		if (valid.length === 0) return undefined;

		const ctrl = new AbortController();
		const onAbort = (e) => {
			const reason = e?.target?.reason ?? e?.reason;
			ctrl.abort(reason);
		};

		for (const s of valid) {
			s.addEventListener('abort', onAbort, {once: true});
		}

		if (valid.some(s => s.aborted)) {
			const first = valid.find(s => s.aborted);
			if (first) ctrl.abort(first.reason);
		}

		return ctrl.signal;
	}

	/** @param {string} text */
	static async parseHTML(text) {
		if (typeof Node !== 'undefined' && typeof Node.from === 'function') {
			return Node.from(text);
		}
		if (typeof DOMParser !== 'undefined') {
			return new DOMParser().parseFromString(text, 'text/html');
		}
		return text;
	}

	/** @param {any} err
	 * @param {Response} [res]
	 */
	static normalizeError(err, res) {
		if ((err && err.name === 'AbortError') || (typeof err === 'string')) {
			return Object.assign(new Error('Request aborted'), {code: 'ABORT_ERR', res});
		}
		if (res && !res.ok) {
			return Object.assign(new Error(`HTTP ${res.status}`), {code: 'HTTP_ERR', res});
		}
		return Object.assign(new Error(err && err.message ? err.message : 'Network error'), {
			code: 'NET_ERR',
			res,
			cause: err
		});
	}

	/** @param {AjaxRequest} args */
	call(args = {}) {
		const {
			type = 'GET',
			headers = {},
			dataType = 'auto',
			timeout = this.current.timeout,
			url,
			body = null,
			params,
			signal,
			success = () => {
			},
			error = () => {
			},
			options = {}
		} = args;

		let req = {
			type: type,
			url: params ? this.buildURL(url, params) : url,
			body,
			headers,
			dataType,
			options
		};

		req = (this.runInterceptors('request', {req}))?.req || req;

		const ctrl = new AbortController();
		const id = String(++this._id);
		this.controllers.set(id, ctrl);

		const combinedSignal = Ajax.anySignal([ctrl.signal, signal]);

		const opts = {
			method: req.type,
			body: req.body,
			headers: {...this.current.headers, ...req.headers},
			credentials: this.current.credentials,
			signal: combinedSignal,
			...req.options
		};

		if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData || opts.body instanceof Blob)) {
			opts.headers['Content-Type'] = opts.headers['Content-Type'] || 'application/json';
			if (opts.headers['Content-Type'] === 'application/json' && typeof opts.body !== 'string') {
				opts.body = JSON.stringify(opts.body);
			}
		}

		const abort = (reason) => ctrl.abort(reason);

		let headerTimeoutId = null;
		if (timeout > 0) {
			headerTimeoutId = setTimeout(() => ctrl.abort(new DOMException('TimeoutError', 'AbortError')), timeout);
		}

		const runner = async () => {
			let res;
			let raw;
			let data;
			try {
				res = await fetch(req.url, opts);

				if (headerTimeoutId) clearTimeout(headerTimeoutId);

				raw = await res.clone().text();
				data = await this._format(res, req.dataType);

				const afterResp = this.runInterceptors('response', {req, res, text: raw, data, requestId: id});
				if (afterResp) {
					if (afterResp.req) req = afterResp.req;
					if (afterResp.res) res = afterResp.res;
					if (typeof afterResp.data !== 'undefined') data = afterResp.data;
				}
				if (res.ok) {
					this.runInterceptors('success', {req, res, text: raw, data, requestId: id});
					try {
						success(data, res);
					} catch (cbErr) {
						console.error(cbErr);
					}
					return {data, res, abort, requestId: id};
				}

				throw Ajax.normalizeError({data}, res);
			} catch (e) {
				const norm = Ajax.normalizeError(e, res);
				this.runInterceptors('error', {req, res: res ?? null, text: raw ?? null, data: data ?? norm, error: norm, requestId: id});
				try {
					error(norm, res ?? null);
				} catch (cbErr) {
					console.error(cbErr);
				}
				throw norm;
			} finally {
				if (headerTimeoutId) clearTimeout(headerTimeoutId);
				this.controllers.delete(id);
			}
		};

		const promise = runner();
		promise.abort = abort;
		promise.requestId = id;
		return promise;
	}


	/** @param {Response} res
	 * @param {'auto'|'json'|'text'|'html'} dataType
	 * */
	async _format(res, dataType) {
		const noContent = res.status === 204 || res.status === 205 || res.status === 304;
		if (noContent) return null;

		if (dataType === 'json') return res.json();
		if (dataType === 'text') return res.text();
		if (dataType === 'html') {
			const txt = await res.text();
			return Ajax.parseHTML(txt);
		}

		const ctRaw = res.headers.get('content-type');
		const ct = ctRaw ? ctRaw.toLowerCase() : '';

		if (ct.includes('application/json')) return res.json();
		if (ct.includes('text/html')) {
			const txt = await res.text();
			return Ajax.parseHTML(txt);
		}
		if (ct.includes('text/')) return res.text();

		try {
			return await res.arrayBuffer();
		} catch {
			return await res.text();
		}
	}

	/** @param {string|number} requestId
	 *  @param {string} [reason]
	 */
	abortById(requestId, reason) {
		const c = this.controllers.get(String(requestId));
		if (c) c.abort(reason);
	}

	/** @param {AjaxRequest} o */
	get(o) {
		return this.call({...o, method: 'GET'});
	}

	/** @param {AjaxRequest} o */
	del(o) {
		return this.call({...o, method: 'DELETE'});
	}

	/** @param {AjaxRequest} o */
	post(o) {
		return this.call({...o, method: 'POST'});
	}

	/** @param {AjaxRequest} o */
	put(o) {
		return this.call({...o, method: 'PUT'});
	}

	/** @param {AjaxRequest} o */
	patch(o) {
		return this.call({...o, method: 'PATCH'});
	}
}

App.classes.ajax = Ajax;
;class DashboardBlockController {
	constructor({host, targetRoot}) {
		this.host = host;
		this.target = targetRoot;
		this._unbind = [];

		this._uid = 'id'+App.helper.random.string(5);
	}

	getAsyncId(name) {
		return `${this._uid}__${name}`;
	}

	isIdleAsync(name) {
		return App.core.async_task.is_idle(this.getAsyncId(name));
	}

	abortAsync(name) {
		App.core.async_task.abort(this.getAsyncId(name));
	}

	ajaxAsync(name, options) {
		return App.core.async_task.ajax({
			...options,
			id: this.getAsyncId(name),
		});
	}

	$(sel, root = this.target) {
		return root?.querySelector(sel) || null;
	}

	$$(sel, root = this.target) {
		return Array.from(root?.querySelectorAll(sel) || []);
	}

	get header() {
		return this.$('.dashboard__block__header');
	}

	get body() {
		return this.$('.dashboard__block__body') || this.$('.dashboard__block_columns');
	}

	get loader() {
		let el = this.$('.dashboard__block__loader');
		if (!el) {
			el = document.createElement('div');
			el.className = 'dashboard__block__loader';
			el.innerHTML = `<ws-icon name="worksection_bold-solid"></ws-icon><div class="loader"></div>`;
			(this.body || this.target).prepend(el);
		}
		return el;
	}

	init() {
		this.initTabs();
		this.initMoreBtn();

		this.$$('[data-do-filter]').forEach((element) => {
			element.addEventListener('click', (e) => {
				e.preventDefault();
				this.doFilter(
					element,
					element.hasAttribute('data-do-filter-full'),
					element.hasAttribute('data-do-filter-resize')
				);
			});
		});

		this.initScrolls();
		this.initMenus();
		this.initClicks();
		this.initBlocks();

		const dash = this.host.closest('.dashboard');
		if (dash && !App.device.is_mobile()) {
			this.initDrag();
		}

		App.core.call_queue.add(`dashboard_block_${this.host.getAttribute('id')}`, () => {
			this.host.closest('.dashboard')?.removeClass('dashboard-loading');
		}, this, null, 1);
	}


	initDrag() {
		$(this.host).closest('.dashboard')
			.find('.dashboard__row__dragLine')
			.bind('mousedown.page touchstart.page', App.controls.dashboard.ontouchstart_height);
		$(document)
			.bind('mouseup.page touchend.page', App.controls.dashboard.ontouchend_height)
			.bind('mousemove.page touchmove.page', App.controls.dashboard.ontouchmove_height);
		this._unbind.push(() => {
			$(this.host).closest('.dashboard')
				.find('.dashboard__row__dragLine')
				.unbind(('mousedown.page touchstart.page', App.controls.dashboard.ontouchstart_height));
			$(document)
				.unbind('mouseup.page touchend.page', App.controls.dashboard.ontouchend_height)
				.unbind('mousemove.page touchmove.page', App.controls.dashboard.ontouchmove_height);
		});
	}

	initBlocks() {
		const fn = this.host.getAttribute('data-init-function');
		if (fn) eval(fn);
	}

	initMenus() {
		this.$$('[data-menu-id]').forEach((value) => {
			const newMenuId = `${this.host.getAttribute('id')}-${value.getAttribute('data-menu-id')}${this.isModalTarget() ? '-modal' : ''}`;
			document.getElementById(newMenuId)?.remove();

			const menu = this.target.querySelector(`#${value.getAttribute('data-menu-id')}.menu`)
				|| this.host.querySelector(`#${value.getAttribute('data-menu-id')}.menu`);
			if (!menu) return;

			menu.setAttribute('id', newMenuId);
			menu.setAttribute('data-block-id', this.host.getAttribute('id'));
			value.setAttribute('data-menu-id', newMenuId);

			value.addMultipleEventListener(['click', 'dblclick', 'contextmenu'], (e) => {
				if ((value.getAttribute('name') || '').includes('btn-disabled')) return false;

				if (newMenuId.includes('extra')) {
					App.controls.dashboard.init_extra_menu($(this.host), newMenuId);
				}
				if (e.type === 'click') {
					App.click_listener.dblclick.call(() => {
						App.menu.show({
							anchor: value,
							box: '#' + newMenuId,
							type: 'mymenu',
							force_dropdown: true,
							onhide: function () {
								if (App.controls.dashboard.block_selected_by_extra) {
									document.querySelectorAll('.dashboard__block')
										.removeClass('dashboard__block-selected', 'route-updown', 'route-leftright');
									App.controls.dashboard.block_selected_by_extra = false;
								}
							}
						});
					});
				}
				if (e.type === 'dblclick') {
					App.click_listener.dblclick.stop(true);
					App.controls.dashboard.extra_dblclick_action(newMenuId);
				}
				return false;
			});
		});
	}

	initScrolls(defaultScroll) {
		this.$$('.scrollZone').forEach((scrollZone) => {
			if (!scrollZone.classList.contains('ps')) App.page.scrollBars.push(App.controls.js_scrollbars.init(scrollZone));
			if (typeof defaultScroll !== 'undefined') scrollZone.scrollTo({top: defaultScroll});

			scrollZone.addMultipleEventListener(["ps-scroll-up", "ps-scroll-down"], () => {
				if (this.target.querySelector('.dashboard__block__header:not(.dashboard__block__header-bottom)')) {
					if (this.target.querySelector('.scrollZone')?.classList?.contains('scrollZone-top')) {
						this.header?.classList?.add('dashboard__block__header-bottomDynamic');
					} else {
						this.header?.classList?.remove('dashboard__block__header-bottomDynamic');
					}
				}
			});
		});
		// this.setupSafeBottom();
	}

	initClicks() {
		if (this.target.__offInitClicks) this.target.__offInitClicks();
		this.target.__offInitClicks = App.ui.onMouse(this.target, '.sidePanel__list__item-task', {
			onBefore(e) {
				e.data.id_task = $(e.el).attr('data-task');
				e.data.url = App.helper.url.task(e.data.id_task);
			},
			click: e => App.task_view.show({url: e.data.url, anchor: e.el}),
			open: e => App.core.new_window(e.data.url)
		});
	}

	initTabs() {
		if (this.target.__offInitTabs) this.target.__offInitTabs();
		this.target.__offInitTabs = App.ui.onMouse(this.target, '.tabs__item', {
			click: e => this.onTabClick(e.el)
		});
	}

	initMoreBtn() {
		if (this.target.__offInitMoreBtn) this.target.__offInitMoreBtn();
		this.target.__offInitMoreBtn = App.ui.onMouse(this.target, '.dashboard__block__more ws-btn', {
			click: e => this.onMore(e.el)
		});
	}

	withLoader(name, run) {
		const body = this.body, header = this.header, loader = this.loader;
		const show = () => {
			if (!body || !header || !loader) return;
			const br = body.getBoundingClientRect(), hr = header.getBoundingClientRect();
			loader.style.height = Math.max(0, br.height - 2) + 'px';
			loader.style.top = this.isModalTarget() ? 0 : hr.height + 'px';
			loader.style.display = 'block';
		};
		const hide = () => {
			if (!loader) return;
			if (this.isIdleAsync(name)) {
				loader.style.display = 'none';
			}
		};

		show();
		try {
			const r = run();

			if (r && typeof r.always === 'function') {
				r.always(hide);
			} else if (r && typeof r.finally === 'function') {
				r.finally(hide);
			} else {
				queueMicrotask(hide);
			}
			return r;
		} catch (e) {
			hide();
			throw e;
		}
	}


	composeUrl(full, {withoutFilter, withoutType, height} = {}) {
		const base = App.helper.endpoint.ajax();
		const params = new URLSearchParams();
		const seed = App.actions.get?.(this.host.getAttribute('data-action'));
		if (seed) {
			(seed instanceof URLSearchParams ? seed : new URLSearchParams(String(seed)))
				.forEach((v, k) => params.append(k, v));
		}
		const add = (k, v) => {
			if (v !== undefined && v !== null && String(v) !== '') params.set(k, String(v));
		};
		const addData = (el, da, qk) => el?.getAttribute && add(qk, el.getAttribute(da));

		add('height', height ?? $(this.host).height?.());

		const body = this.body;
		if (!withoutType) {
			addData(body, 'data-type', 'type');
		}
		addData(body, 'data-limit', 'limit');

		addData(this.host, 'data-grid-name', 'grid_name');
		addData(this.host, 'data-project', 'id_project');
		this.isModalTarget() && add('is_from_modal', 1);

		if (!withoutFilter) {
			addData(body, 'data-sel-user', 'sel_user');
			addData(body, 'data-sel-project', 'sel_project');
			addData(body, 'data-selection', 'selection');
			addData(body, 'data-order', 'order');
		}
		body.getAttributeNames().forEach((name) => {
			if (name.startsWith('data-custom-')) {
				const key = name.substring('data-custom-'.length);
				addData(body, name, key);
			}
		});
		!full && add('is_body_only', 1);
		return `${base}?${params.toString()}`;
	}

	onTabClick(target) {
		if (target.classList.contains('active') && this.loader && this.loader.offsetParent !== null) return false;

		const type = target.getAttribute('data-type');
		let hadScroll = false;
		this.body?.querySelectorAll(':scope > div').forEach((el) => {
			if (!el.isHidden?.() && el.querySelector('.scrollZone')?.classList.contains('ps--active-y')) hadScroll = true;
		});

		this.body?.setAttribute('data-type', type);
		const url = this.composeUrl(false);
		const hide_all = () => this.body?.querySelectorAll(':scope > div').forEach(d => d.hide?.());
		this.$$('.tabs__item').forEach(t => t.classList.toggle('active', Boolean(t.getAttribute('data-type') === type)));

		const div = this.$(`#${this.host.getAttribute('id')}-${type}`);
		if (div) {
			this.abortAsync('getTabContent');
			hide_all();
			div.show?.();
			$.ajax({type: 'POST', url: url + '&save_settings=1&save_tab=1'});
		} else {
			this.withLoader('getTabContent', () => {
				return this.ajaxAsync('getTabContent', {
					type: 'POST',
					url: url + '&save_settings=1',
					success: (html) => {
						hide_all();
						this.body?.append(...Node.from(html));
						if (!hadScroll) {
							this.removeBlockScroll()
						}
						this.initScrolls();
						App.bus.app.emit('block_mounted', {}, {block: this.host});
					},
				});
			});
		}
		return false;
	}

	onMore(targetBtn) {
		if ((targetBtn.getAttribute('name') || '').includes('btn-disabled')) return false;
		const body = this.body;
		if (!body) return false;

		const more = targetBtn.closest('.dashboard__block__more');
		if (more.hasAttribute('data-fake')) {
			this.target.querySelectorAll('.dashboard__block__item-none').forEach(n => n.removeClass('dashboard__block__item-none'));
			more.hide();
			return false;
		}
		const limit = more?.getAttribute('data-limit');
		const type = body.getAttribute('data-type');

		targetBtn.setAttribute('name', `${targetBtn.getAttribute('name') || ''} btn-disabled`);
		body.setAttribute('data-limit', limit);

		this.ajaxAsync('getTabContent', {
			type: 'GET',
			url: this.composeUrl(false),
			success: (html) => {
				const sel = `#${this.host.getAttribute('id')}-${type}`;
				const holder = this.target.querySelector(sel) || document.querySelector(sel);
				const zone = holder?.querySelector('.scrollZone');
				const tmp = (zone && zone.scrollHeight) ? (zone.scrollHeight - zone.clientHeight) : 0;
				if (holder) holder.replaceWith(...Node.from(html));
				this.initScrolls(tmp);
				App.bus.app.emit('block_mounted', {}, {block: this.host});
			}
		});
		return false;
	}

	doFilter(elem, full, resize) {
		App.menu.hide(true);

		const text = elem.getAttribute('data-text');
		const order = elem.getAttribute('data-order');
		const selection = elem.getAttribute('data-selection');
		const selUser = elem.getAttribute('data-sel-user');
		const selProject = elem.getAttribute('data-sel-project');
		const customSettings = {};
		elem.getAttributeNames().forEach((name) => {
			if (name.startsWith('data-custom-')) {
				const key = name.substring('data-custom-'.length);
				customSettings[key] = elem.getAttribute(name);
			}
		})


		const body = this.body;
		if (!body) return;

		let hadScroll = false;
		body.querySelectorAll(':scope > div').forEach((el) => {
			if (!el.isHidden?.() && el.querySelector('.scrollZone')?.classList.contains('ps--active-y')) hadScroll = true;
		});

		if (order != null) body.setAttribute('data-order', order);
		if (selection != null) body.setAttribute('data-selection', selection);
		if (selUser != null) body.setAttribute('data-sel-user', selUser);
		if (selProject != null) body.setAttribute('data-sel-project', selProject);
		Object.keys(customSettings).forEach((key) => {
			body.setAttribute(`data-custom-${key}`, customSettings[key]);
		});
		this.withLoader('getTabContent', () => {
			return this.ajaxAsync('getTabContent', {
				type: 'POST',
				url: this.composeUrl(!!full) + '&save_settings=1' + (resize ? '&resize_reload=1' : ''),
				success: (nodes) => {
					nodes = Node.from(nodes);
					if (full) {
						const newBody = nodes[0].querySelector('.dashboard__block__body') || nodes[0];
						const newHeader = nodes[0].querySelector('.dashboard__block__header');
						if (this.isModalTarget()) {
							this.body?.replaceWith(newBody);
							if (newHeader && this.header) {
								this.header.replaceWith(newHeader);
							}
							this.init();
						} else {
							const block = nodes[0];
							this.host.replaceWith(block);
							block.init?.();
							App.page.blockObjs = document.querySelectorAll('.dashboard__block[data-container]');
						}
					} else {
						const idPart = this.host.getAttribute('id');
						body.querySelectorAll(`[id*="${CSS.escape(idPart)}"]`).forEach(n => n.remove());
						body.append(...nodes);
						if (!hadScroll) {
							this.removeBlockScroll()
						}
					}
					const menu = elem.closest('.menu');
					if (menu) {
						menu.querySelectorAll('.menu__item').forEach(i => i.classList.remove('menu__item-active'));
						elem.classList.add('menu__item-active');
						if (text) this.$(`[data-menu-id="${menu.id}"]`)?.setAttribute('text', text);
					}
					App.controls.dashboard.run_operation({operation: RELOAD_MENU_OPERATION, data: []});
				}
			});
		});
	}

	refresh() {
		this.withLoader('getTabContent', () => {
			return this.ajaxAsync('getTabContent', {
				type: 'POST',
				url: this.composeUrl(true, {withoutFilter: true, withoutType: true}),
				success: (html) => {
					html = Node.from(html);
					if (this.isModalTarget()) {
						const newBody = html.find(n => n?.classList?.contains('dashboard__block__body')) || html[0];
						this.body?.replaceWith(newBody);
						this.initScrolls();
					} else {
						const block = html[0];
						this.host.replaceWith(block);
						block.init?.();
					}
				},
			});
		});
	}

	/**
	 * TODO rename, cause its not real modal now
	 */
	isModalTarget() {
		return !!(this.target.closest?.('.dashboardView') || this.target.querySelector?.('#modal__headerContent'));
	}

	dispose() {
		this._unbind.forEach(fn => fn());
		this._unbind.length = 0;
	}

	removeBlockScroll() {
		// в модалці 2-3 сторінки прогружаються зразу
		if (this.isModalTarget()) return;
		const body = this.body;
		const limit = body.getAttribute('data-limit');
		const itemsSelector = this.host.getItemsSelector({isModalTarget: this.isModalTarget()});
		const itemHeight = Math.max(...Array.from(
			this.$$(itemsSelector).filter((el) => !el.closest('.scrollZone').isHidden())
		).map((el) => el.outerHeight));

		if (itemHeight) {
			const more = Array.from(body.querySelectorAll('.dashboard__block__more'))
				.filter(el => !el.closest('.scrollZone').isHidden())[0];
			let countItems = Math.floor((body.height - (more?.outerHeight || 0)) / itemHeight);
			let removeMoreHidden = false;
			if (limit - countItems < 10) {
				this.$$(itemsSelector)
					.filter((el) => !el.closest('.scrollZone').isHidden())
					.forEach(function (value, index, array) {
						if (index >= countItems) {
							value.classList.add('dashboard__block__item-none');
							removeMoreHidden = true;
						}
					});
				if (removeMoreHidden && more) {
					more.classList.remove('hidden');
				}
			}
		}
		// this.setupSafeBottom();
	}

	// css fix: замінив на padding-bottom у scrollZone
	// setupSafeBottom() {
	// 	if (this.isModalTarget()) {
	// 		this.$$('safe-bottom').forEach((el) => el.classList.remove('safe-bottom'));
	// 		const body = this.body;
	// 		const itemsSelector = this.host.getItemsSelector({isModalTarget: this.isModalTarget()});
	// 		const items = this.$$(itemsSelector).filter((el) => !el.closest('.scrollZone').isHidden());
	// 		const more = Array.from(body.querySelectorAll('.dashboard__block__more'))
	// 			.filter(el => !el.closest('.scrollZone').isHidden())[0];
	// 		const allItems = [...items, more].filter((el) => el && !el.isHidden() && !el.closest('.scrollZone').isHidden());
	// 		const lastItem = allItems[allItems.length - 1];
	// 		lastItem.classList.add('safe-bottom');
	// 	}
	// }
}

App.classes.dashboard_block_controller = DashboardBlockController;

;class DashboardBlockControllerTarget {
	/** @type {HTMLElement|null} */
	host = null;
	/** @type {HTMLElement|null} */
	target = null;
	/** @type {DashboardBlockController|null} */
	controller = null;
	/** @type {function(DashboardBlockControllerTarget)|null} */
	mounted = null;
	/** @type {function(DashboardBlockControllerTarget)|null} */
	unmounted = null;

	constructor({
		beforeMount = null,
		mounted = null,
		beforeUnmount = null,
		unmounted = null,
				} = {}) {
		this.beforeMount = beforeMount;
		this.mounted = mounted;
		this.beforeUnmount = beforeUnmount;
		this.unmounted = unmounted;
	}

	isMounted() { return !!this.target; }


	mount(host, target) {
		if (this.isMounted()) this.unmount();
		this.beforeMount?.(this);
		this.host = host;
		this.target = target;

		this.controller = new DashboardBlockController({ host: host, targetRoot: target });
		this.controller.init();
		this.mounted?.(this);

	}

	unmount() {
		if (!this.isMounted()) return;
		this.beforeUnmount?.(this);
		if (this.controller) {
			this.controller.dispose?.();
			this.controller = null;
		}
		this.target = null;
		this.host = null;
		this.unmounted?.(this);
	}

}

App.classes.dashboard_block_controller_target = DashboardBlockControllerTarget;

;class GlobalEventHub extends SingletonMixin(){

	/** @type {WeakMap<EventTarget, Map<string, { listener: Function, handlers: Set<Function> }>>} */
	_byTarget = new WeakMap();


	/**
	 * @param {EventTarget}                       target
	 * @param {string}                            eventName
	 * @param {Function}                          handler
	 * @param {boolean|AddEventListenerOptions}   [options=false]
	 */
	register(target, eventName, handler, options = false) {
		const key = this._slotKey(eventName, options);

		if (key === null) {
			target.addEventListener(eventName, handler, options);
			return;
		}

		const slots = this._slotsFor(target);
		let   slot  = slots.get(key);

		if (!slot) {
			const listener = (event) => { for (const h of slot.handlers) h(event); };
			slot = { listener, handlers: new Set() };
			slots.set(key, slot);
			target.addEventListener(eventName, listener, options);
		}

		slot.handlers.add(handler);
	}

	/**
	 * @param {EventTarget}                       target
	 * @param {string}                            eventName
	 * @param {Function}                          handler
	 * @param {boolean|AddEventListenerOptions}   [options=false]
	 */
	unregister(target, eventName, handler, options = false) {
		const key = this._slotKey(eventName, options);

		if (key === null) {
			target.removeEventListener(eventName, handler, options);
			return;
		}

		const slots = this._byTarget.get(target);
		if (!slots) return;

		const slot = slots.get(key);
		if (!slot) return;

		slot.handlers.delete(handler);

		if (slot.handlers.size === 0) {
			target.removeEventListener(eventName, slot.listener, options);
			slots.delete(key);
			if (slots.size === 0) this._byTarget.delete(target);
		}
	}


	/** @param {EventTarget} target */
	_slotsFor(target) {
		let slots = this._byTarget.get(target);
		if (!slots) this._byTarget.set(target, (slots = new Map()));
		return slots;
	}

	/**
	 *
	 * @param {string}                          eventName
	 * @param {boolean|AddEventListenerOptions} options
	 * @returns {string|null}
	 */
	_slotKey(eventName, options) {
		if (!options)         return eventName;
		if (options === true) return `${eventName}\x00c`;

		const { capture = false, passive = false, once = false } = options;
		if (once) return null;

		return `${eventName}\x00${capture ? 'c' : ''}${passive ? 'p' : ''}`;
	}
}
;/**
 * @typedef {Object} FormInstanceOptions
 * @property {Object} [initialValues={}] - The initial values of the form fields.
 * @property {(values: Object) => Object} [validate] - A function that takes the current form values and returns an object of validation errors.
 * @property {(values: Object) => Promise<void>} [onSubmit] - An async function that is called when the form is submitted with valid data.
 */

class FormInstance {
	/**
	 * @param {FormInstanceOptions} options
	 */
	constructor({
					initialValues = {},
					validate = null,
					onSubmit = null,
				} = {}) {
		this._initialValues = structuredClone(initialValues);
		this.values = structuredClone(initialValues);

		this.#errors = {};
		this.touched = {};
		this.dirty = {};

		this.submitCount = 0;
		this.isSubmitting = false;

		this._validate = validate;
		this._onSubmit = onSubmit;

		/** @type {Set<Function>} */
		this._subscribers = new Set();

		/** @type {Map<string, Set<Function>>} */
		this._fieldSubscribers = new Map();

		/** @type {Set<Function>} */
		this._validateSubscribers = new Set();

	}

	#errors = {};


	subscribe(listener) {
		this._subscribers.add(listener);
		return () => this._subscribers.delete(listener);
	}

	subscribeField(fieldPath, listener) {
		if (!this._fieldSubscribers.has(fieldPath)) this._fieldSubscribers.set(fieldPath, new Set());
		this._fieldSubscribers.get(fieldPath).add(listener);
		return () => {
			const set = this._fieldSubscribers.get(fieldPath);
			if (!set) return;
			set.delete(listener);
			if (set.size === 0) this._fieldSubscribers.delete(fieldPath);
		};
	}

	subscribeValidate(listener) {
		this._validateSubscribers.add(listener);
		return () => this._validateSubscribers.delete(listener);
	}

	unsubscribeValidate(listener) {
		this._validateSubscribers.delete(listener);
	}

	_emit(changedFieldPath = null) {
		for (const listener of this._subscribers) listener();

		if (changedFieldPath) {
			const set = this._fieldSubscribers.get(changedFieldPath);
			if (set) for (const listener of set) listener();
		}
	}

	getValue(fieldPath) {
		return App.helper.object.readByPath(this.values, fieldPath);
	}

	setValue(fieldPath, nextValue, { touch = true, validate = true } = {}) {
		App.helper.object.setByPath(this.values, fieldPath, nextValue);

		if (touch) this.touched[fieldPath] = true;

		const initialValue = App.helper.object.readByPath(this._initialValues, fieldPath);
		this.dirty[fieldPath] = !FormInstance._shallowEqual(nextValue, initialValue);

		if (validate) this.validate();

		this._emit(fieldPath);
	}

	isDirty(fieldPath = null) {
		if (fieldPath) {
			return !!this.dirty[fieldPath];
		}
		return Object.values(this.dirty).some(Boolean);
	}

	validate() {
		if (!this._validate) {
			this.#errors = {};
			return this.errors;
		}
		const nextErrors = this._validate(this.values) || {};
		this.#errors = nextErrors;
		this._validateSubscribers.forEach(subscriber => subscriber(this.errors))
		return nextErrors;
	}

	get isValid() {
		return !this.errors || Object.keys(this.errors).length === 0;
	}

	async submit() {
		this.submitCount += 1;

		this.validate();
		this._emit(null);

		if (!this.isValid) return { ok: false, errors: this.errors };
		if (!this._onSubmit) return { ok: true };

		this.isSubmitting = true;
		this._emit(null);

		try {
			await this._onSubmit(this.values);
			return { ok: true };
		} finally {
			this.isSubmitting = false;
			this._emit(null);
		}
	}

	reset(nextInitialValues = null) {
		const initial = nextInitialValues ?? this._initialValues;

		this._initialValues = structuredClone(initial);
		this.values = structuredClone(initial);

		this.#errors = {};
		this.touched = {};
		this.dirty = {};

		this.submitCount = 0;
		this.isSubmitting = false;

		this._emit(null);
	}

	useValidation(callback) {
		this._validate = callback;
		return this;
	}

	get errors() {
		if (this.submitCount > 0) {
			return this.#errors;
		}
		const onlyDirty = {};
		Object.keys(this.#errors).forEach(key => {
			if (this.isDirty(key)) {
				onlyDirty[key] = this.#errors[key];
			}
		})
		return onlyDirty;
	}

	static _shallowEqual(a, b) {
		return a === b;

	}
}

;App.classes = App.classes || {};

App.classes.input_command_menu = class InputCommandMenu {

	text = '';
	recording = false;
	type = '';

	constructor({
					input,
					menu,
					onApply
				}) {
		this.input = $(input);
		this.menu = $(menu);
		this.onApply = onApply;
		this.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		this.input
			.on('focus', (e, data = {}) => this.apply())
			.on('blur', (e, data = {}) => {
				this.apply();
			})
			.on('focusout', (e, data = {}) => {
				this.recording = false;
			})
			.on('keydown', (e, data = {}) => {
				if (!App.device.is_mobile()) return this.handleKeyDown(e, data);
			});
			// .on('input', (e, data = {}) => {
			// 	if (App.device.is_mobile()) return this.handleInput(e, data);
			// });
	}

	hide() {
		if (App.menu.in_editor) {
			this.clearInput();
			App.menu.hide_all();
			this.input.focus();
		}
	}

	reset() {
		this.text = '';
		this.recording = false;
		this.type = '';
	}

	clearInput() {
		const value = this.input.val();
		const caretPos = this.input[0].selectionStart;
		const pos = value.lastIndexOf(this.type, caretPos - this.text.length - 1);
		if (pos !== -1) {
			this.input.val(value.slice(0, pos) + value.slice(pos + this.text.length + 1));
			this.input[0].setSelectionRange(pos, pos);
		}
		this.reset();
	}

	apply() {
		this.onApply?.(this.text, this.menu);
	}

	handleKeyDown(e, data = {}) {
		const key = e.key || '';
		const keyCode = e.which || e.keyCode || e.charCode;

		switch (true) {

			case keyCode === App.keycode.Escape && App.menu.in_editor:
				App.menu.hide();
				return false;

			case key === '/':
				this.reset();
				this.type = '/';
				this.openMenu(data.onshow);
				this.recording = true;
				break;

			case keyCode === App.keycode.Space && App.menu.in_editor:
				App.menu.hide();
				return true;

			case keyCode === App.keycode.Backspace:
				if (this.text && this.recording) {
					this.text = this.text.slice(0, -1);
				} else if (App.menu.in_editor && this.recording) {
					App.menu.hide();
					this.reset();
				}
				break;

			case [App.keycode.ArrowRight, App.keycode.ArrowLeft].includes(keyCode):
				if (this.recording) {
					e.preventDefault();
					if (App.keyboard_selection.go_within_container(keyCode === App.keycode.ArrowLeft ? -1 : 1, true)) return true;
				}
				break;

			case [App.keycode.ArrowUp, App.keycode.ArrowDown].includes(keyCode):
				if (this.recording) {
					return;
				}
				break;

			default:
				if (key.length === 1 && this.recording) {
					this.text += key.toLowerCase();
					this.input.trigger('change');
				}
				break;
		}

		App.menu.in_editor && this.apply();
	}

	handleInput(e, data = {}) {
		const key = e.originalEvent.data || e.key || '';

		switch (true) {

			case key === '/':
				this.reset();
				this.type = '/';
				this.openMenu(data.onshow);
				this.recording = true;
				break;

			default:
				if (key.length === 1 && this.recording) {
					this.text += key.toLowerCase();
					this.input.trigger('change');
				}
				break;
		}
		App.menu.in_editor && this.apply();

	}

	openMenu(onshow) {
		const rect = $(this.input).getCaretCoordinates().rect;
		App.menu.show({
			anchor: this.input,
			box: this.menu,
			text_position: {
				x: rect.x,
				y: rect.y,
			},
			onshow,
			onhide: () => {
				this.reset();
				this.input.focus();
			},
		});
		App.menu.in_editor = true;
		this.input.focus();
	}
}
;App.classes = App.classes || {};


class ScheduleView extends SingletonMixin(HasRefsMixin()) {
	container = null;
	limits = null;
	lastDate = null;
	taskId = null;
	loadedDays = 0;
	userIdTo = null;
	userIdFrom = null;
	idProject = null;
	saveQueue = {};
	/**
	 * @type {function(...[*]): void}
	 */
	flushQueueFn = null;

	constructor({container, limits, lastDate, taskId, userIdTo, userIdFrom, projectId}) {
		super();
		this.container = container;
		this.limits = limits ? App.helper.parseTimeToMinutes(limits) : null;
		this.lastDate = lastDate;
		this.taskId = taskId;
		this.userIdTo = userIdTo;
		this.userIdFrom = userIdFrom;
		this.idProject = projectId;
		this.flushQueueFn = App.helper.debounce(() => {
			this.flushSaveQueue();
		}, 300);
		this.setupCallbacks();
	}


	get refsContainer() {
		return this.container;
	}

	get scheduleLines() {
		return this.refs('schedule-date-line');
	}

	get presetsValue() {
		return this.presets.its_value;
	}

	get presets() {
		return this.ref('presets');
	}

	get pie() {
		return this.ref('hours-pie');
	}

	getPresetMinutes(dateLine) {
		const value = this.presetsValue;
		switch (value) {
			case "max":
				return dateLine.timepicker.maxMinutes;
			case "flexible":
				return 60;
			default:
				return (parseInt(value || 0) || 0) * 60;
		}
	}

	setupCallbacks(elems = null) {
		(elems || this.scheduleLines).forEach(dateLine => {
			if (dateLine.nodeName !== 'SCHEDULE-DATE-LINE') {
				return;
			}
			dateLine.addEventListener('add-time', ({detail: {addedMinutes}}) => {
				this.handleAddTime(dateLine, addedMinutes);
			});

			dateLine.addEventListener('remove-time', ({detail: {removedMinutes}}) => {
				this.handleRemoveTime(dateLine, removedMinutes);
			});

			dateLine.addEventListener('checked', () => {
				this.handleChecked(dateLine);
			});
			dateLine.addDelegatedEventListener("click", 'schedule-date-progressbar', () => {
				dateLine.checkbox.toggle();
			});

			dateLine.addEventListener('unchecked', () => {
				this.handleUnchecked(dateLine);
			});

			dateLine.addEventListener('change', ({detail}) => {
				this.handleChange(dateLine, detail);
			});

			dateLine.addEventListener('save', () => {
				this.saveDateLine(dateLine);
			});

			dateLine.addEventListener('time-control-btn-clicked', () => {
				this.presets.its_value = 'flexible';
			});
		});
		if (!elems) {
			this.ref('main-checkbox').addEventListener('change', () => this.handleMainCheckbox());
			this.ref('more-btn').addEventListener('click', () => this.loadMore());
			this.presets.addEventListener('change', () => this.presets.its_value !== 'flexible' && this.applyPreset());
		}
	}


	handleAddTime(dateLine, addedMinutes) {
		dateLine.progressbar.addSelectedNew(addedMinutes);
		this.pie?.track(addedMinutes);
	}

	handleRemoveTime(dateLine, removedMinutes) {
		dateLine.progressbar.removeSelectedNew(removedMinutes);
		this.pie?.untrack(removedMinutes);
	}

	handleChecked(dateLine) {
		this.enableDateLine(dateLine);
		if (!dateLine.timepicker.totalMinutes) {
			dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
		} else {
			this.pie?.track(dateLine.timepicker.totalMinutes);
			this.updateTimeTracked();
		}
		dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleUnchecked(dateLine) {
		this.disableDateLine(dateLine);
		if (dateLine.timepicker.totalMinutes) {
			this.pie?.untrack(dateLine.timepicker.totalMinutes);
		}
		this.updateTimeTracked();
		dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleChange(dateLine, detail) {
		if (!detail || !('newTotalMinutes' in detail)) return;
		const {newTotalMinutes, oldTotalMinutes} = detail;
		newTotalMinutes > 0 ? dateLine.checkbox.check(true) : dateLine.checkbox.uncheck(true);
		this.updateTimeTracked();
		this.enableDateLine(dateLine);

		oldTotalMinutes !== newTotalMinutes && dateLine.dispatchEvent(new Event('save', {bubbles: true, composed: true, cancelable: false}));
	}

	handleMainCheckbox() {
		let remainingRest = Math.max(this.limits - this.getMinutesTracked(), 0);
		this.scheduleLines.forEach(dateLine => {
			if (this.ref('main-checkbox').checked) {
				if (this.limits && remainingRest > 0) {
					if (!dateLine.timepicker.totalMinutes || !dateLine.timepicker.checked) {
						dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
						dateLine.checkbox.check()
					}
					remainingRest -= dateLine.timepicker.totalMinutes;
				} else if (!this.limits) {
					if (!dateLine.timepicker.totalMinutes || !dateLine.timepicker.checked) {
						dateLine.timepicker.setTotalMinutes(this.getPresetMinutes(dateLine));
						dateLine.checkbox.check()
					}
				}
			} else {
				dateLine.checkbox.uncheck();
			}
		});
		this.updateTimeTracked();

	}



	updateTimeTracked() {
		const minutes = this.getMinutesTracked();
		const {hours, minutes: mins} = App.helper.parseTime(minutes);
		this.ref('time-tracked').innerText = `+${hours}:${mins.toString().padEnd(2, '0')}`;
	}


	getMinutesTracked() {
		return [...this.scheduleLines].reduce((total, dateLine) => {
			return dateLine.checkbox.checked ? total + dateLine.timepicker.totalMinutes : total;
		}, 0);
	}

	loadMore() {
		const lastDate = this.lastDate.toString().length === 13 ? Math.floor(this.lastDate / 1000) : this.lastDate;
		$.ajax({
			type: 'GET',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:show') + '&from_date=' + lastDate + '&only_body=1',
			success: (response) => {
				$(response).each((_, line) => {
					const lineElem = $(line)[0];
					this.ref('days-list').appendChild(lineElem);
					this.setupCallbacks([lineElem]);
				})
			}
		});

		const newDate = App.helper.date.from_timestamp(this.lastDate);
		newDate.setDate(newDate.getDate() + 14);
		this.lastDate = newDate.getTime();
		this.ref('week-counter').innerText = `${App.helper.date.get_week_number(newDate)} - ${App.helper.date.get_week_number(newDate) + 1}`;

		this.loadedDays += 14;
		if (this.loadedDays >= 90) {
			this.ref('more-btn').style.display = 'none';
		}
	}

	saveDateLine(dateLine) {
		this.saveQueue[dateLine.date] = {
			id_user_to: this.userIdTo,
			is_on: App.helper.parseBoolean(dateLine.checkbox.checked) ? 1 : 0,
			time: App.helper.minutesToFraction(dateLine.timepicker.totalMinutes),
			dt: dateLine.date
		};
		this.flushQueueFn();
	}

	flushSaveQueue() {
		const values = Object.values(this.saveQueue);
		this.saveQueue = {};
		if (!values.length) {
			return;
		}
		if (values.length === 1) {
			return this.saveSingleDateLine(values[0]);
		} else {
			return this.saveBulkDateLines(values);
		}
	}

	saveBulkDateLines(values) {
		$.ajax({
			type: 'POST',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:do_bulk'),
			data: {records: values},
			success: (response) => {
				eval(response)
			}
		});
	}

	saveSingleDateLine(data) {
		$.ajax({
			type: 'POST',
			url: App.helper.endpoint.task(this.taskId) + '?' + App.actions.get('task_schedule:do'),
			data,
			success: (response) => {
				eval(response)
			}
		});
	}

	selectUser(e, anchor) {
		const _this = this;
		App.controls.user_select_and_go.show_context(anchor || e.currentTarget, App.helper.url.add_query(
			App.helper.endpoint.project(this.idProject), App.actions.get('user_select_and_go:show')), {
			backup: true,
			request: {
				type: 'task_schedule',
				template: 'menu',
				assign_user_id: this.userIdFrom,
				select_user_id: this.userIdTo,
			},
			onload() {
				$(this).on('click', '.menu__item', function () {
					App.modal.show({
						url: App.helper.endpoint.task(_this.taskId) + '?' + App.actions.get('task_schedule:show') + '&id_user_to' + '=' + $(this).data('id'),
						reload: true
					})

				})
			}
		});
	}

	disableDateLine(dateline) {
		dateline.progressbar.disable();
		dateline.timepicker.disable();

	}

	enableDateLine(dateline) {
		dateline.progressbar.enable();
		dateline.timepicker.enable();
	}

	applyPreset() {
		this.scheduleLines.forEach(dateLine => {
			const value = this.getPresetMinutes(dateLine);
			if (dateLine.timepicker.totalMinutes) {
				const previousChecked = dateLine.checkbox.checked;
				dateLine.timepicker.setTotalMinutes(value);
				if (!previousChecked) {
					dateLine.checkbox.uncheck();
				}
			}
		});
		this.updateTimeTracked();
	}

}

App.classes.schedule_view = ScheduleView;
;App.classes = App.classes || {};

class TaskContextMenu extends EventSubscriberMixin() {

	/**
	 * @type {App.classes.TaskContextMenuComponentsContainer}
	 */
	context_menu = null;
	input_command_menu = null;

	constructor({
					components = [],
					input = null,
				}) {
		super();
		this.components = components;
		this.input = input;
		this.init();
	}

	init() {
		this.initContextMenu();
		this.bindSelfEvents();
		this.initInputMenuCommand();
	}

	initContextMenu() {
		this.context_menu = new App.classes.task_context_menu_components_container();
		this.components.map((i) => this.context_menu.addComponent(this.initComponent(i)));
	}

	initInputMenuCommand() {
		this.input_command_menu = new App.classes.input_command_menu({
			input: this.input,
			menu: this.context_menu?.element(),
			onApply(text, menu) {
				App.context_search.menu.apply(text, menu);
			}
		})
		this.on('task_context_menu.component.selected', () => {
			this.close();
		});
		this.on('task_context_menu.component.cleared', () => {
			this.close();
		});
	}

	close() {
		this.input_command_menu.hide();
	}

	initComponent(item) {
		const component = item.config
			? App.classes.base_task_context_menu_component.createFromConfig(item.config)
			: App.classes.base_task_context_menu_component.createByType(item.type);

		if (item.hasOwnProperty('canBeRemoved')) {
			component.canBeRemoved = item.canBeRemoved;
		}
		if (item.hasOwnProperty('counter')) {
			component.counter = item.counter;
		}
		component.selected = item.selected || false;
		component.disabled = item.disabled || false;
		this.bindComponentEvents(component);
		return component;
	}

	bindComponentEvents(component) {
		component.element().on('click', (e) => {
			if (component.disabled) return;
			this.emit('task_context_menu.component.show', component);
		});
		component.removeBtn().on('click', (e) => {
			e.stopPropagation();
			if (component.disabled) return;
			this.emit('task_context_menu.component.clear', component);
		});
	}

	bindSelfEvents() {
		this.on('task_context_menu.component.selected', (type, data = {}) => {
			const component = this.context_menu.getComponent(type);

			component.selected = true;

			if (component.hasCounter) {
				const counter = data?.counter;
				component.counter = counter ?? 0;
			}
		});
		this.on('task_context_menu.component.cleared', (type) => {
			const component = this.context_menu.getComponent(type);

			component.selected = false;

			if (component.hasCounter) {
				component.counter = 0;
			}
		});

		this.on('task_context_menu.component.disabled', (type, state) => {

			const component = this.context_menu.getComponent(type);

			component.disabled = Boolean(state);
		});
	}

}

App.classes.task_context_menu = TaskContextMenu

;App.classes = App.classes || {};

App.classes.base_task_context_menu_component = class BaseTaskContextMenuComponent {

	constructor({container, icon, text, type, canBeRemoved, group, hasCounter}) {
		this.container = container;
		this.icon = icon;
		this.text = text;
		this.type = type;
		this.canBeRemoved = canBeRemoved;
		this.group = group;
		this.hasCounter = hasCounter;
	}

	_element;
	_selected = false;
	_disabled = false;
	_counter = 0;

	render() {
		return App.helper.template.fill('task_context_menu_item', {
			icon: this.icon,
			text: this.text,
			container: this.container,
			canBeRemoved: this.canBeRemoved,
			hasCounter: this.hasCounter,
			counter: this._counter,
			isCounterActive: this.hasCounter && this._counter > 0
		});
	}

	element() {
		return this._element || (this._element = $(this.render()));
	}

	removeBtn() {
		return this.element().find('ws-btn[data-role=remove]');
	}

	counterElement() {
		return this.element().find('.counter');
	}

	get selected() {
		return this._selected;
	}

	set selected(value) {
		this._selected = value;
		this.element().toggleClass('menu__item-active', value);
	}

	get disabled() {
		return this._disabled;
	}

	set disabled(value) {
		this._disabled = value;
		this.element().toggleClass('menu__item-disabled', value);
		this.removeBtn().attr('disabled', value);
	}

	get counter() {
		return this._counter;
	}

	set counter(value) {

		if (!this.hasCounter) return;

		const parsedValue = parseInt(value, 10) || 0;
		this._counter = parsedValue;

		const $counter = this.counterElement();
		$counter.text(parsedValue);
		this.element().toggleClass('menu__item--hasCounter', parsedValue > 0);
	}

	static createByType(type) {
		if (!configs()[type]) throw new Error(`Unknown component type: ${type}`);

		return BaseTaskContextMenuComponent.createFromConfig({ type, ...configs()[type] });
	}

	static createFromConfig(config) {
		return new BaseTaskContextMenuComponent({
			type: config.type,
			container: config.container,
			icon: config.icon,
			text: config.text,
			canBeRemoved: config.canBeRemoved !== false,
			group: config.group ?? 'main',
			hasCounter: !!config.hasCounter,
			disabled: !!config.disabled
		});
	}
}

const configs = () => ({
	project: {
		icon: 'project_sm',
		text: App.object_state.locale.task_menu.project,
		container: 'project',
		group: 'header',
	},
	parent: {
		icon: 'parent',
		text: App.object_state.locale.task_menu.parent,
		container: 'parent',
		group: 'header',
	},
	terms: {
		icon: 'calendar_date',
		text: App.object_state.locale.task_menu.terms,
		container: 'terms'
	},
	priority: {
		icon: 'priority-circle',
		text: App.object_state.locale.task_menu.priority,
		container: 'priority'
	},
	assignee: {
		icon: 'person',
		text: App.object_state.locale.task_menu.assignee,
		container: 'assignee'
	},
	stage: {
		icon: 'task_workflow/started-50',
		text: App.object_state.locale.task_menu.stage,
		container: 'stage'
	},
	labels: {
		icon: 'tag',
		text: App.object_state.locale.task_menu.labels,
		container: 'labels'
	},
	limits: {
		icon: 'clockface',
		text: App.object_state.locale.task_menu.limits,
		container: 'limits'
	},
	subscribers: {
		icon: 'notifications',
		text: App.object_state.locale.task_menu.subscribers,
		container: 'subscribers',
		canBeRemoved: false,
		group: 'footer',
		hasCounter: true
	},
	visibility: {
		icon: 'lock_open_alt:lock',
		text: App.object_state.locale.task_menu.visibility,
		container: 'hidden',
		group: 'footer'
	},
});

;App.classes = App.classes || {};

App.classes.task_context_menu_components_container = class TaskContextMenuComponentsContainer {

	/**
	 * @type {{[componentType: string]: BaseTaskContextMenuComponent}}
	 */
	components = {};

	_element = null;

	/**
	 * @param {BaseTaskContextMenuComponent} component
	 */
	addComponent(component) {
		this.components[component.type] = component;
	}

	getComponent(type) {
		return this.components[type];
	}


	render() {
		const container = $(App.helper.template.fill('task_context_menu'));
		for (const key in this.components) {
			const component = this.components[key];
			let componentsContainer;
			switch (component.group) {
				case 'header':
					componentsContainer = container.find('[data-container=header_components]');
					break;
				case 'footer':
					componentsContainer = container.find('[data-container=footer_components]');
					break;
				case 'main':
				default:
					componentsContainer = container.find('[data-container=main_components]');
					break;
			}
			componentsContainer.append(component.element());
		}
		return container;
	}

	element() {
		return this._element || (this._element = this.render());
	}


}
;class UIStateManager extends EventSubscriberMixin(SingletonMixin()) {

	static CATEGORIES = Object.freeze({
		DEFAULT: 'default',
		MOBILE: 'mobile'
	});

	constructor({state = {}} = {}) {
		super();
		this._state = state;
	}

	_state = {};

	_requestedStateCategories = {};
	_requestStateDebounce = App.helper.debounce(() => {
		this._requestState()
	});

	_stateToSave = {};
	_saveStateDebounce = App.helper.debounce(() => {
		this._saveState()
	}, 100)

	getState(key, category = 'default') {
		return this._state[category]?.[key];
	}

	setState(key, value, category = 'default') {
		this.setStateSilent(key, value, category, false);
		this.saveState(key, value, category);
	}

	setStateSilent(key, value, category = 'default', withSync = true) {
		if (!this._state[category]) {
			this._state[category] = {};
		}
		this._state[category][key] = value;
		withSync && this.syncStateToOtherTabs();
	}

	mergeStateSilent(state) {
		for (const category in state) {
			for (const key in state[category]) {
				if (!this._state[category]) {
					this._state[category] = {};
				}
				this._state[category][key] = state[category][key];
			}
		}
	}

	loadState({
				  category = 'default',
				  onLoaded = () => {
				  }
			  } = {}) {
		if (this._state[category]) {
			onLoaded(this._state[category]);
			return;
		}
		let onLoadedCalls = [onLoaded];
		if (this._requestedStateCategories[category]) {
			onLoadedCalls = this._requestedStateCategories[category].onLoadedCalls.concat(onLoaded);
		}
		this._requestedStateCategories[category] = {onLoadedCalls};
		this._requestStateDebounce();
	}

	_requestState() {
		const categories = Object.keys(this._requestedStateCategories);
		if (categories.length === 0) return;
		$.ajax({
			url: App.helper.endpoint.ajax() + '?'
				+ App.actions.get('ui_state:load'),
			type: 'POST',
			data: {
				categories: categories,
			},
			success: (data) => {
				for (const category of categories) {
					this._state[category] = {...(this._state[category] || {}), ...(data[category] || {})};
					const {onLoadedCalls} = this._requestedStateCategories[category];
					onLoadedCalls.forEach(cb => cb(this._state[category]));
					delete this._requestedStateCategories[category];
				}
			}
		});
	}

	saveState(key, value, category = 'default') {
		if (!this._stateToSave[category]) {
			this._stateToSave[category] = {};
		}
		this._stateToSave[category][key] = value;
		this._saveStateDebounce();
	}

	_saveState() {
		const data = this._stateToSave;
		this._stateToSave = {};
		$.ajax({
			url: App.helper.endpoint.ajax() + '?'
				+ App.actions.get('ui_state:save'),
			type: 'POST',
			data: {
				state: data,
			},
			success: () => {
				this.syncStateToOtherTabs();
			}
		});
	}

	syncStateToOtherTabs() {
		App.core.send_message_to_other_tabs({ command: 'ui_state', data: { state: this._state } });
	}

}

App.classes.ui_state_manager = UIStateManager;
;class QueryCodec {
	/**
	 * @param {string} str
	 * @returns {Object}
	 * */
	static parse(str) {
		if (!str) return {};
		if (str[0] === '?') str = str.slice(1);
		const out = {};
		for (const pair of str.split('&')) {
			if (!pair) continue;
			const eq = pair.indexOf('=');
			const key = App.helper.url.safe_decode(eq === -1 ? pair : pair.slice(0, eq));
			const value = App.helper.url.safe_decode(eq === -1 ? '' : pair.slice(eq + 1));
			QueryCodec.#assign(out, QueryCodec.#bracketed(key), value);
		}
		return out;
	}

	/**
	 * @param {Object} data
	 * @returns {string}
	 * */
	static stringify(data) {
		return App.helper.object.toFormUrlEncoded(data);
	}

	/**
	 * @param {string} key
	 * @returns {string[]}
	 * */
	static #bracketed(key) {
		const out = [];
		let i = 0, buf = '';
		while (i < key.length && key[i] !== '[') buf += key[i++];
		if (buf) out.push(buf);
		while (i < key.length) {
			if (key[i++] !== '[') continue;
			buf = '';
			while (i < key.length && key[i] !== ']') buf += key[i++];
			i++;
			out.push(buf);
		}
		return out;
	}

	/**
	 * @param {Object|Array} target
	 * @param {string[]} keys
	 * @param {*} value
	 * */
	static #assign(target, keys, value) {
		let cur = target;
		for (let i = 0; i < keys.length - 1; i++) {
			const k = keys[i];
			const nextIsPush = keys[i + 1] === '';
			if (cur[k] == null) cur[k] = nextIsPush ? [] : {};
			cur = cur[k];
		}
		const last = keys.at(-1);
		if (last === '' && Array.isArray(cur)) cur.push(value);
		else cur[last] = value;
	}
}


class Query {
	/** @type {Record<string,*>} */
	data = {};

	/** @param {Query|Object|string} [input] */
	constructor(input) {
		this.data = Query.#dataOf(input);
	}

	/**
	 * @param {string|string[]} path
	 * @returns {*}
	 * */
	get(path) {
		return Array.isArray(path)
			? Query.#walk(this.data, path)
			: App.helper.object.readByPath(this.data, path);
	}

	/**
	 * @param {string|string[]} path
	 * @param {*} value
	 * @returns {this}
	 * */
	set(path, value) {
		if (Array.isArray(path)) Query.#deepSet(this.data, path, value);
		else App.helper.object.setByPath(this.data, path, value);
		return this;
	}

	/**
	 * @param {string|string[]} path
	 * @returns {boolean}
	 * */
	has(path) {
		let cur = this.data;
		for (const k of Query.#pathArr(path)) {
			if ((!App.helper.object.is_plain(cur) && !Array.isArray(cur)) || !(k in cur)) return false;
			cur = cur[k];
		}
		return true;
	}

	/**
	 * @param {string|string[]} path
	 * @returns {this}
	 * */
	delete(path) {
		const keys = Query.#pathArr(path);
		if (!keys.length) return this;
		let cur = this.data;
		for (let i = 0; i < keys.length - 1; i++) {
			cur = cur?.[keys[i]];
			if (cur == null) return this;
		}
		delete cur[keys.at(-1)];
		return this;
	}

	/**
	 * @param {Query|Object|string} other
	 * @returns {this}
	 * */
	merge(other) {
		App.helper.object.deep_append(this.data, Query.#dataOf(other));
		return this;
	}

	clone() {
		return new Query(this);
	}

	toJSON() {
		return this.data;
	}

	toString() {
		return QueryCodec.stringify(this.data);
	}

	/**
	 * @param {Query|Object|string} input
	 * @returns {Object}
	 * */
	static #dataOf(input) {
		if (input instanceof Query) return structuredClone(input.data);
		if (typeof input === 'string') return QueryCodec.parse(input);
		if (App.helper.object.is_plain(input)) return structuredClone(input);
		return {};
	}

	/**
	 * @param {string|string[]} path
	 * @returns {string[]}
	 * */
	static #pathArr(path) {
		if (Array.isArray(path)) return path.map(String);
		if (typeof path !== 'string') return [String(path)];
		return path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.');
	}

	static #walk(data, keys) {
		let cur = data;
		for (const k of keys) {
			if (cur == null) return undefined;
			cur = cur[k];
		}
		return cur;
	}

	static #deepSet(target, keys, value) {
		let cur = target;
		for (let i = 0; i < keys.length - 1; i++) {
			const k = keys[i];
			if (cur[k] == null) cur[k] = Number.isInteger(+keys[i + 1]) ? [] : {};
			cur = cur[k];
		}
		cur[keys.at(-1)] = value;
	}
}


class URLParser {
	/**
	 * @param {string} input
	 * @returns {{parts: URLParts, errors: string[]}}
	 * */
	static parse(input) {
		const errors = [];
		/** @type {URLParts} */
		const parts = {};
		if (typeof input !== 'string') {
			errors.push('not a string');
			return {parts, errors};
		}

		let s = input.trim();
		if (!s) return {parts, errors};

		s = URLParser.#takeFragment(s, parts);
		s = URLParser.#takeQuery(s, parts);
		s = URLParser.#takeScheme(s, parts);
		s = URLParser.#promoteAuthority(s);
		s = URLParser.#takeAuthority(s, parts, errors);
		URLParser.#takePath(s, parts);

		return {parts, errors};
	}

	static detect(input) {
		if (!input) return {parts: {}, errors: []};
		const t = input.trim();
		if (t[0] === '?') return {parts: {query: new Query(t.slice(1))}, errors: []};
		if (t[0] === '#') return {parts: {fragment: t.slice(1)}, errors: []};
		if (!t.includes('/') && !t.includes('://') && t.includes('=')) {
			return {parts: {query: new Query(t)}, errors: []};
		}
		return URLParser.parse(t);
	}

	static #takeFragment(s, parts) {
		const i = s.indexOf('#');
		if (i === -1) return s;
		parts.fragment = s.slice(i + 1);
		return s.slice(0, i);
	}

	static #takeQuery(s, parts) {
		const i = s.indexOf('?');
		if (i === -1) return s;
		parts.query = new Query(s.slice(i + 1));
		return s.slice(0, i);
	}

	static #takeScheme(s, parts) {
		const m = /^([a-zA-Z][a-zA-Z0-9+\-.]*):/.exec(s);
		if (!m) return s;
		if (/^\d+(\/|$)/.test(s.slice(m[0].length))) return s;
		parts.scheme = m[1];
		return s.slice(m[0].length);
	}

	static #promoteAuthority(s) {
		if (!s) return s;
		if (s.startsWith('//') || s.startsWith('/') || s.startsWith('./') || s.startsWith('../')) return s;
		const slash = s.indexOf('/');
		const first = slash === -1 ? s : s.slice(0, slash);
		return URLParser.#looksLikeHost(first) ? '//' + s : s;
	}

	static #looksLikeHost(seg) {
		if (!seg) return false;
		if (seg.startsWith('[') && seg.includes(']')) return true;
		const colon = seg.lastIndexOf(':');
		if (colon !== -1 && /^\d+$/.test(seg.slice(colon + 1))) return true;
		return seg.includes('.') && !seg.startsWith('.') && !seg.endsWith('.');
	}

	static #takeAuthority(s, parts, errors) {
		if (!s.startsWith('//')) return s;
		parts.hasAuthority = true;
		s = s.slice(2);
		const slash = s.indexOf('/');
		const auth = slash === -1 ? s : s.slice(0, slash);
		const rest = slash === -1 ? '' : s.slice(slash);
		URLParser.#splitAuthority(auth, parts, errors);
		return rest;
	}

	static #takePath(s, parts) {
		if (!s) return;
		if (s[0] === '/') s = s.slice(1);
		if (s) parts.path = s.split('/');
	}

	static #splitAuthority(auth, parts, errors) {
		const at = auth.lastIndexOf('@');
		let hostPort = auth;
		if (at !== -1) {
			parts.userinfo = auth.slice(0, at);
			hostPort = auth.slice(at + 1);
		}
		if (!hostPort) return;

		if (hostPort.startsWith('[')) {
			const close = hostPort.indexOf(']');
			if (close === -1) {
				errors.push('unclosed ipv6 bracket');
				parts.host = hostPort;
				return;
			}
			parts.host = hostPort.slice(0, close + 1);
			const tail = hostPort.slice(close + 1);
			if (tail.startsWith(':')) URLParser.#takePort(tail.slice(1), parts, errors);
			return;
		}

		const colon = hostPort.lastIndexOf(':');
		if (colon !== -1 && /^\d+$/.test(hostPort.slice(colon + 1))) {
			parts.host = hostPort.slice(0, colon);
			parts.port = Number(hostPort.slice(colon + 1));
		} else {
			parts.host = hostPort;
		}
	}

	static #takePort(str, parts, errors) {
		const n = Number(str);
		if (Number.isFinite(n)) parts.port = n;
		else errors.push('invalid port');
	}
}

class URLBuilder {
	scheme = '';
	userinfo = '';
	host = '';
	/** @type {number|null} */
	port = null;
	/** @type {string[]} */
	path = [];
	/** @type {Query} */
	query = new Query();
	fragment = '';
	hasAuthority = false;
	/** @type {string[]} */
	errors = [];


	/** @typedef {'override'|'merge'|'patch'} ApplyStrategy */
	static APPLY_STRATEGIES = Object.freeze({
		patch(target, parts) {
			if (parts.path !== undefined) target.path = [...parts.path];
			if (parts.query !== undefined) target.query.merge(parts.query);
		},
		override(target, parts) {
			if (parts.path !== undefined) target.path = [...parts.path];
			if (parts.query !== undefined) target.query = new Query(parts.query);
		},
		merge(target, parts) {
			if (parts.path !== undefined) target.path = [...target.path, ...parts.path];
			if (parts.query !== undefined) target.query.merge(parts.query);
		},
	});

	static SCALAR_FIELDS = ['scheme', 'userinfo', 'host', 'port', 'fragment', 'hasAuthority'];


	/** @param {string|URLParts|URLBuilder} [input] */
	constructor(input) {
		const {parts, errors} = URLBuilder.#partsOf(input);
		this.errors = errors;
		this.#assign(parts);
	}

	static parse(input) {
		return new URLBuilder(input);
	}

	get valid() {
		return this.errors.length === 0;
	}

	/**
	 * @param {string|URLParts|URLBuilder} input
	 * @param {{strategy?: ApplyStrategy}} [opts]
	 * @returns {this}
	 */
	apply(input, {strategy = 'patch'} = {}) {
		const apply = URLBuilder.APPLY_STRATEGIES[strategy];
		if (!apply) throw new Error(`Unknown strategy: ${strategy}`);

		const {parts} = URLBuilder.#partsOf(input, {detect: true});
		this.#assignScalars(parts);
		apply(this, parts);
		return this;
	}


	/**
	 * @param {String} action
	 * @return {URLBuilder}
	 */
	useAction(action) {
		return this.apply(App.actions.get(action));
	}

	clone() {
		return new URLBuilder(this);
	}

	/** @returns {URLParts} */
	toParts() {
		return {
			scheme: this.scheme,
			userinfo: this.userinfo,
			host: this.host,
			port: this.port,
			path: [...this.path],
			query: structuredClone(this.query.data),
			fragment: this.fragment,
			hasAuthority: this.hasAuthority,
		};
	}

	toString() {
		const out = [];
		if (this.scheme) out.push(this.scheme, ':');
		if (this.#hasAuth()) {
			if (this.scheme) out.push('//');
			if (this.userinfo) out.push(this.userinfo, '@');
			out.push(this.host);
			if (this.port != null) out.push(':', String(this.port));
		}
		if (this.path.length) out.push('/', this.path.join('/'));
		const q = this.query.toString();
		if (q) out.push('?', q);
		if (this.fragment) out.push('#', this.fragment);
		return out.join('');
	}

	#hasAuth() {
		return this.hasAuthority || !!this.host || !!this.userinfo || this.port != null;
	}

	/** @param {URLParts} parts */
	#assign(parts) {
		this.#assignScalars(parts);
		if (parts.path !== undefined) this.path = [...parts.path];
		if (parts.query !== undefined) this.query = new Query(parts.query);
	}

	/** @param {URLParts} parts */
	#assignScalars(parts) {
		for (const k of URLBuilder.SCALAR_FIELDS) if (parts[k] !== undefined) this[k] = parts[k];
	}

	/** @returns {{parts: URLParts, errors: string[]}} */
	static #partsOf(input, {detect = false} = {}) {
		if (input instanceof URLBuilder) return {parts: input.toParts(), errors: []};
		if (typeof input === 'string') return detect ? URLParser.detect(input) : URLParser.parse(input);
		if (App.helper.object.is_plain(input)) return {parts: input, errors: []};
		return {parts: {}, errors: []};
	}
}

/**
 * @typedef {{
 *   scheme?: string, userinfo?: string, host?: string, port?: number|null,
 *   path?: string[], query?: Query|Object|string,
 *   fragment?: string, hasAuthority?: boolean,
 * }} URLParts
 *
 * @typedef {'override'|'merge'} ApplyStrategy
 */


;class AttributeObserver {
	/**
	 * @param {HTMLElement} hostElement
	 * @param {Object} [options]
	 * @param {AttributeModifierRunner} [options.runModifier]
	 */
	constructor(hostElement, {runModifier} = {}) {
		/** @type {HTMLElement} */
		this._hostElement = hostElement;

		/** @type {AttributeModifierRunner | undefined} */
		this._runModifier = runModifier;

		/** @type {Object<string, ObserveAttributeOptions>} */
		this._runtimeObservedAttributes = Object.create(null);
	}

	/**
	 * @param {string} attributeName
	 * @param {ObserveAttributeOptions} [options]
	 * @returns {void}
	 */
	observeAttribute(attributeName, options = {}) {
		this._runtimeObservedAttributes[attributeName] = options;
	}

	/**
	 * @param {string} attributeName
	 * @returns {boolean}
	 */
	isObserved(attributeName) {
		return Boolean(this._runtimeObservedAttributes[attributeName]);
	}

	/**
	 * @param {string} attributeName
	 * @param {*} value
	 * @returns {*}
	 */
	applyModifiers(attributeName, value) {
		const options = this._runtimeObservedAttributes[attributeName];
		if (!options) return value;

		const modifiers = options.modifiers || [];
		let result = value;

		for (const modifierName of modifiers) {
			result = this._runModifier(modifierName, result);
		}

		return result;
	}

	/** @returns {void} */
	parseObserveAttributesAttribute() {
		const raw = this._hostElement.getAttribute('observe-attributes');
		if (!raw) return;

		raw.split(',')
			.map(part => part.trim())
			.filter(Boolean)
			.forEach(part => {
				const [namePart, modifiersPart] = part.split('|').map(s => s.trim());
				if (!namePart) return;

				const modifiers = modifiersPart
					? modifiersPart.split(',').map(s => s.trim()).filter(Boolean)
					: [];

				this.observeAttribute(namePart, {modifiers});
			});
	}
}

/**
 * @typedef {Object} ObserveAttributeOptions
 * @property {string[]} [modifiers]
 */

/**
 * @callback AttributeModifierRunner
 * @param {string} modifierName
 * @param {*}      value
 * @returns {*}
 */
;class BindingFrame {
	/**
	 * @param {Node}                target
	 * @param {RenderableComponent} component
	 */
	constructor(target, component) {
		/** @type {Node} */
		this.$target = target;
		/** @type {RenderableComponent} */
		this.$component = component;
	}
}

class EventFrame {
	/**
	 * @param {Node}                target
	 * @param {RenderableComponent} component
	 */
	constructor(target, component) {
		/** @type {Node} */
		this.$target = target;
		/** @type {RenderableComponent} */
		this.$component = component;
		/** @type {Event | null} */
		this.$event = null;
	}

	/**
	 * @param {Event} event
	 * @returns {this}
	 */
	fire(event) {
		this.$event = event;
		return this;
	}

	/** @returns {void} */
	release() {
		this.$event = null;
	}
}

App.classes.binding_frame = BindingFrame;
App.classes.event_frame = EventFrame;

class BindingManager {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 * @param {Object}                            [options]
	 * @param {DirectiveRegistry}                 [options.directiveRegistry]
	 */
	constructor(hostElement, {
		directiveRegistry,
	} = {}) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {DirectiveRegistry} */
		this._directiveRegistry = directiveRegistry;

		/** @type {BindingTracker} */
		this._tracker = new BindingTracker();

		/** @type {Binding[]} */
		this._bindings = [];

		/** @type {Map<Node, Binding[]>} */
		this._bindingsByElement = new Map();

		/** @type {Set<Element> | null} */
		this._hydratedNodes = null;
	}

	/**
	 * @param {Element} node
	 * @returns {void}
	 */
	markHydrated(node) {
		(this._hydratedNodes ??= new Set()).add(node);
	}

	/** @returns {void} */
	rebuildAll() {
		this.disposeAll();
		this._collectBindings(this._hostElement, true, null);
		this._hydratedNodes = null;
	}

	/**
	 * @param {Object<string, *>}  context
	 * @param {Set<string> | null} [dirtyKeys=null]  — `null` = force-update all bindings
	 * @param {boolean}            [skipTracking=false]
	 * @returns {void}
	 */
	updateAll(context, dirtyKeys = null, skipTracking = false) {
		const tracker = this._tracker;
		const forceAll = dirtyKeys === null;

		for (let bi = 0; bi < this._bindings.length; bi++) {
			const binding = this._bindings[bi];
			if (!binding || typeof binding.update !== 'function' || binding._disposed) continue;
			if (!forceAll && binding.deps !== null && !this._isBindingDirty(binding, dirtyKeys)) continue;

			const scope = binding.ephemeral?.context ?? context;
			const frame = binding.frame;

			if (skipTracking) {
				try {
					binding.update(scope, frame);
				} catch (error) {
					console.error('Error updating binding', binding, error);
				}
				continue;
			}

			const trackedScope = tracker.wrapScope(scope);
			tracker.beginTracking(binding.deps);
			let threw = false;
			try {
				binding.update(trackedScope, frame);
			} catch (error) {
				console.error('Error updating binding', binding, error);
				threw = true;
			} finally {
				binding.deps = threw ? null : tracker.endTracking();
			}
		}

		this._clearEphemeralDirty();
	}

	/** @returns {void} */
	sleepAll() {
		for (let i = 0; i < this._bindings.length; i++) {
			this._bindings[i].sleep?.();
		}
	}

	/** @returns {void} */
	wakeupAll() {
		for (let i = 0; i < this._bindings.length; i++) {
			this._bindings[i].wakeup?.();
		}
	}

	/**
	 * @param {PlanEntry[]}         plan
	 * @param {Element}             root
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	executePlan(plan, root, ephemeral = null) {
		const len = plan.length;
		/** @type {(Element | null)[]} */
		const nodes = new Array(len);
		for (let pi = 0; pi < len; pi++) {
			nodes[pi] = this._resolveByPath(root, plan[pi].path);
		}

		for (let pi = 0; pi < len; pi++) {
			const node = nodes[pi];
			if (!node) continue;

			this._directiveRegistry.processElementFromPlan({
				entry: plan[pi],
				node,
				component: this._hostElement,
				addBinding: (binding, to = node) => this._addBinding(to, binding, ephemeral),
				ephemeral,
				bindingManager: this,
				makeFrame: (target) => new BindingFrame(target, this._hostElement),
				makeEventFrame: (target) => new EventFrame(target, this._hostElement),
			});
		}
	}

	/**
	 * @param {Element}  root
	 * @param {number[]} path
	 * @returns {Element | null}
	 */
	_resolveByPath(root, path) {
		let node = root;
		for (let i = 0; i < path.length; i++) {
			node = node.children?.[path[i]] ?? null;
			if (!node) return null;
		}
		return node;
	}

	/**
	 * @returns {WeakMap<EphemeralScope, Set<string>> | null}
	 */
	_flushEphemeralDirty() {
		/** @type {WeakMap<EphemeralScope, Set<string>> | null} */
		let map = null;
		for (let i = 0; i < this._bindings.length; i++) {
			const binding = this._bindings[i];
			if (!binding?.ephemeral) continue;
			if (!map) map = new WeakMap();
			if (map.has(binding.ephemeral)) continue;
			map.set(binding.ephemeral, binding.ephemeral.flushDirty());
		}
		return map;
	}

	/**
	 * @param {Binding}     binding
	 * @param {Set<string>} parentDirty
	 * @returns {boolean}
	 */
	_isBindingDirty(binding, parentDirty) {
		const ephemeral = binding.ephemeral;
		for (const dep of binding.deps) {
			if (parentDirty.has(dep) || ephemeral?.hasDirty(dep)) return true;
		}
		return false;
	}

	/** @returns {void} */
	_clearEphemeralDirty() {
		/** @type {Set<EphemeralScope> | null} */
		let seen = null;
		for (let i = 0; i < this._bindings.length; i++) {
			const ephemeral = this._bindings[i]?.ephemeral;
			if (!ephemeral) continue;
			(seen ??= new Set()).add(ephemeral);
		}
		seen?.forEach(e => e.clearDirty());
	}


	/** @returns {void} */
	disposeAll() {
		for (const binding of this._bindings) {
			this._disposeBinding(binding);
		}
		this._bindings = [];
		this._bindingsByElement.clear();
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @returns {void}
	 */
	disposeInRange(startAnchor, endAnchor) {
		/** @type {Set<Binding>} */
		const bindingsToRemove = new Set();
		for (const node of this._iterateRange(startAnchor, endAnchor)) {
			this._disposeNodeBindings(node, bindingsToRemove);
		}
		this._removeDisposedFromList(bindingsToRemove);
	}

	/**
	 * @param {Node}                startAnchor
	 * @param {Node}                endAnchor
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	collectInRange(startAnchor, endAnchor, ephemeral = null) {
		for (const element of this._iterateTopLevelElementsBetween(startAnchor, endAnchor)) {
			this._collectBindings(element, false, ephemeral);
		}
	}

	/**
	 * @param {Node}                rootNode
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {Number}
	 */
	collectInSubtree(rootNode, ephemeral = null) {
		return this._collectBindings(rootNode, false, ephemeral);
	}

	/**
	 * @param {Node} rootNode
	 * @returns {void}
	 */
	disposeInSubtree(rootNode) {
		/** @type {Set<Binding>} */
		const bindingsToRemove = new Set();
		for (const node of this._iterateSubtree(rootNode)) {
			this._disposeNodeBindings(node, bindingsToRemove);
		}
		this._removeDisposedFromList(bindingsToRemove);
	}

	/**
	 * @param {Node}                rootNode
	 * @param {boolean}             isHostRoot
	 * @param {EphemeralScope|null} ephemeral
	 * @returns {Number}
	 */
	_collectBindings(rootNode, isHostRoot, ephemeral) {
		let addedCount = 0;
		/** @type {AddBindingFn} */
		const addBinding = (binding, to = rootNode) => {
			addedCount++;
			this._addBinding(to, binding, ephemeral);
		}
		const host = this._hostElement;
		const makeFrame = (target) => new BindingFrame(target, host);
		const makeEventFrame = (target) => new EventFrame(target, host);

		/**
		 * @param {Node}    node
		 * @param {boolean} isRootElement
		 */
		const walk = (node, isRootElement) => {
			if (!(node instanceof Element)) return;
			if (this._hydratedNodes?.has(node)) return;

			const {shouldSkipChildren} = this._directiveRegistry.processElement({
				element: node,
				component: this._hostElement,
				addBinding,
				customPrefix: (isRootElement && isHostRoot) ? 'ws-host-' : 'ws-',
				ephemeral,
				bindingManager: this,
				makeFrame,
				makeEventFrame,
			});

			if (!isRootElement && this._isBoundaryComponent(node)) return;
			if (shouldSkipChildren) return;
			if (isRootElement && !isHostRoot && this._isBoundaryComponent(node)) return;
			const snapshot = Array.from(node.children);
			for (let i = 0; i < snapshot.length; i++) walk(snapshot[i], false);
		};

		walk(rootNode, true);
		return addedCount;
	}

	/**
	 * @param {Node}                node
	 * @param {Binding}             binding
	 * @param {EphemeralScope|null} [ephemeral=null]
	 * @returns {void}
	 */
	_addBinding(node, binding, ephemeral = null) {
		if (ephemeral) binding.ephemeral = ephemeral;
		binding.element = node;
		binding.deps = null;
		if (!binding.frame) binding.frame = new BindingFrame(node, this._hostElement);

		this._bindings.push(binding);

		let list = this._bindingsByElement.get(node);
		if (!list) this._bindingsByElement.set(node, (list = []));
		list.push(binding);
	}

	/**
	 * @param {Node}         node
	 * @param {Set<Binding>} bindingsToRemove
	 * @returns {void}
	 */
	_disposeNodeBindings(node, bindingsToRemove) {
		const nodeBindings = this._bindingsByElement.get(node);
		if (!nodeBindings || nodeBindings.length === 0) return;

		for (const binding of nodeBindings) {
			this._disposeBinding(binding);
			bindingsToRemove.add(binding);
		}

		this._bindingsByElement.delete(node);
	}

	/**
	 * @param {Set<Binding>} bindingsToRemove
	 * @returns {void}
	 */
	_removeDisposedFromList(bindingsToRemove) {
		if (!bindingsToRemove || bindingsToRemove.size === 0) return;
		this._bindings = this._bindings.filter(binding => !bindingsToRemove.has(binding));
	}

	/**
	 * @param {Binding} binding
	 * @returns {void}
	 */
	_disposeBinding(binding) {
		if (!binding || typeof binding.dispose !== 'function') return;
		try {
			binding.dispose();
			binding._disposed = true;
		} catch (error) {
			console.error(error);
		}
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @yields {Node}
	 * @returns {Generator<Node, void, unknown>}
	 */
	* _iterateRange(startAnchor, endAnchor) {
		const root = startAnchor?.parentNode;
		if (!root) return;

		if (endAnchor?.parentNode !== root) {
			console.error('BindingManager: start and end anchors have different parents');
			return;
		}

		const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
		walker.currentNode = startAnchor;
		let node = walker.nextNode();

		while (node && node !== endAnchor) {
			yield node;
			if (node.nodeType === Node.ELEMENT_NODE && this._isBoundaryComponent(node)) {
				node = walker.nextSibling();
				continue;
			}
			node = walker.nextNode();
		}
	}

	/**
	 * @param {Node} rootNode
	 * @yields {Node}
	 * @returns {Generator<Node, void, unknown>}
	 */
	* _iterateSubtree(rootNode) {
		yield rootNode;
		if (!(rootNode instanceof Element)) return;

		const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
		let node = walker.firstChild();

		while (node) {
			yield node;

			if (node.nodeType === Node.ELEMENT_NODE && this._isBoundaryComponent(node)) {
				node = walker.nextSibling();
				continue;
			}

			const child = walker.firstChild();
			if (child) {
				node = child;
				continue;
			}

			let sibling = walker.nextSibling();
			while (!sibling) {
				const parent = walker.parentNode();
				if (!parent || parent === rootNode) {
					node = null;
					break;
				}
				sibling = walker.nextSibling();
			}
			node = sibling;
		}
	}

	/**
	 * @param {Node} startAnchor
	 * @param {Node} endAnchor
	 * @yields {Element}
	 * @returns {Generator<Element, void, unknown>}
	 */
	* _iterateTopLevelElementsBetween(startAnchor, endAnchor) {
		let node = startAnchor?.nextSibling;
		while (node && node !== endAnchor) {
			if (node.nodeType === Node.ELEMENT_NODE) yield /** @type {Element} */ (node);
			node = node.nextSibling;
		}
	}

	/**
	 * @param {Node} node
	 * @returns {boolean}
	 */
	_isBoundaryComponent(node) {
		return node instanceof RenderableComponent;
	}
}

class BindingTracker {
	constructor() {
		/** @type {WeakMap<Object, Object>} */
		this._proxyByScope = new WeakMap();

		/** @type {Set<string> | null} */
		this._current = null;
	}

	/**
	 * @param {Object} scope
	 * @returns {Object}
	 */
	wrapScope(scope) {
		let proxy = this._proxyByScope.get(scope);
		if (proxy) return proxy;

		const tracker = this;
		proxy = new Proxy(scope, {
			has(target, key) {
				return key in target;
			},
			get(target, key, receiver) {
				if (typeof key === 'string') tracker._current?.add(key);
				return Reflect.get(target, key, receiver);
			},
		});

		this._proxyByScope.set(scope, proxy);
		return proxy;
	}

	/**
	 * @param {Set<string> | null} [existing=null]
	 * @returns {void}
	 */
	beginTracking(existing = null) {
		if (existing) {
			existing.clear();
			this._current = existing;
		} else {
			this._current = new Set();
		}
	}

	/** @returns {Set<string>} */
	endTracking() {
		const deps = this._current;
		this._current = null;
		return deps;
	}
}

/**
 * @typedef {Object} Binding
 * @property {(scope: Object<string, *>, frame: BindingFrame) => void} update
 * @property {() => void}       [dispose]
 * @property {() => void}       [sleep]
 * @property {() => void}       [wakeup]
 * @property {EphemeralScope}   [ephemeral]
 * @property {Node}             [element]
 * @property {BindingFrame}     [frame]
 * @property {Set<string>|null} [deps]
 * @property {string}           [label]
 * @property {boolean}          [_disposed]
 */

/**
 * @callback AddBindingFn
 * @param {Binding} binding
 * @param {Node}    [forElement]
 * @returns {void}
 */
;class ComponentScope {
	constructor() {
		/** @type {Map<string, ScopeSourceFn>} */
		this._sources = new Map();

		/** @type {Object<string, *>} */
		this._context = Object.create(null);

		/** @type {Set<string>} */
		this._knownKeys = new Set();

		/** @type {Set<string>} */
		this._staticKeys = new Set();

		/** @type {Set<string>} */
		this._nextKeysBuf = new Set();

		/** @type {Set<string>} */
		this._changedBuf = new Set();
	}

	/**
	 * @param {string}        name
	 * @param {ScopeSourceFn} fn
	 * @returns {void}
	 */
	addSource(name, fn) {
		this._sources.set(name, fn);
	}

	/**
	 * @param {string} name
	 * @returns {void}
	 */
	removeSource(name) {
		this._sources.delete(name);
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	setStatic(key, value) {
		this._context[key] = value;
		this._staticKeys.add(key);
	}

	/**
	 * @param {string}        name
	 * @param {ScopeSourceFn} fn
	 * @returns {void}
	 */
	addStaticSource(name, fn) {
		let data;
		try {
			data = fn();
		} catch (e) {
			console.error(`[ComponentScope] static source "${name}" threw`, e);
			return;
		}

		if (!data || typeof data !== 'object') return;

		for (const [key, value] of Object.entries(data)) {
			this._context[key] = value;
			this._staticKeys.add(key);
		}
	}

	/**
	 * @returns {Set<string>} — keys whose values changed since last `update()` (including removed keys)
	 */
	update() {
		const changed  = this._changedBuf;
		const nextKeys = this._nextKeysBuf;
		changed.clear();
		nextKeys.clear();

		this._sources.forEach((fn, name) => {
			let data;
			try {
				data = fn();
			} catch (e) {
				console.error(`[ComponentScope] source "${name}" threw`, e);
				return;
			}

			if (!data || typeof data !== 'object') return;

			const keys = Object.keys(data);
			for (let i = 0; i < keys.length; i++) {
				const key  = keys[i];
				const next = data[key];
				if (!Object.is(this._context[key], next)) changed.add(key);
				this._context[key] = next;
				nextKeys.add(key);
			}
		});

		this._knownKeys.forEach(key => {
			if (!nextKeys.has(key) && !this._staticKeys.has(key)) {
				changed.add(key);
				delete this._context[key];
			}
		});

		this._nextKeysBuf = this._knownKeys;
		this._knownKeys   = nextKeys;

		return changed;
	}

	/** @returns {Object<string, *>} */
	get context() {
		return this._context;
	}

	/** @returns {void} */
	dispose() {
		this._sources.clear();
		this._knownKeys.clear();
		this._staticKeys.clear();
	}
}

class EphemeralScope {
	/**
	 * @param {Object<string, *>} parentContext
	 */
	constructor(parentContext) {
		/** @type {Object<string, *>} */
		this._context = Object.create(parentContext);

		/** @type {Set<string>} */
		this._dirtyKeys = new Set();
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	set(key, value) {
		if (!Object.is(this._context[key], value)) this._dirtyKeys.add(key);
		this._context[key] = value;
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	markDirty(key) {
		this._dirtyKeys.add(key);
	}

	/**
	 * @param {string} key
	 * @returns {boolean}
	 */
	hasDirty(key) {
		return this._dirtyKeys.has(key);
	}

	/** @returns {void} */
	clearDirty() {
		this._dirtyKeys.clear();
	}

	/** @returns {boolean} */
	get isDirty() {
		return this._dirtyKeys.size > 0;
	}

	/** @returns {Object<string, *>} */
	get context() {
		return this._context;
	}

	/** @returns {void} */
	dispose() {
		Object.setPrototypeOf(this._context, null);
		this._dirtyKeys.clear();
	}
}

App.classes.ephemeral_scope = EphemeralScope;
App.classes.component_scope = ComponentScope;

/**
 * @callback ScopeSourceFn
 * @returns {Object<string, *> | null | undefined}
 */
;class ComponentChannel {
	/** @type {string} */
	static HANDSHAKE_TOPIC = 'use-provided';

	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {Map<string, ProviderResolver>} */
		this._resolversByKey = new Map();

		/** @type {Map<string, *>} */
		this._lastEmittedValueByKey = new Map();

		/** @type {Map<string, Map<symbol, ProviderSubscriber>>} */
		this._subscribersByKey = new Map();

		/** @type {Set<string>} */
		this._dirtyKeys = new Set();

		/** @type {(() => void) | null} */
		this._unsubHandshake = null;
		this._mountOnce();
	}

	/**
	 * @param {string}           key
	 * @param {ProviderResolver} resolver
	 * @returns {void}
	 */
	provide(key, resolver) {
		if (typeof resolver !== 'function') {
			throw new Error(`ComponentChannel.provide("${key}") expects a resolver function`);
		}
		this._resolversByKey.set(String(key), resolver);
		this._dirtyKeys.add(String(key));
	}

	/**
	 * @param {string} key
	 * @param {*}      value
	 * @returns {void}
	 */
	provideValue(key, value) {
		const normalizedKey = String(key);
		this._resolversByKey.set(normalizedKey, () => value);
		this._dirtyKeys.add(normalizedKey);
	}

	/**
	 * @param {Object<string, ProviderResolver | *>} providesObject
	 * @returns {void}
	 */
	provideMany(providesObject) {
		if (!providesObject || typeof providesObject !== 'object') return;
		for (const [key, valueOrResolver] of Object.entries(providesObject)) {
			if (typeof valueOrResolver === 'function') this.provide(key, valueOrResolver);
			else this.provideValue(key, valueOrResolver);
		}
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	unprovide(key) {
		const normalizedKey = String(key);
		this._resolversByKey.delete(normalizedKey);
		this._lastEmittedValueByKey.delete(normalizedKey);
		this._subscribersByKey.delete(normalizedKey);
		this._dirtyKeys.delete(normalizedKey);
	}

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	notifyProvided(key) {
		this._dirtyKeys.add(String(key));
	}

	/** @returns {void} */
	notifySubscribers() {
		const keysToCheck = new Set([
			...this._subscribersByKey.keys(),
			...this._dirtyKeys,
		]);

		if (keysToCheck.size === 0) return;

		for (const key of keysToCheck) {
			const resolver = this._resolversByKey.get(key);
			if (!resolver) continue;

			/** @type {*} */
			let nextValue;
			try {
				nextValue = resolver({});
			} catch (error) {
				console.error(`[ComponentChannel] resolver error for "${key}"`, error);
				continue;
			}

			const prevValue = this._lastEmittedValueByKey.get(key);
			const changed = !App.helper.object.shallowEqual(prevValue, nextValue);

			if (!changed) {
				this._dirtyKeys.delete(key);
				continue;
			}

			this._lastEmittedValueByKey.set(key, nextValue);
			this._dirtyKeys.delete(key);

			const subscribers = this._subscribersByKey.get(key);
			if (!subscribers) continue;

			for (const [subscriberId, subscriber] of subscribers) {
				let shouldUnsubscribe = false;
				try {
					shouldUnsubscribe = subscriber(nextValue) === false;
				} catch (error) {
					shouldUnsubscribe = true;
					console.error(`[ComponentChannel] subscriber error for "${key}"`, error);
				}

				if (shouldUnsubscribe) {
					subscribers.delete(subscriberId);
				}
			}

			if (subscribers.size === 0) {
				this._subscribersByKey.delete(key);
			}
		}
	}

	/** @returns {void} */
	close() {
		if (this._unsubHandshake) {
			this._unsubHandshake();
			this._unsubHandshake = null;
		}
		this._resolversByKey.clear();
		this._lastEmittedValueByKey.clear();
		this._subscribersByKey.clear();
		this._dirtyKeys.clear();
	}

	/** @returns {void} */
	_mountOnce() {
		this._unsubHandshake = this._hostElement.onHandshake(ComponentChannel.HANDSHAKE_TOPIC, (event) => {
			const requestedKey = event.detail?.requestedKey;
			const request = event.detail?.request || {};
			if (!requestedKey) return undefined;

			const key = String(requestedKey);
			const resolver = this._resolversByKey.get(key);
			if (!resolver) return undefined;

			if (request.subscribe && typeof request.onUpdate === 'function') {
				return this._addSubscriber(key, request.onUpdate);
			}

			return resolver(request);
		});
	}

	/**
	 * @param {string}             key
	 * @param {ProviderSubscriber} onUpdate
	 * @returns {() => void}
	 */
	_addSubscriber(key, onUpdate) {
		let subscribers = this._subscribersByKey.get(key);
		if (!subscribers) this._subscribersByKey.set(key, (subscribers = new Map()));

		const subscriberId = Symbol(key);
		subscribers.set(subscriberId, onUpdate);

		return () => {
			const map = this._subscribersByKey.get(key);
			if (!map) return;
			map.delete(subscriberId);
			if (map.size === 0) {
				this._subscribersByKey.delete(key);
				this._lastEmittedValueByKey.delete(key);
				this._dirtyKeys.delete(key);
			}
		};
	}
}

App.classes.component_channel = ComponentChannel;

class DeepProvider extends ComponentChannel {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		super(hostElement,);

		/** @type {Map<string, () => void>} */
		this._consumerUnsubByKey = new Map();

		/** @type {Map<string, *>} */
		this._consumerCacheByKey = new Map();

		/** @type {Set<string>} */
		this._consumerDirtyKeys = new Set();

		/** @type {Object | undefined} */
		this._providedProxy = undefined;
	}

	/**
	 * @param {string}  key
	 * @param {Object}  [request]
	 * @returns {*}
	 */
	useProvided(key, request = undefined) {
		return this._hostElement.requestHandshake(ComponentChannel.HANDSHAKE_TOPIC, {
			details: {requestedKey: String(key), request}
		});
	}

	/**
	 * @param {string}             key
	 * @param {ProviderSubscriber} onUpdate
	 * @param {Object}             [request]
	 * @returns {() => void}
	 */
	subscribeProvided(key, onUpdate, request = undefined) {
		const unsubscribe = this._hostElement.requestHandshake(ComponentChannel.HANDSHAKE_TOPIC, {
			details: {requestedKey: String(key), request: {...request, subscribe: true, onUpdate}}
		});
		return typeof unsubscribe === 'function' ? unsubscribe : () => {};
	}

	/**
	 * @param {ProvidedProxyInvalidator} onInvalidate
	 * @returns {Object}
	 */
	getProvidedProxy(onInvalidate) {
		const self = this;

		return this._providedProxy || (this._providedProxy = new Proxy(Object.create(null), {
			get(_, prop) {
				const key = String(prop);

				if (!self._consumerUnsubByKey.has(key)) {
					const unsubscribe = self.subscribeProvided(key, () => {
						self._consumerDirtyKeys.add(key);
						try {
							onInvalidate(key);
						} catch (error) {
							console.error(error)
						}
					});
					self._consumerUnsubByKey.set(key, unsubscribe);
					self._consumerDirtyKeys.add(key);
				}

				if (!self._consumerDirtyKeys.has(key) && self._consumerCacheByKey.has(key)) {
					return self._consumerCacheByKey.get(key);
				}

				const value = self.useProvided(key);
				self._consumerCacheByKey.set(key, value);
				self._consumerDirtyKeys.delete(key);
				return value;
			}
		}));
	}

	/** @returns {void} */
	disposeConsumers() {
		for (const unsubscribe of this._consumerUnsubByKey.values()) {
			try {
				unsubscribe();
			} catch (error) {
				console.error('[DeepProvider] consumer unsubscribe error', error);
			}
		}
		this._consumerUnsubByKey.clear();
		this._consumerCacheByKey.clear();
		this._consumerDirtyKeys.clear();
	}

	/** @returns {void} */
	dispose() {
		this.disposeConsumers();
		this.close();
	}
}

App.classes.deep_provider = DeepProvider;

/**
 * @callback ProviderResolver
 * @param {Object} request
 * @returns {*}
 */

/**
 * @callback ProviderSubscriber
 * @param {*} nextValue
 * @returns {false | void}
 */

/**
 * @callback ProvidedProxyInvalidator
 * @param {string} key
 * @returns {void}
 */

/**
 * @typedef {Object} HandshakeRequest
 * @property {string}              requestedKey
 * @property {Object}              [request]
 * @property {boolean}             [subscribe]
 * @property {ProviderSubscriber}  [onUpdate]
 */
;class DirectiveRegistry extends SingletonMixin() {
	/** @type {DirectiveDefinition[]} */
	_definitions = [];

	/**
	 * @type {ApplyMemoFn}
	 */
	_applyMemo = (evaluate, apply, areEqual) =>
		this._applyMemoHelper(evaluate, apply, areEqual ?? Object.is);

	/**
	 * @type {DefaultFrameFactory}
	 */
	_defaultMakeFrame = (target) => ({$target: target, $component: null});

	/**
	 * @type {DefaultFrameFactory}
	 */
	_defaultMakeEventFrame = (target) => ({$target: target, $component: null, $event: null});

	/**
	 * @param {DirectiveDefinition} definition
	 * @returns {void}
	 */
	register(definition) {
		this._definitions.push(definition);
	}

	/**
	 * @param {Element} root
	 * @returns {void}
	 */
	normalizeForHydration(root) {
		for (const def of this._definitions) {
			def.normalizeHydration?.(root);
		}
		for (const tpl of root.querySelectorAll('template')) {
			const inner = tpl.content?.firstElementChild;
			if (inner) this.normalizeForHydration(inner);
		}
	}

	/**
	 * @param {ProcessElementOptions} options
	 * @returns {ProcessElementResult}
	 */
	processElement({
					   element,
					   component,
					   addBinding,
					   customPrefix = 'ws-',
					   ephemeral = null,
					   bindingManager = component.bindingManager,
					   makeFrame = this._defaultMakeFrame,
					   makeEventFrame = this._defaultMakeEventFrame,

				   }) {
		const directives = this._parseDirectives(element, customPrefix);

		return this._executeDirectives(directives, element, {
			component, addBinding, ephemeral, bindingManager,
			makeFrame, makeEventFrame,
			subPlan: null
		});
	}

	/**
	 * @param {ProcessElementFromPlanOptions} options
	 * @returns {ProcessElementResult}
	 */
	processElementFromPlan({
							   entry,
							   node,
							   component,
							   addBinding,
							   ephemeral,
							   bindingManager,
							   makeFrame = this._defaultMakeFrame,
							   makeEventFrame = this._defaultMakeEventFrame,
						   }) {
		return this._executeDirectives(entry.directives, node, {
			component, addBinding, ephemeral, bindingManager,
			makeFrame, makeEventFrame,
			subPlan: entry.subPlan
		});
	}


	/**
	 * @param {PlanDirective[]}     directives
	 * @param {Element}             node
	 * @param {Object}              ctx
	 * @param {RenderableComponent} ctx.component
	 * @param {AddBindingFn}  ctx.addBinding
	 * @param {EphemeralScope|null} ctx.ephemeral
	 * @param {BindingManager}      ctx.bindingManager
	 * @param {PlanEntry[]|null}    ctx.subPlan
	 * @returns {ProcessElementResult}
	 */
	_executeDirectives(directives, node, {
		component, addBinding, ephemeral, bindingManager,
		makeFrame, makeEventFrame,
		subPlan
	}) {
		const ctor = component.constructor;
		const cache = ctor._expressionCache ??= new Map();
		/** @type {EvaluateExpressionFn} */
		const evaluateExpression = (expression, scope, frame) =>
			frame
				? App.helper.eval_safe_context(expression, scope, frame, cache)
				: App.helper.eval_safe_context(expression, scope, cache);

		const applyMemo = this._applyMemo;

		let shouldSkipChildren = false;

		/**
		 * @param {PlanDirective} directive
		 * @returns {void}
		 */
		const runDirective = ({name, attrName, value, def}) => {
			def.init({
				element: node,
				component,
				name,
				value,
				addBinding: (binding, to = node) => {
					binding.label = name;
					addBinding(binding, to);
				},
				ephemeral,
				evaluateExpression,
				applyMemo,
				bindingManager,
				subPlan,
				makeFrame,
				makeEventFrame,
			});
			if (def.controlsSubtree) shouldSkipChildren = true;
			if (def.removeAttribute !== false) node.removeAttribute(attrName);
		};

		const owner = directives.find(d => d.def.controlsElement) ?? null;

		if (owner) {
			runDirective(owner);
			return {shouldSkipChildren, didOwnElement: true};
		}

		for (let i = 0; i < directives.length; i++) runDirective(directives[i]);

		return {shouldSkipChildren, didOwnElement: false};
	}

	/**
	 * @param {Element} element
	 * @param {string}  prefix
	 * @returns {PlanDirective[]}
	 */
	_parseDirectives(element, prefix) {
		/** @type {PlanDirective[]} */
		const directives = [];

		for (const attr of element.attributes) {
			if (!attr.name.startsWith(prefix)) continue;

			const name = prefix !== 'ws-'
				? attr.name.replace(prefix, 'ws-')
				: attr.name;

			const def = this._definitions.find(d => d.matches(name));
			if (def) directives.push({name, attrName: attr.name, value: attr.value, def});
		}

		return directives;
	}

	/**
	 * @template T
	 * @param {(scope: Object, frame: BindingFrame) => T}            evaluate
	 * @param {(value: T, scope: Object, frame: BindingFrame) => void} apply
	 * @param {(a: T, b: T) => boolean}           [areEqual]
	 * @returns {(scope: Object, frame: BindingFrame) => void}
	 */
	_applyMemoHelper(evaluate, apply, areEqual = Object.is) {
		/** @type {boolean} */
		let hasValue = false;
		/** @type {T} */
		let lastValue;

		return function update(scope, frame) {
			const nextValue = evaluate(scope, frame);
			if (hasValue && areEqual(nextValue, lastValue)) {
				return;
			}
			hasValue = true;
			lastValue = nextValue;
			apply(nextValue, scope, frame);
		};
	}

	/**
	 * @param {Element} root
	 * @param {string}  [rootPrefix='ws-']
	 * @param {string}  [childPrefix='ws-']
	 * @returns {PlanEntry[]}
	 */
	buildBindingPlan(root, rootPrefix = 'ws-', childPrefix = 'ws-') {
		/** @type {PlanEntry[]} */
		const plan = [];
		this._walkForPlan(root, [], rootPrefix, childPrefix, plan, true);
		return plan;
	}

	/**
	 * @param {Element}     node
	 * @param {number[]}    path
	 * @param {string}      prefix
	 * @param {string}      childPrefix
	 * @param {PlanEntry[]} targetPlan
	 * @param {boolean}     [isRoot=false]
	 * @returns {void}
	 */
	_walkForPlan(node, path, prefix, childPrefix, targetPlan, isRoot = false) {
		if (!(node instanceof Element)) return;
		if (!isRoot && node instanceof RenderableComponent) return;

		const effectivePrefix = isRoot ? prefix : childPrefix;
		/** @type {PlanDirective[]} */
		const directives = [];
		/** @type {PlanDirective | null} */
		let owner = null;

		for (const attr of node.attributes) {
			if (!attr.name.startsWith(effectivePrefix)) continue;

			const name = effectivePrefix !== 'ws-'
				? attr.name.replace(effectivePrefix, 'ws-')
				: attr.name;

			const def = this._definitions.find(d => d.matches(name));
			if (!def) continue;

			if (!owner && def.controlsElement) owner = {name, attrName: attr.name, value: attr.value, def};
			directives.push({name, attrName: attr.name, value: attr.value, def});
		}

		if (owner) {
			const templateNode = owner.def.getTemplateNode?.(node) ?? null;
			const subPlan = templateNode
				? this.buildBindingPlan(templateNode, childPrefix, childPrefix)
				: null;

			targetPlan.push({path, directives, subPlan});
			return;
		}

		if (directives.length) {
			targetPlan.push({path, directives, subPlan: null});
		}

		if (directives.some(d => d.def.controlsSubtree)) return;

		for (let i = 0; i < node.children.length; i++) {
			this._walkForPlan(node.children[i], [...path, i], prefix, childPrefix, targetPlan, false);
		}
	}

}

App.classes.directive_registry = DirectiveRegistry;

/* ===========================	REACTIVE DIRECTIVES =========================== */
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-show',
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			let originalDisplay = element.style.display || '';
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame),
					(show) => element.style.display = show ? '' : 'none'
				),
				dispose() {
					element.style.display = originalDisplay;
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-text',
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						let result = evaluateExpression(expression, scope, frame);
						result = result === null || result === undefined ? '' : result;

						if (result === null || result === undefined) {
							result = '';
						} else if (typeof result !== 'string' && typeof result !== 'number' && typeof result !== 'boolean') {
							result = JSON.stringify(result, null, '\t');
						} else {
							result = String(result);
						}

						modifierSet.forEach((mod) => {
							result = App.helper.template.run_modifier(mod, result);
						});
						return result;
					},
					(result) => element.textContent = result
				),
				dispose: () => {
					element.textContent = '';
				}
			});
		}
	}));
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-html',
		removeAttribute: true,

		/**
		 * @param {string|Node|DocumentFragment|null} value
		 * @returns {DocumentFragment}
		 */
		_toFragment(value) {
			if (value instanceof DocumentFragment) {
				return value.cloneNode(true);
			}

			if (value instanceof Node) {
				const fragment = document.createDocumentFragment();
				fragment.appendChild(value.cloneNode(true));
				return fragment;
			}

			const template = document.createElement('template');
			template.innerHTML = value || '';
			return template.content;
		},

		init({element, component, value, addBinding, evaluateExpression, applyMemo, bindingManager}) {
			const toFragment = (v) => this._toFragment(v);
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame) ?? '',
					(next) => {
						for (const childNode of Array.from(element.childNodes)) {
							bindingManager.disposeInSubtree(childNode);
						}
						element.replaceChildren(toFragment(next));
						let addedCount = 0;
						for (const childElement of Array.from(element.children)) {
							addedCount += bindingManager.collectInSubtree(childElement);
						}
						addedCount && component.invalidateLifecycle('ws-html', LifecycleInvalidations.BINDINGS);
					}
				),
				dispose() {
					for (const childNode of Array.from(element.childNodes)) {
						bindingManager.disposeInSubtree(childNode);
					}
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-attr-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const attrName = name.slice(8);
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						let result = evaluateExpression(expression, scope, frame);
						modifierSet.forEach((mod) => {
							result = App.helper.template.run_modifier(mod, result);
						});
						return result;
					},
					(result) => {
						if (result == null || result === false) {
							element.removeAttribute(attrName);
						} else {
							element.setAttribute(attrName, String(result));
						}
					}
				),
				dispose() {
					element.removeAttribute(attrName);
				},
				el: element,
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-prop-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const propName = name.slice(8);
			const originalValue = element[propName];
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						return evaluateExpression(value, scope, frame);
					},
					(result) => {
						if (element instanceof RenderableComponent) {
							element.setPropPath(propName, result, 'pass-state');
						} else {
							element[propName] = result;
						}
					}
				),
				dispose() {
					if (element instanceof RenderableComponent) {
						// nothing todo???
					} else {
						element[propName] = originalValue;
					}
				},
				el: element,
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-state-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const propName = name.slice(9);
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						return evaluateExpression(value, scope, frame);
					},
					(result) => {
						if (element instanceof RenderableComponent) {
							element.setPropPath(propName, result, 'pass-state');
						} else {
							console.warn(`ws-state-${propName} is not supported on ${element.constructor.name}`);
						}
					}
				),
				el: element,
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-slot-'),
		removeAttribute: true,
		init({element, name, value, addBinding, evaluateExpression, applyMemo}) {
			const slotName = name.slice(8);
			let [expression, modifiers] = value.split(':').map(s => s?.trim());
			let modifierSet = new Set();
			if (modifiers) {
				modifiers.split(',').forEach(mod => modifierSet.add(mod.trim()));
			}
			addBinding({
				update: applyMemo(
					(scope, frame) => {
						return evaluateExpression(expression, scope, frame);
					},
					(result) => {
						element.setSlot(slotName, result);
					}
				),
				dispose() {
					element.setSlot(slotName, '');
				},
			});
		}
	}));


App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-proxy-attr-'),
		removeAttribute: true,
		init({element, component, name, value, addBinding, applyMemo}) {
			const attrName = name.slice('ws-proxy-attr-'.length);
			addBinding({
				update: applyMemo(
					(scope, frame) => component.getAttribute(value),
					(attrVal) => {
						if (attrVal == null || attrVal === false) {
							element.removeAttribute(attrName);
						} else {
							element.setAttribute(attrName, attrVal);
						}
					}
				),
				dispose() {
					element.removeAttribute(attrName);
				}
			});
		}
	}));
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-proxy-attrs',
		removeAttribute: true,
		init({element, component, value, addBinding, applyMemo}) {
			const specifiedAttrs = value
				.split(',')
				.map(s => s.trim())
				.filter(Boolean);

			const proxyAll = specifiedAttrs.length === 0;

			/** @type {Map<string, string|null>} */
			let lastApplied = new Map();

			/** @returns {Map<string, string|null>} */
			const snapshot = () => {
				/** @type {Map<string, string|null>} */
				const map = new Map();
				if (proxyAll) {
					for (const {name, value} of Array.from(component.attributes)) {
						map.set(name, value);
					}
				} else {
					for (const name of specifiedAttrs) {
						map.set(name, component.hasAttribute(name)
							? component.getAttribute(name)
							: null
						);
					}
				}
				return map;
			};

			/**
			 * @param {Map<string, string|null>} map
			 * @returns {string}
			 */
			const signature = (map) =>
				Array.from(map.entries())
					.map(([k, v]) => `${k}\x00${v ?? '\x01'}`)
					.join('\x02');

			/**
			 * @param {Map<string, string|null>} map
			 * @returns {void}
			 */
			const applyMap = (map) => {
				for (const [name] of lastApplied) {
					if (!map.has(name) || map.get(name) === null) {
						element.removeAttribute(name);
					}
				}
				for (const [name, val] of map) {
					if (val === null) {
						if (lastApplied.has(name) && lastApplied.get(name) !== null) {
							element.removeAttribute(name);
						}
					} else {
						if (lastApplied.get(name) !== val) {
							element.setAttribute(name, val);
						}
					}
				}
				lastApplied = map;
			};

			addBinding({
				update: applyMemo(
					(scope, frame) => {
						const map = snapshot();
						return {map, sig: signature(map)};
					},
					({map}) => applyMap(map),
					(a, b) => a.sig === b.sig
				),
				dispose() {
					for (const [name] of lastApplied) {
						element.removeAttribute(name);
					}
					lastApplied = new Map();
				}
			});
		}
	})
);
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-if',
		removeAttribute: true,
		controlsElement: true,
		controlsSubtree: true,

		getTemplateNode(element) {
			const clone = element.cloneNode(true);
			clone.removeAttribute('ws-if');
			return clone;
		},

		init({element, value, addBinding, evaluateExpression, applyMemo, bindingManager, subPlan}) {

			const hydrating = element instanceof HTMLTemplateElement;

			/** @type {Element} */
			let originalElement;
			/** @type {Comment} */
			let placeholder;
			/** @type {boolean} */
			let isInDOM;

			if (hydrating) {
				originalElement = element.content.firstElementChild;
				placeholder = document.createComment('ws-if placeholder');
				element.replaceWith(placeholder);
				isInDOM = false;
			} else {
				originalElement = element;
				placeholder = document.createComment('ws-if placeholder');
				isInDOM = true;
			}

			if (subPlan) {
				bindingManager.executePlan(subPlan, originalElement);
			} else {
				originalElement.removeAttribute('ws-if');
				bindingManager.collectInSubtree(originalElement);
			}

			const removeElement = () => {
				if (isInDOM) {
					originalElement.replaceWith(placeholder);
					isInDOM = false;
				}
			};
			const insertElement = () => {
				if (!isInDOM) {
					placeholder.replaceWith(originalElement);
					isInDOM = true;
				}
			};

			let disposing = false;
			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(value, scope, frame),
					(show) => {
						if (show) {
							insertElement();
						} else {
							removeElement();
						}
					}
				),
				dispose() {
					if (!disposing) {
						disposing = true;
						bindingManager.disposeInSubtree(placeholder);
					}
					placeholder = null;
				}
			});
			addBinding({
				dispose() {
					if (!disposing) {
						disposing = true;
						bindingManager.disposeInSubtree(originalElement);
					}
					originalElement = null;
				}
			}, placeholder);
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-class';
		},
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			const raw = value.trim();
			if (!raw) return;
			/** @type {Set<string>} */
			let lastApplied = new Set();
			/** @type {Set<string>} */
			let nextBuffer = new Set();

			/**
			 * @param {string[]} classList
			 * @returns {void}
			 */
			const applyClasses = (classList) => {
				const next = nextBuffer;
				next.clear();
				for (const c of classList) next.add(c);

				for (const c of lastApplied) if (!next.has(c) && element.classList.contains(c)) element.classList.remove(c);
				for (const c of next) if (!lastApplied.has(c) && !element.classList.contains(c)) element.classList.add(c);

				nextBuffer = lastApplied;
				lastApplied = next;
			};


			addBinding({
				update: applyMemo(
					(scope, frame) => {
						const arr = evaluateExpression(raw, scope, frame);
						if (!Array.isArray(arr)) return {signature: '', list: []};

						const list = arr
							.map(x => (typeof x === 'string' ? x.trim() : null))
							.filter(Boolean);
						return {signature: list.join('\u0001'), list};
					},
					(result) => applyClasses(result.list),
					(a, b) => a.signature === b.signature
				),
				dispose() {
					applyClasses([]);
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-style';
		},
		removeAttribute: true,
		init({element, value, addBinding, evaluateExpression, applyMemo}) {
			const raw = (value || '').trim();
			if (!raw) return;

			const looksLikeSingleExpression =
				!raw.includes(';') && raw.indexOf(':') === -1;

			/**
			 * @param {*} styleValue
			 * @returns {string | null}
			 */
			const normalizeStyleValue = (styleValue) => {
				if (styleValue == null || styleValue === false) return null;
				return String(styleValue);
			};

			/**
			 * @param {string} prop
			 * @returns {string}
			 */
			const toCssProp = (prop) => App.helper.string.to_kebab_case(prop);

			/** @type {Map<string, string|null>} */
			let lastValuesByProp = new Map();

			/**
			 * @param {Object<string, *> | null} map
			 * @returns {void}
			 */
			const applyMap = (map) => {
				/** @type {Map<string, string|null>} */
				const nextValuesByProp = new Map();

				if (map && typeof map === 'object') {
					for (const key in map) {
						if (!Object.prototype.hasOwnProperty.call(map, key)) continue;

						const cssProp = toCssProp(key);
						const nextValue = normalizeStyleValue(map[key]);

						nextValuesByProp.set(cssProp, nextValue);

						const prevValue = lastValuesByProp.get(cssProp);
						if (Object.is(prevValue, nextValue)) continue;


						if (nextValue === null) {
							element.style.removeProperty(cssProp);
						} else {
							const [value, priority] = nextValue.split('!').map(s => s.trim());
							element.style.setProperty(cssProp, value, priority);
						}
					}
				}

				for (const [prevProp, prevValue] of lastValuesByProp) {
					if (!nextValuesByProp.has(prevProp)) {
						if (prevValue !== null) element.style.removeProperty(prevProp);
					}
				}

				lastValuesByProp = nextValuesByProp;
			};

			/**
			 * @param {string} cssText
			 * @returns {Object<string, string>}
			 */
			const parseCssTextToMap = (cssText) => {
				/** @type {Object<string, string>} */
				const map = {};
				if (!cssText) return map;

				cssText
					.split(';')
					.map((chunk) => chunk.trim())
					.filter(Boolean)
					.forEach((chunk) => {
						const colonIndex = chunk.indexOf(':');
						if (colonIndex === -1) return;

						const prop = chunk.slice(0, colonIndex).trim();
						const val = chunk.slice(colonIndex + 1).trim();
						if (!prop) return;

						map[prop] = val;
					});

				return map;
			};

			/** @type {Array<{propPart: string, expression: string}> | null} */
			let compiledRules = null;

			if (!looksLikeSingleExpression) {
				compiledRules = raw
					.split(';')
					.map((chunk) => chunk.trim())
					.filter(Boolean)
					.map((chunk) => {
						const colonIndex = chunk.indexOf(':');
						if (colonIndex === -1) return null;

						const propPart = chunk.slice(0, colonIndex).trim();
						const exprPart = chunk.slice(colonIndex + 1).trim();

						if (!propPart) return null;

						return {
							propPart,
							expression: exprPart || 'null',
						};
					})
					.filter(Boolean);
			}

			const updateSingleExpression = applyMemo(
				(scope, frame) => evaluateExpression(raw, scope, frame),
				(styleValue) => {
					if (typeof styleValue === 'string') {
						applyMap(parseCssTextToMap(styleValue));
						return;
					}
					applyMap(styleValue || {});
				},
				(prev, next) => Object.is(prev, next)
			);

			const updateRules = applyMemo(
				(scope, frame) => {
					/** @type {Object<string, *>} */
					const map = {};
					let signature = '';

					for (const rule of compiledRules) {
						const evaluatedValue = evaluateExpression(rule.expression, scope, frame);
						map[rule.propPart] = evaluatedValue;
						const normalizedValue = normalizeStyleValue(evaluatedValue);
						signature += rule.propPart + '=' + (normalizedValue === null ? '' : normalizedValue) + ';';
					}

					return {signature, map};
				},
				(result) => applyMap(result.map),
				(prev, next) => prev.signature === next.signature
			);

			addBinding({
				update(scope, frame) {
					try {
						if (looksLikeSingleExpression) {
							updateSingleExpression(scope, frame);
							return;
						}
						updateRules(scope, frame);
					} catch (error) {
						console.error('ws-style error', raw, error);
					}
				},
				dispose() {
					applyMap({});
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches(attributeName) {
			return attributeName === 'ws-model';
		},
		removeAttribute: true,
		init({element, value, component, addBinding, applyMemo}) {
			const parts = value.split('|').map(s => s.trim());
			/** @type {Object<string, string>} */
			const config = {};
			parts.forEach(part => {
				const [key, val] = part.split(':').map(s => s.trim());
				if (key && val !== undefined) {
					config[key] = val;
				}
			});
			const {event = 'change', attr, bind = 'value', path = 'value'} = config;
			if (!attr) {
				console.error('ws-model requires "attr" configuration');
				return;
			}

			/** @param {Event} e */
			const onModelEvent = (e) => {
				/** @type {*} */
				let newValue;
				if (path) {
					newValue = App.helper.object.readByPath(e, path);
				} else {
					newValue = e.target.value;
				}

				if (newValue == null) {
					component.removeAttribute(attr);
				} else {
					component.setAttribute(attr, String(newValue));
				}
			};

			const {unbind} = element.addEventListenerWs(event, onModelEvent);
			addBinding({
				update: applyMemo(
					(scope, frame) => component.getAttribute(attr),
					(modelValue) => {
						if (modelValue == null) element.removeAttribute(bind);
						else element.setAttribute(bind, modelValue);
					}
				),
				dispose() {
					unbind();
				}
			});
		}
	}));

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-for',
		removeAttribute: true,
		controlsSubtree: true,
		controlsElement: true,
		getTemplateNode(element) {
			const clone = element.cloneNode(true);
			clone.removeAttribute('ws-for');
			return clone;
		},

		/** @param {Element} root */
		normalizeHydration(root) {
			for (const tpl of root.querySelectorAll('template[ws-for-src]')) {
				let node = tpl.nextSibling;
				while (node && !(node.nodeType === Node.COMMENT_NODE && node.data === 'ws-for:start')) {
					node = node.nextSibling;
				}
				if (!node) continue;

				const fragment = document.createDocumentFragment();
				while (node) {
					const next = node.nextSibling;
					const isEnd = node.nodeType === Node.COMMENT_NODE && node.data === 'ws-for:end';
					fragment.appendChild(node);
					if (isEnd) break;
					node = next;
				}
				tpl._hydrateFragment = fragment;
			}
		},
		init({
				 element,
				 value,
				 component,
				 ephemeral: parentEphemeral = null,
				 addBinding,
				 evaluateExpression,
				 applyMemo,
				 subPlan = null,
				 bindingManager,
			 }) {

			/**
			 * @param {string} raw
			 * @returns {WsForConfig}
			 */
			const parse = (raw) => {
				const [headRaw, ...tailParts] = String(raw || '').split('|').map(s => s.trim()).filter(Boolean);
				const head = headRaw || '';

				const inMatch = head.match(/^(.*?)\s+in\s+(.+)$/);
				if (!inMatch) throw new Error(`ws-for: invalid syntax "${raw}". Expected "item in items"`);

				const varsPart = inMatch[1].trim();
				const listExpression = inMatch[2].trim();

				const vars = varsPart.split(',').map(s => s.trim()).filter(Boolean);
				const itemVarName = vars[0] || 'item';
				const indexVarName = vars[1] || 'index';

				/** @type {Object<string, string>} */
				const options = {};
				for (const part of tailParts) {
					const [key, ...rest] = part.split(':');
					if (!key) continue;
					options[key.trim()] = rest.join(':').trim();
				}

				return {
					itemVarName,
					indexVarName,
					listExpression,
					keyExpression: options.key || null,
				};
			};

			const config = parse(value);

			const hydrating = element instanceof HTMLTemplateElement;

			const templateElement = hydrating
				? element.content.firstElementChild
				: element.cloneNode(true);

			if (!hydrating) templateElement.removeAttribute('ws-for');

			/** @type {Comment} */
			let loopStart;
			/** @type {Comment} */
			let loopEnd;

			if (hydrating) {
				const holding = element._hydrateFragment;
				if (holding) {
					element.parentNode.insertBefore(holding, element.nextSibling);
					element._hydrateFragment = null;
				}
				const anchors = this._findForAnchors(element);
				loopStart = anchors.start;
				loopEnd = anchors.end;
				element.remove();
			} else {
				loopStart = document.createComment('ws-for:start');
				loopEnd = document.createComment('ws-for:end');
				element.replaceWith(loopStart, loopEnd);
			}

			/**
			 * @param {Comment} rangeStart
			 * @param {Comment} rangeEnd
			 * @returns {DocumentFragment}
			 */
			const extractRangeToFragment = (rangeStart, rangeEnd) => {
				const fragment = document.createDocumentFragment();
				const parent = rangeStart.parentNode;
				if (!parent || rangeEnd.parentNode !== parent) return fragment;

				let node = rangeStart;
				while (node) {
					const next = node.nextSibling;
					fragment.appendChild(node);
					if (node === rangeEnd) break;
					node = next;
				}
				return fragment;
			};

			/**
			 * @param {Comment} rangeStart
			 * @param {Comment} rangeEnd
			 * @returns {void}
			 */
			const removeRange = (rangeStart, rangeEnd) => {
				const parent = rangeStart.parentNode;
				if (!parent || rangeEnd.parentNode !== parent) return;
				let node = rangeStart;
				while (node) {
					const next = node.nextSibling;
					parent.removeChild(node);
					if (node === rangeEnd) break;
					node = next;
				}
			};

			/** @type {Map<string, WsForInstance>} */
			const instancesByKey = new Map();
			/** @type {number} */
			let renderTick = 0;

			/**
			 * @param {string}  key
			 * @param {Element} root
			 * @param {Node}    insertBeforeNode
			 * @returns {WsForInstance}
			 */
			const createInstance = (key, root, insertBeforeNode) => {
				const itemStart = document.createComment(`ws-for:item:${key}:start`);
				const itemEnd = document.createComment(`ws-for:item:${key}:end`);

				const fragment = document.createDocumentFragment();
				fragment.appendChild(itemStart);
				fragment.appendChild(root);
				fragment.appendChild(itemEnd);
				loopEnd.parentNode.insertBefore(fragment, insertBeforeNode);

				return this._buildInstance({
					key, root, itemStart, itemEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager,
				});
			};

			/**
			 * @param {string} key
			 * @param {Node}   insertBeforeNode
			 * @returns {WsForInstance}
			 */
			const ensureInstance = (key, insertBeforeNode) => {
				let instance = instancesByKey.get(key);
				if (instance) return instance;

				const clone = templateElement.cloneNode(true);
				const instance_ = createInstance(key, clone, insertBeforeNode);
				instancesByKey.set(key, instance_);
				return instance_;
			};

			if (hydrating) {
				this._adoptExistingInstances({
					loopStart, loopEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager, instancesByKey,
				});
			}

			/**
			 * @param {WsForInstance} instance
			 * @param {Node}          insertBeforeNode
			 * @returns {void}
			 */
			const moveInstanceBefore = (instance, insertBeforeNode) => {
				if (instance.end.nextSibling === insertBeforeNode) return;
				const fragment = extractRangeToFragment(instance.start, instance.end);
				loopEnd.parentNode.insertBefore(fragment, insertBeforeNode);
			};

			/**
			 * @param {*}      keyValue
			 * @param {number} fallbackIndex
			 * @returns {string}
			 */
			const normalizeKey = (keyValue, fallbackIndex) => {
				if (keyValue == null) return String(fallbackIndex);
				const keyType = typeof keyValue;
				if (keyType === 'string' || keyType === 'number') return String(keyValue);
				console.warn('ws-for: invalid key type, expected string|number. Fallback to index.', keyValue);
				return String(fallbackIndex);
			};

			/**
			 * @param {WsForInstance} instance
			 * @returns {void}
			 */
			const disposeInstance = (instance) => {
				instance.owned.release();
				removeRange(instance.start, instance.end);
			};

			/**
			 * @param {WsForInstance} instance
			 * @returns {void}
			 */
			const collectDeferred = (instance) => {
				if (!instance._needsCollect) return;
				instance._needsCollect = false;

				bindingManager._hydratedNodes?.delete(instance.root);
				subPlan
					? component.bindingManager.executePlan(subPlan, instance.root, instance.ephemeral)
					: component.bindingManager.collectInSubtree(instance.root, instance.ephemeral);
			};

			addBinding({
				update: applyMemo(
					(scope, frame) => evaluateExpression(config.listExpression, scope, frame),
					(source, scope, frame) => {
						renderTick++;

						/** @type {*[]} */
						let items = [];
						if (Array.isArray(source)) items = source;
						else if (source && typeof source === 'object') items = Object.values(source);

						const total = items.length;

						const keyScope = config.keyExpression
							? Object.create(scope)
							: null;

						/** @type {WsForInstance[]} */
						const orderedInstances = new Array(total);
						for (let i = 0; i < total; i++) {
							const item = items[i];

							let key = String(i);
							if (keyScope) {
								keyScope[config.itemVarName] = item;
								keyScope[config.indexVarName] = i;
								key = normalizeKey(evaluateExpression(config.keyExpression, keyScope, frame), i);
							}

							const instance = ensureInstance(key, loopEnd);
							instance._seenTick = renderTick;
							instance.item = item;
							instance.index = i;
							instance.total = total;
							instance.syncContext();
							collectDeferred(instance);

							orderedInstances[i] = instance;
						}

						for (const [key, instance] of instancesByKey) {
							if (instance._seenTick === renderTick) continue;
							disposeInstance(instance);
							instancesByKey.delete(key);
						}

						/** @type {Node} */
						let cursor = loopEnd;
						for (let i = total - 1; i >= 0; i--) {
							const instance = orderedInstances[i];
							moveInstanceBefore(instance, cursor);
							if (instance.ephemeral.isDirty) instance.provider.notifySubscribers();
							cursor = instance.start;
						}
					}
				),

				dispose() {
					for (const [, instance] of instancesByKey) {
						disposeInstance(instance);
					}
					instancesByKey.clear();
				}
			}, loopStart);
		},

		/**
		 * @param {Object}              options
		 * @param {string}              options.key
		 * @param {Element}             options.root
		 * @param {Comment}             options.itemStart
		 * @param {Comment}             options.itemEnd
		 * @param {WsForConfig}         options.config
		 * @param {RenderableComponent} options.component
		 * @param {EphemeralScope|null} options.parentEphemeral
		 * @param {PlanEntry[]|null}    options.subPlan
		 * @param {BindingManager}      options.bindingManager
		 * @param {boolean}             [options.skipCollect=false]
		 * @returns {WsForInstance}
		 */
		_buildInstance({
						   key, root, itemStart, itemEnd,
						   config, component, parentEphemeral,
						   subPlan, bindingManager,
						   skipCollect = false,
					   }) {
			const parentContext = parentEphemeral ? parentEphemeral.context : component.scope.context;
			const ephemeral = new EphemeralScope(parentContext);
			const loop = {};

			ephemeral.set(config.itemVarName, null);
			ephemeral.set(config.indexVarName, 0);
			ephemeral.set('loop', loop);

			/** @type {WsForInstance} */
			const instance = {
				key,
				start: itemStart,
				end: itemEnd,
				root,
				ephemeral,
				loop,
				item: null,
				index: 0,
				total: 0,
			};

			instance.syncContext = () => {
				ephemeral.set(config.itemVarName, instance.item);
				ephemeral.set(config.indexVarName, instance.index);

				const nextFirst = instance.index === 0;
				const nextLast = instance.index === instance.total - 1;
				const nextLength = instance.total;

				if (
					loop.index !== instance.index ||
					loop.first !== nextFirst ||
					loop.last !== nextLast ||
					loop.length !== nextLength
				) {
					ephemeral.markDirty('loop');
				}

				loop.index = instance.index;
				loop.key = instance.key;
				loop.first = nextFirst;
				loop.last = nextLast;
				loop.length = nextLength;
			};

			instance.provider = new ComponentChannel(root);
			instance.provider.provideMany({
				[config.itemVarName]: () => instance.item,
				[config.indexVarName]: () => instance.index,
				loop: () => instance.loop,
			});

			if (skipCollect) {
				instance._needsCollect = true;
			} else {
				bindingManager._hydratedNodes?.delete(root);
				subPlan
					? component.bindingManager.executePlan(subPlan, root, ephemeral)
					: component.bindingManager.collectInSubtree(root, ephemeral);
			}

			instance.owned = new OwnedResources(`ws-for:${key}`);
			instance.owned.add('channel', () => instance.provider.close());
			instance.owned.add('bindings', () => component.bindingManager.disposeInRange(instance.start, instance.end));
			instance.owned.add('ephemeral', () => instance.ephemeral.dispose());

			return instance;
		},

		/**
		 * @param {Object}                       options
		 * @param {Comment}                      options.loopStart
		 * @param {Comment}                      options.loopEnd
		 * @param {WsForConfig}                  options.config
		 * @param {RenderableComponent}          options.component
		 * @param {EphemeralScope|null}          options.parentEphemeral
		 * @param {PlanEntry[]|null}             options.subPlan
		 * @param {BindingManager}               options.bindingManager
		 * @param {Map<string, WsForInstance>}   options.instancesByKey
		 * @returns {void}
		 */
		_adoptExistingInstances({
									loopStart, loopEnd,
									config, component, parentEphemeral,
									subPlan, bindingManager, instancesByKey,
								}) {
			const ITEM_PREFIX = 'ws-for:item:';
			const SUFFIX_START = ':start';
			const SUFFIX_END = ':end';

			let node = loopStart.nextSibling;
			while (node && node !== loopEnd) {
				if (node.nodeType !== Node.COMMENT_NODE || !node.textContent.startsWith(ITEM_PREFIX)) {
					node = node.nextSibling;
					continue;
				}

				const text = node.textContent;
				if (!text.endsWith(SUFFIX_START)) {
					node = node.nextSibling;
					continue;
				}

				const key = text.slice(ITEM_PREFIX.length, -SUFFIX_START.length);
				const itemStart = node;
				const expectedEnd = `${ITEM_PREFIX}${key}${SUFFIX_END}`;

				/** @type {Element | null} */
				let root = null;
				/** @type {Comment | null} */
				let itemEnd = null;
				let cursor = itemStart.nextSibling;

				while (cursor && cursor !== loopEnd) {
					if (cursor.nodeType === Node.COMMENT_NODE && cursor.textContent === expectedEnd) {
						itemEnd = cursor;
						break;
					}
					if (!root && cursor.nodeType === Node.ELEMENT_NODE) {
						root = /** @type {Element} */ (cursor);
					}
					cursor = cursor.nextSibling;
				}

				if (!root || !itemEnd) {
					node = cursor?.nextSibling ?? null;
					continue;
				}

				bindingManager.markHydrated(root);

				const instance = this._buildInstance({
					key, root, itemStart, itemEnd,
					config, component, parentEphemeral,
					subPlan, bindingManager,
					skipCollect: true,
				});

				instancesByKey.set(key, instance);
				node = itemEnd.nextSibling;
			}
		},

		/**
		 * @param {HTMLTemplateElement} templateElement
		 * @returns {{start: Comment, end: Comment}}
		 */
		_findForAnchors(templateElement) {
			/** @type {Comment | null} */
			let start = null;
			/** @type {Comment | null} */
			let end = null;

			let node = templateElement.nextSibling;
			while (node) {
				if (node.nodeType === Node.COMMENT_NODE) {
					if (node.textContent === 'ws-for:start') start = /** @type {Comment} */ (node);
					if (node.textContent === 'ws-for:end') { end = /** @type {Comment} */ (node); break; }
				}
				node = node.nextSibling;
			}

			if (!start || !end) {
				throw new Error('ws-for hydration: missing <!--ws-for:start--> / <!--ws-for:end--> anchors');
			}

			return {start, end};
		},
	}));


/* ===========================	END OF REACTIVE DIRECTIVES =========================== */
/* ===========================	ONE-TIME DIRECTIVES =========================== */
App.classes.directive_registry.getInstance().register({
	matches: name => name.startsWith('ws-on-'),
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, name, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
		const eventName = name.slice(6);
		const handlerName = value.trim();
		const handlerLooksLikeCall = handlerName.endsWith(')');
		const eventFrame = makeEventFrame(element);
		/** @type {Object | null} */
		let lastScope = null;

		const {unbind} = element.addEventListenerWs(eventName, event => {
			const scope = lastScope ?? component.bakeTemplateContext();
			if (handlerLooksLikeCall) {
				eventFrame.fire(event);
				try { evaluateExpression(handlerName, scope, eventFrame); }
				finally { eventFrame.release(); }
				return;
			}
			const handler = App.helper.object.readByPath(scope, handlerName) ?? App.helper.object.readByPath(component, handlerName)?.bind(component);
			if (typeof handler === 'function') handler(event, element, scope);
			else console.warn(`Handler "${handlerName}" not found for ws-on-${eventName}`);
		});

		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			dispose() {
				lastScope = null;
				unbind();
			}
		});
	}
});

App.classes.directive_registry.getInstance().register({
	matches: name => name === 'ws-long-press',
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, applyMemo, component, addBinding}) {
		/** @type {ReturnType<typeof setInterval> | undefined} */
		let pressInterval;
		const handlerName = value.trim();
		/** @type {Object | null} */
		let lastScope = null;

		const {unbind: unbindSet} = element.addMultipleEventListener(['mousedown', 'touchstart'], event => {
			clearInterval(pressInterval);
			pressInterval = setInterval(() => {
				const scope = lastScope || component.bakeTemplateContext();
				const handler = scope[handlerName] || component[handlerName]?.bind(component);
				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerName}" not found for ws-on-long-click`);
			}, 200);
		})
		const {unbind: unbindClear} = element.addMultipleEventListener(['mouseup', 'mouseleave', 'touchend', 'touchcancel'], () => {
			clearInterval(pressInterval);
		});
		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			dispose() {
				unbindSet();
				unbindClear();
				clearInterval(pressInterval);
				lastScope = null;
			}
		});
	}
});

App.classes.directive_registry.getInstance().register({
	matches: name => name.startsWith('ws-delegated-on-'),
	removeAttribute: true,
	/** @param {DirectiveInitOptions} options */
	init({element, value, name, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
		const eventName = name.slice('ws-delegated-on-'.length);
		const [selector, handlerName] = value.split('|').map(s => s.trim());
		/** @type {Object | null} */
		let lastScope = null;
		const handlerLooksLikeCall = handlerName.endsWith(')');

		/** @param {Event} event */
		const onEvent = (event, el) => {
			const start = (typeof event.composedPath === 'function' && event.composedPath()[0]) ?? event.target;
			const match = start?.closest?.(selector);
			if (!match || !element.contains(match)) return;

			const scope = lastScope ?? component.bakeTemplateContext();
			if (handlerLooksLikeCall) {
				const eventFrame = makeEventFrame(el);
				eventFrame.fire(event);
				try { evaluateExpression(handlerName, scope, eventFrame); }
				finally { eventFrame.release(); }
				return;
			}
			const handler = App.helper.object.readByPath(scope, handlerName) || App.helper.object.readByPath(component, handlerName)?.bind(component);
			if (typeof handler === 'function') handler(event, match, scope);
			else console.warn(`Handler "${handlerName}" not found for ws-delegated-on-${eventName}`);
		};

		GlobalEventHub.getInstance().register(element, eventName, onEvent);

		addBinding({
			update: applyMemo((scope) => scope, (scope) => {
				lastScope = scope;
			}, Object.is),
			sleep() {
				GlobalEventHub.getInstance().unregister(element, eventName, onEvent);
			},
			wakeup() {
				GlobalEventHub.getInstance().register(element, eventName, onEvent);
			},
			dispose() {
				lastScope = null;
				GlobalEventHub.getInstance().unregister(element, eventName, onEvent);
			},
		});
	},
});

App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name.startsWith('ws-onkey-'),
		removeAttribute: true,
		init({element, name, value, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
			const keyPart = name.slice('ws-onkey-'.length).toLowerCase();
			const handlerName = value.trim();
			const handlerLooksLikeCall = handlerName.endsWith(')');
			const eventFrame = makeEventFrame(element);
			/** @type {Object | null} */
			let lastScope = null;

			/** @param {KeyboardEvent} event */
			const onKeydown = (event) => {
				if (event.key.toLowerCase() !== keyPart) return;

				const scope = lastScope ?? component.bakeTemplateContext();
				if (handlerLooksLikeCall) {
					eventFrame.fire(event);
					try { evaluateExpression(handlerName, scope, eventFrame); }
					finally { eventFrame.release(); }
					return;
				}

				const handler =
					App.helper.object.readByPath(scope, handlerName) ??
					App.helper.object.readByPath(component, handlerName)?.bind(component);

				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerName}" not found for ws-onkey-${keyPart}`);
			};

			GlobalEventHub.getInstance().register(element, 'keydown', onKeydown);

			addBinding({
				update: applyMemo((scope) => scope, (scope) => {
					lastScope = scope;
				}, Object.is),
				sleep() {
					GlobalEventHub.getInstance().unregister(element, 'keydown', onKeydown);
				},
				wakeup() {
					GlobalEventHub.getInstance().register(element, 'keydown', onKeydown);
				},
				dispose() {
					lastScope = null;
					GlobalEventHub.getInstance().unregister(element, 'keydown', onKeydown);
				},
			});
		},
	})
);
App.classes.directive_registry.getInstance().register(
	/** @type {DirectiveDefinition} */
	({
		matches: name => name === 'ws-click-outside',
		removeAttribute: true,
		init({element, value, component, addBinding, applyMemo, evaluateExpression, makeEventFrame}) {
			const pipeIndex = value.indexOf('|');
			const handlerExpr = (pipeIndex === -1 ? value : value.slice(0, pipeIndex)).trim();
			const conditionExpr = pipeIndex === -1 ? '' : value.slice(pipeIndex + 1).trim();
			const hasCondition = conditionExpr !== '';
			const handlerLooksLikeCall = handlerExpr.endsWith(')');

			if (!handlerExpr) return;

			const eventFrame = makeEventFrame(element);
			/** @type {Object | null} */
			let lastScope = null;
			let lastActive = !hasCondition;
			let registered = false;

			/** @param {PointerEvent} event */
			const onClickOutside = (event) => {
				if (element.contains(event.target)) return;

				const scope = lastScope ?? component.bakeTemplateContext();

				if (handlerLooksLikeCall) {
					eventFrame.fire(event);
					try { evaluateExpression(handlerExpr, scope, eventFrame); }
					finally { eventFrame.release(); }
					return;
				}

				const handler =
					App.helper.object.readByPath(scope, handlerExpr) ??
					App.helper.object.readByPath(component, handlerExpr)?.bind(component);

				if (typeof handler === 'function') handler(event, element, scope);
				else console.warn(`Handler "${handlerExpr}" not found for ws-click-outside`);
			};

			const attach = () => {
				if (registered) return;
				GlobalEventHub.getInstance().register(document, 'pointerdown', onClickOutside, true);
				registered = true;
			};

			const detach = () => {
				if (!registered) return;
				GlobalEventHub.getInstance().unregister(document, 'pointerdown', onClickOutside, true);
				registered = false;
			};

			if (!hasCondition) attach();

			addBinding({
				update: applyMemo(
					(scope, frame) => {
						lastScope = scope;
						return hasCondition ? !!evaluateExpression(conditionExpr, scope, frame) : true;
					},
					(active) => {
						lastActive = active;
						active ? attach() : detach();
					},
					Object.is
				),
				sleep: detach,
				wakeup() { if (lastActive) attach(); },
				dispose() {
					lastScope = null;
					detach();
				},
			});
		},
	})
);
/* ===========================	END OF ONE-TIME DIRECTIVES =========================== */



/**
 * @typedef {Object} DirectiveDefinition
 * @property {(attributeName: string) => boolean} matches
 *           — predicate; receives normalized name (e.g. `'ws-text'`) and returns `true` to claim the attribute
 * @property {boolean} [removeAttribute=true]
 *           — when `true` (default), the matched attribute is removed from the DOM after `init()`
 * @property {boolean} [controlsSubtree]
 *           — when `true`, child-element walking is skipped; the directive manages descendant bindings itself (e.g. `ws-html`)
 * @property {boolean} [controlsElement]
 *           — when `true`, this directive is the sole owner of the element; no other directives execute on it (e.g. `ws-if`, `ws-for`)
 * @property {(element: Element) => Element | null} [getTemplateNode]
 *           — for `controlsElement` directives: return a detached clone suitable for precompiled binding plan generation
 * @property {(root: Element) => void} [normalizeHydration]
 *           — SSR hydration pre-pass hook; called once per root before binding collection
 * @property {(options: DirectiveInitOptions) => void} init
 *           — set up the directive: create bindings, attach event listeners, configure DOM
 */

/**
 * @typedef {Object} DirectiveInitOptions
 * @property {Element}                element          — the DOM element the directive is bound to
 * @property {RenderableComponent}    component        — the host component instance
 * @property {string}                 name             — normalized directive name (e.g. `'ws-text'`, `'ws-attr-href'`)
 * @property {string}                 value            — raw attribute value (expression string)
 * @property {AddBindingFn}           addBinding       — register a binding; `label` is auto-set to `name` before forwarding
 * @property {EvaluateExpressionFn}   evaluateExpression — compile+eval a JS expression string against a context object
 * @property {ApplyMemoFn}            applyMemo        — create a memoized update function with optional custom equality
 * @property {BindingManager}         bindingManager   — the host's binding manager for sub-collection/disposal
 * @property {EphemeralScope | null}  ephemeral        — `ws-for` iteration scope, or `null` at component root
 * @property {PlanEntry[] | null}     [subPlan]        — precompiled sub-plan for `controlsElement` directives; `null` when running in walk mode
 */

/**
 * @callback EvaluateExpressionFn
 * @param {string}          expression — JS expression string (e.g. `'item.name'`, `'count > 0'`, `'onClick($event)'`)
 * @param {Object<string,*>} context   — scope context object; expression variables resolve against this
 * @returns {*}                          evaluated result
 */

/**
 * @callback ApplyMemoFn
 * @template T
 * @param {(context: Object<string,*>) => T}            evaluate — extract a value from context each cycle
 * @param {(value: T, context: Object<string,*>) => void} apply  — side-effect to run when value changes
 * @param {((a: T, b: T) => boolean)}                   [areEqual=Object.is] — custom equality; return `true` to skip `apply`
 * @returns {(context: Object<string,*>) => void}          memoized update function suitable as `Binding.update`
 */

/**
 * @typedef {Object} PlanDirective
 * @property {string}              name     — normalized directive name (e.g. `'ws-text'`)
 * @property {string}              attrName — original attribute name as it appeared in DOM (e.g. `'ws-host-text'`)
 * @property {string}              value    — raw attribute value (expression string)
 * @property {DirectiveDefinition} def      — reference to the matched directive definition
 */

/**
 * @typedef {Object} PlanEntry
 * @property {number[]}          path       — child-index path from root: `root.children[path[0]].children[path[1]]...`
 * @property {PlanDirective[]}   directives — all directives found on the target node
 * @property {PlanEntry[] | null} subPlan   — nested plan for `controlsElement` directives with `getTemplateNode`; `null` otherwise
 */

/**
 * @typedef {Object} ProcessElementResult
 * @property {boolean} shouldSkipChildren — `true` if a `controlsSubtree` directive claimed the element
 * @property {boolean} didOwnElement      — `true` if a `controlsElement` directive claimed the element
 */

/**
 * @typedef {Object} ProcessElementOptions
 * @property {Element}              element                      — DOM element to process
 * @property {RenderableComponent}  component                    — host component
 * @property {AddBindingFn}         addBinding                   — binding registration callback
 * @property {string}               [customPrefix='ws-']         — attribute prefix to scan (e.g. `'ws-host-'` for root element)
 * @property {EphemeralScope | null} [ephemeral=null]            — iteration scope for `ws-for` children
 * @property {BindingManager}       [bindingManager=component.bindingManager] — binding manager instance
 */

/**
 * @typedef {Object} ProcessElementFromPlanOptions
 * @property {PlanEntry}            entry          — precompiled plan entry with path-resolved node
 * @property {Element}              node           — the resolved DOM element
 * @property {RenderableComponent}  component      — host component
 * @property {AddBindingFn}         addBinding     — binding registration callback
 * @property {EphemeralScope | null} ephemeral     — iteration scope
 * @property {BindingManager}       bindingManager — binding manager instance
 */

/**
 * @typedef {Object} WsForConfig
 * @property {string}      itemVarName    — loop item variable name (default `'item'`)
 * @property {string}      indexVarName   — loop index variable name (default `'index'`)
 * @property {string}      listExpression — expression string evaluated to produce the iterable
 * @property {string|null} keyExpression  — expression for stable key extraction; `null` = use index as key
 */

/**
 * @typedef {Object} WsForInstance
 * @property {string}           key         — stable string key for this iteration
 * @property {Comment}          start       — `<!--ws-for:item:{key}:start-->` anchor
 * @property {Comment}          end         — `<!--ws-for:item:{key}:end-->` anchor
 * @property {Element}          root        — the cloned template root element
 * @property {EphemeralScope}   ephemeral   — scoped context for this iteration (prototype-linked to parent)
 * @property {Object}           loop        — `{index, key, first, last, length}` metadata object exposed as `loop` in template
 * @property {*}                item        — current item value from the source array/object
 * @property {number}           index       — current zero-based index
 * @property {number}           total       — total number of items in this render cycle
 * @property {() => void}       syncContext — push `item`/`index`/`loop` into ephemeral scope and mark dirty if changed
 * @property {ComponentChannel} provider    — per-instance provider for `item`/`index`/`loop` to child components
 * @property {OwnedResources}   owned       — lifetime resources (channel, bindings, ephemeral disposal)
 * @property {boolean}          [_needsCollect] — `true` for hydrated instances awaiting first `collectDeferred()`
 * @property {number}           [_seenTick]     — render-tick counter for stale-instance detection
 */

;class LifecycleEngine {
	/**
	 * @param {Object}              options
	 * @param {() => boolean}       options.isConnected
	 * @param {() => HTMLElement}   options.getHost
	 * @param {LifecycleHub}        options.hub
	 * @param {number}              [options.traceLimit=30]
	 */
	constructor({
					isConnected,
					getHost,
					hub,
					traceLimit = 30,
				}) {
		/** @type {() => boolean} */
		this._isConnected = isConnected;

		/** @type {() => HTMLElement} */
		this._getHost = getHost;

		/** @type {LifecycleHub} */
		this._hub = hub;

		/** @type {LifecycleState} */
		this._state = /** @type {LifecycleState} */ ('constructed');
		this._hub.emit('constructed');

		/** @type {boolean} */
		this._mountedOnce = false;

		/** @type {boolean} */
		this._dirty = false;

		/** @type {Set<string>} */
		this._reasons = new Set();

		/** @type {number} */
		this._invalidations = 0;

		/** @type {boolean} */
		this._flushing = false;

		/** @type {TraceEntry[]} */
		this._trace = [];

		/** @type {number} */
		this._traceLimit = traceLimit;
	}

	/** @returns {void} */
	onConnected() {
		if (this._isState('connected') || this._isState('mounted')) {
			return;
		}
		this._setState('connected');
		this._hub.emit('connected');
		this.invalidate('connected', LifecycleInvalidations.NONE);
	}

	/** @returns {void} */
	onDisconnected() {
		if (this._isState('disconnected')) {
			return;
		}

		if (this._mountedOnce) {
			this._hub.emit('unmount:before');
			this._hub.emit('unmount');
			this._hub.emit('unmount:after');
		}

		this._dirty = false;
		this._reasons.clear();
		this._invalidations = 0;

		this._setState('disconnected');
		this._hub.emit('disconnected');
	}

	/** @returns {void} */
	onRemove() {
		this._hub.emit('remove');
	}

	/**
	 * @param {string} [reason='unknown']
	 * @param {number} [invalidationMask=0] — bitmask of `LifecycleInvalidations` flags
	 * @returns {void}
	 */
	invalidate(reason = 'unknown', invalidationMask = 0) {
		if (this._isState('disconnected')) {
			return;
		}

		this._dirty = true;
		this._reasons.add(reason);
		this._invalidations |= invalidationMask;

		this._pushTrace(reason);
		ComponentsScheduler.getInstance().markDirty(this, reason);
	}

	/** @returns {void} */
	flush() {
		if (!this._isConnected()) {
			this._dirty = false;
			this._reasons.clear();
			this._invalidations = 0;
			return;
		}
		if (this._flushing) return;
		if (!this._dirty) return;

		const reasons = Array.from(this._reasons);
		const invalidations = this._invalidations;

		this._dirty = false;
		this._reasons.clear();
		this._invalidations = 0;

		try {
			this._flushing = true;

			if (!this._mountedOnce) {
				this._hub.emit('mount:before', {reasons});
				this._hub.emit('mount', {reasons});
				this._mountedOnce = true;
				this._setState('mounted');
				this._hub.emit('mount:after', {reasons});
				return;
			}

			this._hub.emit('update:before', {reasons, invalidations});
			this._hub.emit('update', {reasons, invalidations});
			this._hub.emit('update:after', {reasons, invalidations});
		} finally {
			this._flushing = false;
		}
	}

	/**
	 * @param {number} flushesInTurn
	 * @param {number} maxFlushesPerTurn
	 * @returns {void}
	 */
	onBurstLimitReached(flushesInTurn, maxFlushesPerTurn) {
		/** @type {LifecycleBlockedPayload} */
		const info = {
			type: 'burst-limit',
			flushesInTurn,
			maxFlushesPerTurn,
			trace: this.getTrace(),
		};
		this._hub.emit('blocked', info);
	}

	/** @returns {HTMLElement} */
	getHost() {
		return this._getHost();
	}

	/** @returns {TraceEntry[]} */
	getTrace() {
		return this._trace.slice();
	}

	/** @returns {LifecycleState} */
	getState() {
		return this._state;
	}

	/** @returns {boolean} */
	isInitialized() {
		return this._mountedOnce;
	}

	/**
	 * @param {string} reason
	 * @returns {void}
	 */
	_pushTrace(reason) {
		this._trace.push({at: performance.now(), reason});
		if (this._trace.length > this._traceLimit) this._trace.shift();
	}

	/**
	 * @param {LifecycleState} newState
	 * @returns {void}
	 */
	_setState(newState) {
		this._state = newState;
	}

	/**
	 * @param {LifecycleState} state
	 * @returns {boolean}
	 */
	_isState(state) {
		return this._state === state;
	}
}


class LifecycleHub {
	constructor() {
		/** @type {Map<string, Set<LifecycleHandler>>} */
		this._handlersByPhase = new Map();
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void} unsubscribe function
	 */
	on(phase, handler) {
		let handlers = this._handlersByPhase.get(phase);
		if (!handlers) this._handlersByPhase.set(phase, (handlers = new Set()));
		handlers.add(handler);
		return () => this.off(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void} unsubscribe function
	 */
	once(phase, handler) {
		const unsubscribe = this.on(phase, (...args) => {
			unsubscribe();
			handler(...args);
		});
		return unsubscribe;
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {void}
	 */
	off(phase, handler) {
		const handlers = this._handlersByPhase.get(phase);
		if (!handlers) return;
		handlers.delete(handler);
		if (handlers.size === 0) this._handlersByPhase.delete(phase);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecyclePayload} [payload]
	 * @returns {void}
	 */
	emit(phase, payload) {
		const handlers = this._handlersByPhase.get(phase);
		if (!handlers || handlers.size === 0) return;

		for (const handler of Array.from(handlers)) {
			handler(payload);
		}
	}

	/** @returns {void} */
	clear() {
		this._handlersByPhase.clear();
	}
}

/**
 * Bitmask flags for selective invalidation of component subsystems.
 * Combine with bitwise OR: `LifecycleInvalidations.PROPS | LifecycleInvalidations.ATTRS`
 *
 * @readonly
 * @enum {number}
 */
const LifecycleInvalidations = Object.freeze({
	/** No subsystem invalidated; triggers a flush with zero invalidation bits */
	NONE:     0,
	/** State props changed via `PropsManager` */
	PROPS:    1 << 0,   // 1
	/** DOM attributes changed (observed or runtime-observed) */
	ATTRS:    1 << 1,   // 2
	/** Slot content was replaced or appended */
	SLOTS:    1 << 2,   // 4
	/** A `provided` value changed upstream */
	PROVIDED: 1 << 3,   // 8
	/** Binding topology changed (e.g. `ws-html` rebuilt subtree); forces full re-collect */
	BINDINGS: 1 << 4,   // 16
	/** Bypass dirty-key optimization — force-update every binding */
	FORCE:    1 << 30,  // 1073741824
});

class ComponentsScheduler extends SingletonMixin() {

	/**
	 * @param {Object} [options]
	 * @param {number}  [options.maxFlushesPerTurn=25] — per-engine safety limit within one macro-turn
	 * @param {number}  [options.yieldEvery=200]       — yield to main thread every N flushes via `setTimeout(0)`
	 */
	constructor({
					maxFlushesPerTurn = 25,
					yieldEvery = 200,
				} = {}) {
		super();

		/** @type {number} */
		this._maxFlushesPerTurn = maxFlushesPerTurn;

		/** @type {number} */
		this._yieldEvery = yieldEvery;

		/** @type {Set<LifecycleEngine>} */
		this._dirtyEngines = new Set();

		/** @type {Map<LifecycleEngine, number>} */
		this._flushesInTurn = new Map();

		/** @type {boolean} */
		this._scheduled = false;

		/** @type {boolean} */
		this._turnResetScheduled = false;

		/** @type {Set<WeakRef<AfterFlushCallback>>} */
		this._afterFlushHooks = new Set();
	}

	/**
	 * @param {LifecycleEngine} engine
	 * @param {string}          reason
	 * @returns {void}
	 */
	markDirty(engine, reason) {
		this._dirtyEngines.add(engine);
		this._scheduleFlush(reason);
	}

	/**
	 * @param {AfterFlushCallback} callback
	 * @returns {() => void} unsubscribe function
	 */
	onAfterFlush(callback) {
		const ref = new WeakRef(callback);
		this._afterFlushHooks.add(ref);
		return () => this._afterFlushHooks.delete(ref);
	}

	/**
	 * @param {string} _reason
	 * @returns {void}
	 */
	_scheduleFlush(_reason) {
		this._scheduleTurnResetOnce();

		if (this._scheduled) return;
		this._scheduled = true;

		queueMicrotask(() => {
			this._scheduled = false;
			this._flushAll();
		});
	}

	/** @returns {Promise<void>} */
	async _flushAll() {
		if (this._dirtyEngines.size === 0) return;

		const sorted = this._sortByDomOrder(Array.from(this._dirtyEngines));
		this._dirtyEngines.clear();

		for (let i = 0; i < sorted.length; i++) {
			const engine = sorted[i];
			const flushes = (this._flushesInTurn.get(engine) || 0) + 1;
			this._flushesInTurn.set(engine, flushes);

			if (flushes > this._maxFlushesPerTurn) {
				engine.onBurstLimitReached(flushes, this._maxFlushesPerTurn);
				continue;
			}

			engine.flush();

			if (i % this._yieldEvery === 0) await new Promise(resolve => setTimeout(resolve, 0));
		}

		this._notifyAfterFlushHooks({ engines: sorted, duration: 0 });
	}

	/**
	 * @param {AfterFlushPayload} payload
	 * @returns {void}
	 */
	_notifyAfterFlushHooks(payload) {
		if (this._afterFlushHooks.size === 0) return;

		for (const ref of this._afterFlushHooks) {
			const callback = ref.deref();
			if (!callback) {
				this._afterFlushHooks.delete(ref);
				continue;
			}
			try {
				callback(payload);
			} catch (error) {
				console.error('[ComponentsScheduler] afterFlush hook error', error);
			}
		}
	}

	/**
	 * @param {LifecycleEngine[]} engines
	 * @returns {LifecycleEngine[]}
	 */
	_sortByDomOrder(engines) {
		return engines.sort((a, b) => {
			const hostA = a.getHost();
			const hostB = b.getHost();
			if (!hostA || !hostB) return 0;

			const position = hostA.compareDocumentPosition(hostB);

			if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
			if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
			return 0;
		});
	}

	/** @returns {void} */
	_scheduleTurnResetOnce() {
		if (this._turnResetScheduled) return;
		this._turnResetScheduled = true;

		setTimeout(() => {
			this._flushesInTurn.clear();
			this._turnResetScheduled = false;
		}, 0);
	}
}

App.classes.components_scheduler = ComponentsScheduler;

App.classes.components.lifecycle_engine = LifecycleEngine;
App.classes.components.lifecycle_hub = LifecycleHub;
App.classes.components.lifecycle_invalidations = LifecycleInvalidations;

/**
 * @typedef {'constructed' | 'connected' | 'mounted' | 'disconnected'} LifecycleState
 */

/**
 * @typedef {'constructed'
 *   | 'connected'
 *   | 'disconnected'
 *   | 'mount:before'
 *   | 'mount'
 *   | 'mount:after'
 *   | 'update:before'
 *   | 'update'
 *   | 'update:after'
 *   | 'unmount:before'
 *   | 'unmount'
 *   | 'unmount:after'
 *   | 'remove'
 *   | 'blocked'
 * } LifecyclePhase
 */

/**
 * @typedef {Object} LifecycleMountPayload
 * @property {string[]} reasons
 */

/**
 * @typedef {Object} LifecycleUpdatePayload
 * @property {string[]} reasons
 * @property {number}   invalidations
 */

/**
 * @typedef {Object} LifecycleBlockedPayload
 * @property {'burst-limit'}  type
 * @property {number}         flushesInTurn
 * @property {number}         maxFlushesPerTurn
 * @property {TraceEntry[]}   trace
 */

/**
 * @typedef {LifecycleMountPayload | LifecycleUpdatePayload | LifecycleBlockedPayload | undefined} LifecyclePayload
 */

/**
 * @callback LifecycleHandler
 * @param {LifecyclePayload} [payload]
 * @returns {void}
 */

/**
 * @typedef {Object} LifecycleRegister
 * @property {(phase: LifecyclePhase, handler: LifecycleHandler) => (() => void)} on
 * @property {(phase: LifecyclePhase, handler: LifecycleHandler) => (() => void)} once
 * @property {(reason: string, invalidationMask?: number) => void} invalidate
 */

/**
 * @callback LifecycleLayer
 * @this RenderableComponent
 * @param {LifecycleRegister} register
 * @returns {void}
 */

/**
 * @typedef {Object} AfterFlushPayload
 * @property {LifecycleEngine[]} engines
 * @property {number}            duration
 */

/**
 * @callback AfterFlushCallback
 * @param {AfterFlushPayload} payload
 * @returns {void}
 */

/**
 * @typedef {Object} TraceEntry
 * @property {number} at
 * @property {string} reason
 */
;class OwnedResources {
	/**
	 * @param {string} [label]
	 */
	constructor(label = '') {
		/** @type {string} */
		this._label = label;

		/** @type {DisposerEntry[]} */
		this._disposers = [];

		/** @type {boolean} */
		this._released = false;
	}

	/**
	 * @param {string}     label
	 * @param {() => void} disposer
	 * @returns {void}
	 */
	add(label, disposer) {
		if (typeof disposer !== 'function') {
			console.warn(`[OwnedResources:${this._label}] add("${label}") — disposer is not a function`);
			return;
		}

		if (this._released) {
			try { disposer(); } catch (e) { console.error(`[OwnedResources:${this._label}] disposer "${label}" threw after release`, e); }
			return;
		}

		this._disposers.push({label, disposer});
	}

	/** @returns {void} */
	release() {
		if (this._released) return;
		this._released = true;

		for (let i = this._disposers.length - 1; i >= 0; i--) {
			const {label, disposer} = this._disposers[i];
			try {
				disposer();
			} catch (e) {
				console.error(`[OwnedResources:${this._label}] disposer "${label}" threw`, e);
			}
		}

		this._disposers = [];
	}

	/** @returns {string[]} */
	inspect() {
		if (this._released) return [`[released]`];
		return this._disposers.map(d => d.label);
	}

	/** @returns {boolean} */
	get isReleased() {
		return this._released;
	}
}

App.classes.owned_resources = OwnedResources;

/**
 * @typedef {Object} DisposerEntry
 * @property {string}     label    — human-readable identifier for debug/inspect output
 * @property {() => void} disposer — teardown callback; executed in reverse-registration order on `release()`
 */
;App.classes = App.classes || {};

class PropsManager {
	/**
	 * @param {RenderableComponent & HTMLElement} component
	 * @param {Object}                            [options]
	 * @param {PropAttrReader}                    [options.readAttr]
	 * @param {PropInvalidator}                   [options.invalidateComponent]
	 */
	constructor(component, {readAttr, invalidateComponent} = {}) {
		/** @type {RenderableComponent & HTMLElement} */
		this._component = component;

		/** @type {PropAttrReader} */
		this._readAttr = readAttr || ((name, fallback) => {
			return component.hasAttribute(name) ? component.getAttribute(name) : fallback;
		});

		/** @type {PropInvalidator} */
		this._invalidateComponent = invalidateComponent;

		/** @type {boolean} */
		this._isInitialized = false;

		/** @type {Object<string, *>} */
		this._stateProps = {};
	}

	/** @returns {void} */
	initOnce() {
		if (this._isInitialized) return;
		this._isInitialized = true;

		this.mergeState(this._readInitialJsonPropsFromDom() || {})
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	get(name, fallback = null) {
		if (this._component.hasAttribute(name)) {
			return this._readAttr(name, fallback);
		}

		if (Object.prototype.hasOwnProperty.call(this._stateProps, name)) {
			return this._stateProps[name];
		}

		return fallback;
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [fallback=false]
	 * @returns {boolean}
	 */
	getBool(name, fallback = false) {
		const raw = this.get(name, null);
		if (raw == null) return fallback;
		return App.helper.parseBoolean(raw);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	getInt(name, fallback = 0) {
		const raw = this.get(name, null);
		if (raw == null || raw === '') return fallback;
		const parsed = parseInt(String(raw), 10);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	getFloat(name, fallback = 0) {
		const raw = this.get(name, null);
		if (raw == null || raw === '') return fallback;
		const parsed = parseFloat(String(raw));
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getJson(name, fallback = null) {
		const value = this.get(name, null);
		if (value && typeof value === 'object') return value;
		if (value == null || value === '') return fallback;
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (!trimmed) return fallback;

			try {
				return JSON.parse(trimmed);
			} catch (_) {
				return fallback;
			}
		}

		return fallback;
	}

	/** @returns {Object<string, *>} */
	getAll() {
		return this._stateProps;
	}

	/**
	 * @param {string}         name
	 * @param {*}              value
	 * @param {SetPropOptions} [options]
	 * @returns {void}
	 */
	set(name, value, options = {}) {
		const {to = 'auto', silent = false} = options;

		/** @type {PropTarget} */
		const target = (to === 'auto')
			? this._chooseTargetForName(name)
			: to;

		if (target === 'attr') {
			this._writeToAttribute(name, value);
			return;
		}

		const oldValue = this._stateProps[name];
		this._stateProps[name] = value;

		if (!silent && oldValue !== value) {
			this._invalidateComponent(`props:set:${name}`);
		}
	}

	/**
	 * @param {Object<string, *>} nextStateProps
	 * @param {string}            [reason='replaceState']
	 * @returns {void}
	 */
	replaceState(nextStateProps, reason = 'replaceState') {
		this._stateProps = App.helper.object.sanitize(nextStateProps);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {Object<string, *>} partialStateProps
	 * @param {string}            [reason='assignState']
	 * @returns {void}
	 */
	assignState(partialStateProps, reason = 'assignState') {
		if (!App.helper.object.is_plain(partialStateProps)) return;
		Object.assign(this._stateProps, partialStateProps);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @returns {boolean} — `true` if at least one value changed (`!==` comparison)
	 */
	mergeState(partial) {
		let changed = false;
		for (const key in partial) {
			const next = partial[key];
			if (this.get(key) !== next) {
				this.set(key, next);
				changed = true;
			}
		}
		return changed;
	}

	/**
	 * @param {string} path
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getStatePath(path, fallback = null) {
		return App.helper.object.readByPath(this._stateProps, path, fallback);
	}

	/**
	 * @param {string} path
	 * @param {*}      value
	 * @param {string} [reason='setStatePath']
	 * @returns {void}
	 */
	setStatePath(path, value, reason = 'setStatePath') {
		App.helper.object.setByPath(this._stateProps, path, value);
		this._invalidateComponent(`props:${reason}`);
	}

	/**
	 * @param {string} name
	 * @returns {PropTarget}
	 */
	_chooseTargetForName(name) {
		if (this._component.constructor.observedAttributes?.includes(name) || this._component.hasAttribute(name)) {
			return 'attr';
		}

		return 'data';
	}

	/**
	 * @param {string} name
	 * @param {*}      value
	 * @returns {void}
	 */
	_writeToAttribute(name, value) {
		if (value == null || value === false) {
			this._component.removeAttribute(name);
			return;
		}

		if (value === true) {
			this._component.setAttribute(name, 'true');
			return;
		}

		this._component.setAttribute(name, String(value));
	}

	/**
	 * @returns {Object<string, *>}
	 */
	_readInitialJsonPropsFromDom() {
		const scriptNode = this._component.querySelector(':scope > script[data-ws-props]');
		if (!scriptNode) return {};

		const rawJson = (scriptNode.textContent || '').trim();
		scriptNode.remove();

		if (!rawJson) return {};

		try {
			const parsed = JSON.parse(rawJson);
			return App.helper.object.sanitize(parsed);
		} catch (error) {
			console.error('Invalid ws-props JSON payload in component', this._component, error);
			return {};
		}
	}
}

App.classes.props_manager = PropsManager;

/**
 * @typedef {Object} SetPropOptions
 * @property {PropTarget} [to='auto']   — routing strategy: `'auto'` picks `attr` if the attribute exists
 *                                        or is in `observedAttributes`, otherwise `'data'`
 * @property {boolean}    [silent=false] — when `true`, skip invalidation even if the value changed
 */

/**
 * @typedef {'auto' | 'attr' | 'data'} PropTarget
 * `'attr'`  — write to DOM attribute via `setAttribute`/`removeAttribute`
 * `'data'`  — write to internal `_stateProps` object
 * `'auto'`  — resolved at runtime: `'attr'` if attribute exists or is in `observedAttributes`, else `'data'`
 */

/**
 * @callback PropAttrReader
 * @param {string} name     — attribute name
 * @param {*}      fallback — value to return when the attribute is absent
 * @returns {*}               attribute value (possibly transformed by `AttributeObserver` modifiers)
 */

/**
 * @callback PropInvalidator
 * @param {string} reason — invalidation reason string (e.g. `'props:set:title'`, `'props:replaceState'`)
 * @returns {void}
 */

;App.classes = App.classes || {};

class RenderableComponent extends HasRefsMixin(HasEventCallbacksMixin(HTMLElement)) {

	/**
	 * @type {{
	 *   owned: OwnedResources | null,
	 *   scope: ComponentScope | null,
	 *   slotManager: SlotManager | null,
	 *   bindingManager: BindingManager | null,
	 *   deepProvider: DeepProvider | null,
	 *   propsManager: PropsManager | null,
	 *   attributeObserver: AttributeObserver | null,
	 *   lifecycle: LifecycleHub | null,
	 *   lifecycleEngine: LifecycleEngine | null,
	 * }}
	 */
	_m = {
		owned: null,
		scope: null,
		slotManager: null,
		bindingManager: null,
		deepProvider: null,
		propsManager: null,
		attributeObserver: null,
		lifecycle: null,
		lifecycleEngine: null,
	}


	constructor() {
		super();
		this._attachLifecycleCoreHandlers();
		this._attachLifecycleSubclassHandlers();
	}


	/** @returns {void} */
	connectedCallback() {
		this.lifecycleEngine.onConnected();
	}

	/** @returns {void} */
	disconnectedCallback() {
		this.lifecycleEngine.onDisconnected();
	}

	/** @returns {void} */
	remove() {
		super.remove();
		this.lifecycleEngine.onRemove();
	}

	/** @returns {void} */
	_attachLifecycleCoreHandlers() {

		this.useConnection('wakeUpSleepBindings', {
			connect:  () => this.bindingManager.wakeupAll(),
			disconnect: () => this.bindingManager.sleepAll()
		})

		this.onceLifecycle('connected', () => {
			this.owned.add('bindingManager', () => this.bindingManager.disposeAll());
			this.owned.add('deepProvider', () => this.deepProvider.dispose());
			this.owned.add('scope', () => this.scope.dispose());
			this.owned.add('lifecycle', () => this.lifecycle.clear());

			this.propsManager.initOnce();
			this.attributeObserver.parseObserveAttributesAttribute();
			this.provideMany(this.provides());
		});

		this.onceLifecycle('mount', () => {
			this._initScope();

			if (this.hasAttribute('ws-server')) {
				this._hydrateDOM();
			} else {
				this._renderDOM();
			}

			this._applyBindings(null, true);
			this.deepProvider.notifySubscribers();
		});

		this.onLifecycle('update', ({invalidations}) => {
			const dirtyKeys = this._computeDirtyKeys(invalidations);
			this._applyBindings(dirtyKeys);
			this.deepProvider.notifySubscribers();
		});
		this.onLifecycle('unmount', () => {
			this.bindingManager.sleepAll();
			this.deepProvider.disposeConsumers();
		});
		this.onLifecycle('remove', () => {
			this.owned.release();
		});
	}

	/** @returns {void} */
	_initScope() {
		this.scope.addStaticSource('base', () => this.#baseTemplateContext());
		this.scope.addSource('props', () => this.propsManager.getAll());
		this.scope.addSource('template', () => this.templateContext());
		this.scope.setStatic('provided', this.deepProvider.getProvidedProxy(
			() => this.invalidateLifecycle('providedChanged', LifecycleInvalidations.PROVIDED)
		));
		this.scope.update();
	}

	/**
	 * @param {number} invalidations
	 * @returns {Set<string> | null}
	 */
	_computeDirtyKeys(invalidations) {
		if (invalidations & LifecycleInvalidations.FORCE) return null;
		if (invalidations & LifecycleInvalidations.BINDINGS) return null;

		const changed = this.scope.update();
		if (invalidations & LifecycleInvalidations.PROVIDED) changed.add('provided');

		return changed;
	}

	/**
	 * @param {Set<string> | null} [dirtyKeys=null]
	 * @param {boolean}            [skipTracking=false]
	 * @returns {void}
	 */
	_applyBindings(dirtyKeys = null, skipTracking = false) {
		this.bindingManager.updateAll(this.scope.context, dirtyKeys, skipTracking);
	}

	/** @returns {void} */
	_renderDOM() {
		this.slotManager.captureOriginalChildren();
		const rawTemplate = this.template();
		/** @type {Element} */
		let selfElement;
		if (this.constructor.precompile) {
			selfElement = this._resolveTemplateNode(rawTemplate, this.scope.context);
		} else {
			selfElement = Node.fromSingle(App.helper.template.fill(rawTemplate, this.scope.context).trim());
		}
		if (!selfElement) throw new Error('Template must contain <self> as root');

		App.helper.dom.merge_attributes(this, selfElement);

		const fragment = document.createDocumentFragment();
		while (selfElement.firstChild) fragment.append(selfElement.firstChild);
		this.replaceChildren(fragment);

		this.slotManager.mountSlots();
		this.slotManager.read().length > 0 && this.scope.update();

		if (this.constructor.precompile) {
			this.bindingManager.executePlan(this._getOrBuildPlan(), this);
			for (const [, {start, end}] of this.slotManager.anchors()) {
				this.bindingManager.collectInRange(start, end);
			}
		} else {
			this.bindingManager.rebuildAll();
		}
	}

	/** @returns {void} */
	_hydrateDOM() {
		this.slotManager.hydrateSlots();
		if (this.constructor.precompile) {
			App.classes.directive_registry.getInstance().normalizeForHydration(this);
			this.bindingManager.executePlan(this._getOrBuildPlan(), this);
			for (const [, {start, end}] of this.slotManager.anchors()) {
				this.bindingManager.collectInRange(start, end);
			}
		} else {
			this.bindingManager.rebuildAll();
		}

		this.removeAttribute('ws-server');
	}

	/**
	 * @param {string} rawTemplate
	 * @param {Object} context
	 * @returns {Element}
	 */
	_resolveTemplateNode(rawTemplate, context) {
		const cache = this.constructor._templateCache ??= {deps: null, nodes: new Map()};

		if (!cache.deps) {
			/** @type {Set<string>} */
			const deps = new Set();
			const tracked = new Proxy(context, {
				get(target, key) {
					if (typeof key === 'string') deps.add(key);
					return Reflect.get(target, key);
				}
			});

			const rendered = App.helper.template.fill(rawTemplate, tracked).trim();
			const node = Node.fromSingle(rendered);

			cache.deps = deps;
			cache.nodes.set(this._templateCacheKey(context, deps), node);
			return node.cloneNode(true);
		}

		const key = this._templateCacheKey(context, cache.deps);
		let node = cache.nodes.get(key);

		if (!node) {
			node = Node.fromSingle(App.helper.template.fill(rawTemplate, context).trim());
			cache.nodes.set(key, node);
		}

		return node.cloneNode(true);
	}

	/**
	 * @param {Object}      context
	 * @param {Set<string>} deps
	 * @returns {string}
	 */
	_templateCacheKey(context, deps) {
		if (this.constructor.templateVariantKey) {
			return String(this.constructor.templateVariantKey(context));
		}

		let key = '';
		deps.forEach(dep => {
			key += `${dep}:${context[dep]}\x00`;
		});
		return key;
	}

	/** @returns {PlanEntry[]} */
	_getOrBuildPlan() {
		const ctor = this.constructor;
		/** @type {Map<string, PlanEntry[]>} */
		const plans = ctor._bindingPlans ??= new Map();
		const raw = this.template();

		if (!ctor._templateCache) {
			this._resolveTemplateNode(raw, this.scope.context);
		}

		const key = this._templateCacheKey(this.scope.context, ctor._templateCache.deps);

		if (!plans.has(key)) {
			const node = this._resolveTemplateNode(raw, this.scope.context);
			plans.set(key, App.classes.directive_registry.getInstance()
				.buildBindingPlan(node, 'ws-host-', 'ws-'));
		}
		return plans.get(key);
	}

	/** @returns {void} */
	_attachLifecycleSubclassHandlers() {
		/** @type {LifecycleRegister} */
		const register = {
			on: (phase, handler) => this.onLifecycle(phase, handler),
			once: (phase, handler) => this.onceLifecycle(phase, handler),
			invalidate: (reason, mask) => this.invalidateLifecycle(reason, mask),
		};
		/** @type {Function[]} */
		const constructors = [];
		let current = this.constructor;
		while (current && current !== RenderableComponent) {
			constructors.push(current);
			current = Object.getPrototypeOf(current);
		}
		constructors.reverse();

		for (const ctor of constructors) {
			const lifecycleFactory = ctor.defineLifecycle;
			if (typeof lifecycleFactory !== 'function') continue;
			/** @type {LifecycleLayer | Object<string, LifecycleHandler>} */
			const layer = lifecycleFactory.call(ctor);
			if (typeof layer === 'function') {
				layer.call(this, register);
				continue;
			}
			if (layer && typeof layer === 'object') {
				for (const [phase, handler] of Object.entries(layer)) {
					register.on(phase, handler.bind(this));
				}
			}
		}
	}


	/**
	 * @param {string} label
	 * @param {((this: RenderableComponent) => (() => void)) | ConnectionHandler} handler
	 * @returns {void}
	 */
	useConnection(label, handler) {
		/** @type {(() => void) | null} */
		let teardown = null;

		const isObject = typeof handler === 'object';
		const connectFn = isObject ? handler.connect : handler;
		const disconnectFn = isObject ? handler.disconnect : null;

		const activate = () => {
			teardown = connectFn.call(this);
		};

		const deactivate = () => {
			if (disconnectFn) {
				disconnectFn.call(this);
			} else if (teardown) {
				teardown();
			}
			teardown = null;
		};

		this.onLifecycle('connected', activate);
		this.onLifecycle('disconnected', deactivate);
		this.owned.add(label, deactivate);
		this.isConnected && activate();
	}


	/**
	 * @param {string}          label
	 * @param {Store}           store
	 * @param {UseStoreOptions} [options]
	 * @returns {void}
	 */
	useStore(label, store, options = {}) {
		const {scope, select, sync, invalidate} = options;
		const sourceKey = `store:${label}`;

		if (!select && !sync) {
			/**
			 * @param {ReactiveStatePendingMap} changeset
			 * @returns {void}
			 */
			const onNotify = (changeset) => {
				if (!invalidate) return;
				if (invalidate(changeset) === true) this.invalidateLifecycle(`${sourceKey}:changed`);
			};
			this.useConnection(sourceKey, () => {
				return scope
					? store.$watch(scope, onNotify)
					: store.$subscribe(onNotify);
			});
			return;
		}

		/** @type {Object<string, *>} */
		const stores = this._stores ??= Object.create(null);

		/** @returns {void} */
		const refresh = () => {
			let changed = false;

			if (select) {
				const next = select(store);
				if (next === stores[label]) return;
				stores[label] = next;
				changed = true;
			}

			if (sync) {
				const ref = select ? stores[label] : store;
				const data = sync(ref);
				if (data && typeof data === 'object') {
					changed = this.propsManager.mergeState(data) || changed;
				}
			}

			if (changed) this.invalidateLifecycle(`${sourceKey}:changed`);
		};

		this.useConnection(sourceKey, () => {
			refresh();
			return scope
				? store.$watch(scope, refresh)
				: store.$subscribe(refresh);
		});
	}

	/**
	 * @param {string} label
	 * @returns {*}
	 */
	store(label) {
		return this._stores?.[label];
	}


	/**
	 * @returns {string}
	 * @abstract
	 */
	template() {
		throw new Error('Implement template() in subclass');
	}

	/** @returns {Object} */
	templateContext() {
		return {};
	}

	/** @returns {Object} */
	bakeTemplateContext() {
		this.scope.update();
		return this.scope.context;
	}

	/** @returns {Object} */
	#baseTemplateContext() {
		return {
			is_mobile: RenderableComponent._isMobile ??= App.device.is_mobile(),
			locale: App.object_state.locale,
			$t: App.i18n.t,
		}
	}

	/**
	 * @returns {LifecycleLayer}
	 */
	static defineLifecycle() {}

	/** @returns {string[]} */
	static get observedAttributes() {
		return [];
	}

	/**
	 * @param {string} attributeName
	 * @returns {void}
	 */
	attributeChangedCallback(attributeName) {
		this.invalidateLifecycle(`attrChanged:${attributeName}`, LifecycleInvalidations.ATTRS);
	}

	/**
	 * @param {string} name
	 * @param {string} value
	 * @returns {void}
	 */
	setAttribute(name, value) {
		super.setAttribute(name, value);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`runtimeObservedAttr:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string} name
	 * @returns {void}
	 */
	removeAttribute(name) {
		super.removeAttribute(name);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`attrChanged:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [force]
	 * @returns {void}
	 */
	toggleAttribute(name, force) {
		super.toggleAttribute(name, force);
		if (this.attributeObserver.isObserved(name)) {
			this.invalidateLifecycle(`attrChanged:${name}`, LifecycleInvalidations.ATTRS);
		}
	}

	/**
	 * @param {string} name
	 * @param {*}      [def=null]
	 * @returns {*}
	 */
	attr(name, def = null) {
		const rawValue = this.hasAttribute(name) ? this.getAttribute(name) : def;
		return this.attributeObserver.applyModifiers(name, rawValue);
	}

	/** @returns {Object<string, Function | *>} */
	provides() {
		return {};
	}

	/** @returns {OwnedResources} */
	get owned() {
		return this._m.owned ||= new OwnedResources(this.constructor.name);
	}

	/** @returns {ComponentScope} */
	get scope() {
		return this._m.scope ||= new ComponentScope();
	}

	/** @returns {SlotManager} */
	get slotManager() {
		return this._m.slotManager ||= new SlotManager(this);
	}

	/** @returns {BindingManager} */
	get bindingManager() {
		return this._m.bindingManager ||= new BindingManager(this, {
			directiveRegistry: App.classes.directive_registry.getInstance(),
		});
	}

	/** @returns {DeepProvider} */
	get deepProvider() {
		return this._m.deepProvider ||= new DeepProvider(this);
	}

	/** @returns {PropsManager} */
	get propsManager() {
		return this._m.propsManager ||= new PropsManager(this, {
			invalidateComponent: (reason) => this.invalidateLifecycle(reason, LifecycleInvalidations.PROPS),
			readAttr: (name, fallback) => this.attr(name, fallback),
		});
	}

	/** @returns {AttributeObserver} */
	get attributeObserver() {
		return this._m.attributeObserver ||= new AttributeObserver(this, {
			runModifier: (modifierName, value) => App.helper.template.run_modifier(modifierName, value),
		});
	}

	/** @returns {LifecycleHub} */
	get lifecycle() {
		return this._m.lifecycle ||= new LifecycleHub({});
	}

	/** @returns {LifecycleEngine} */
	get lifecycleEngine() {
		return this._m.lifecycleEngine ||= new LifecycleEngine({
			isConnected: () => this.isConnected,
			getHost: () => this,
			hub: this.lifecycle,
		});
	}

	/**
	 * @param {BuildOptions} [options]
	 * @returns {RenderableComponent}
	 */
	static build({
					 content = '',
					 attributes = {},
					 props = null
				 } = {}) {

		const self = new this();

		Node.from(content).forEach(node => self.append(node));

		for (const [key, value] of Object.entries(attributes)) {
			self.setAttribute(key, value);
		}

		props && self.mergeProps(props);

		return self;
	}

	/**
	 * @param {string|null}                   [name=null]
	 * @param {string | Node | Node[] | null} [content=null]
	 * @returns {void}
	 */
	setSlot(name = null, content = null) {

		if (!this.isLifecycleInitialized()) {
			this.slotManager.override(name, content);
			this.invalidateLifecycle('setSlot:preinit');
			return;
		}

		const {start, end} = this.slotManager.getSlotAnchors(name);

		this.bindingManager.disposeInRange(start, end);
		this.slotManager.replace(name, content);
		this.bindingManager.collectInRange(start, end);

		this.invalidateLifecycle('setSlot:rebindSlotOnly');
	}

	/**
	 * @param {string|null}                   [name=null]
	 * @param {string | Node | Node[] | null} [content=null]
	 * @returns {void}
	 */
	appendToSlot(name = null, content = null) {
		if (content == null) throw new Error('Slot content must be provided');

		if (!this.isLifecycleInitialized()) {
			this.slotManager.append(name, content);
			this.invalidateLifecycle('appendToSlot:preinit');
			return;
		}

		const {appendedNodes} = this.slotManager.append(name, content);

		for (const appendedNode of appendedNodes) {
			if (appendedNode && appendedNode.nodeType === Node.ELEMENT_NODE) {
				this.bindingManager.collectInSubtree(appendedNode);
			}
		}

		this.invalidateLifecycle('appendToSlot:rebindNewOnly');
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void}
	 */
	onLifecycle(phase, handler) {
		return this.lifecycle.on(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {() => void}
	 */
	onceLifecycle(phase, handler) {
		return this.lifecycle.once(phase, handler);
	}

	/**
	 * @param {LifecyclePhase}   phase
	 * @param {LifecycleHandler} handler
	 * @returns {void}
	 */
	offLifecycle(phase, handler) {
		this.lifecycle.off(phase, handler);
	}

	/**
	 * @param {string} reason
	 * @param {number} [invalidationMask=0]
	 * @returns {void}
	 */
	invalidateLifecycle(reason, invalidationMask) {
		this.lifecycleEngine.invalidate(reason, invalidationMask);
	}

	/** @returns {boolean} */
	isLifecycleInitialized() {
		return this.lifecycleEngine.isInitialized();
	}

	/** @returns {LifecycleState} */
	getLifecycleState() {
		return this.lifecycleEngine.getState();
	}

	/** @returns {TraceEntry[]} */
	traceLifecycle() {
		return this.lifecycleEngine.getTrace();
	}

	/** @returns {Object<string, *>} */
	props() {
		return this.propsManager.getAll();
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	prop(name, fallback = null) {
		return this.propsManager.get(name, fallback);
	}

	/**
	 * @param {string}  name
	 * @param {boolean} [fallback=false]
	 * @returns {boolean}
	 */
	propBool(name, fallback = false) {
		return this.propsManager.getBool(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	propInt(name, fallback = 0) {
		return this.propsManager.getInt(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {number} [fallback=0]
	 * @returns {number}
	 */
	propFloat(name, fallback = 0) {
		return this.propsManager.getFloat(name, fallback);
	}

	/**
	 * @param {string} name
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	propJson(name, fallback = null) {
		return this.propsManager.getJson(name, fallback);
	}

	/**
	 * @param {string} path
	 * @param {*}      [fallback=null]
	 * @returns {*}
	 */
	getPropPath(path, fallback = null) {
		return this.propsManager.getStatePath(path, fallback);
	}

	/**
	 * @param {Object<string, *>} nextState
	 * @param {string}            [reason='replaceState']
	 * @returns {void}
	 */
	replaceProps(nextState, reason) {
		this.propsManager.replaceState(nextState, reason);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @param {string}            [reason='assignState']
	 * @returns {void}
	 */
	setProps(partial, reason) {
		this.propsManager.assignState(partial, reason);
	}

	/**
	 * @param {Object<string, *>} partial
	 * @returns {boolean}
	 */
	mergeProps(partial) {
		return this.propsManager.mergeState(partial);
	}

	/**
	 * @param {string}         name
	 * @param {*}              value
	 * @param {SetPropOptions} [options]
	 * @returns {void}
	 */
	setProp(name, value, options) {
		this.propsManager.set(name, value, options);
	}

	/**
	 * @param {string} path
	 * @param {*}      value
	 * @param {string} [reason='setStatePath']
	 * @returns {void}
	 */
	setPropPath(path, value, reason) {
		this.propsManager.setStatePath(path, value, reason);
	}

	/**
	 * @param {string}                 key
	 * @param {(request: Object) => *} resolver
	 * @returns {void}
	 */
	provide(key, resolver) {
		this.deepProvider.provide(key, resolver);
	}

	/**
	 * @param {Object<string, Function | *>} providesObject
	 * @returns {void}
	 */
	provideMany(providesObject) {
		this.deepProvider.provideMany(providesObject);
	}

	/**
	 * @param {string}  key
	 * @param {Object}  [request]
	 * @returns {*}
	 */
	useProvided(key, request) {
		return this.deepProvider.useProvided(key, request);
	}

	/**
	 * @param {string}                          key
	 * @param {(value: *) => (false | void)}    onUpdate
	 * @param {Object}                          [request]
	 * @returns {() => void}
	 */
	subscribeProvided(key, onUpdate, request) {
		return this.deepProvider.subscribeProvided(key, onUpdate, request);
	}

	/**
	 * @param {string|null} [slotName]
	 * @returns {Node[]}
	 */
	slot(slotName) {
		return this.slotManager.read(slotName);
	}

	/**
	 * @param {string}                   attributeName
	 * @param {ObserveAttributeOptions}  [options]
	 * @returns {void}
	 */
	observeAttribute(attributeName, options) {
		this.attributeObserver.observeAttribute(attributeName, options);
	}

}

App.classes.renderable_component = RenderableComponent;

/**
 * @typedef {Object} ConnectionHandler
 * @property {(this: RenderableComponent) => (() => void) | void} connect
 *           — called on `connected`; return a teardown function or `undefined`.
 *             When a teardown is returned, it is called on `disconnected` (unless `disconnect` is also provided)
 * @property {(this: RenderableComponent) => void} [disconnect]
 *           — explicit disconnect handler; when provided, `connect`'s return value is ignored for teardown
 */

/**
 * @typedef {Object} UseStoreOptions
 * @property {string} [scope]
 *           — store watch scope passed to `store.$watch(scope, callback)`;
 *             when omitted, `store.$subscribe(callback)` is used instead
 * @property {(store: Store) => *} [select]
 *           — selector: extract a reference from the store (e.g. a single record);
 *             the return value is cached and accessible via `this.store(label)`
 * @property {(ref: *) => Object<string,*>} [sync]
 *           — sync mapper: receives the selected ref (or the store itself if `select` is absent)
 *             and returns a plain object whose entries are merged into `_stateProps` via `mergeState()`
 * @property {(changeset: ReactiveStatePendingMap) => boolean} [invalidate]
 *           — notification filter (when neither `select` nor `sync` are provided);
 *             return `true` to trigger an invalidation, `false` to ignore the changeset
 */

/**
 * @typedef {Object} BuildOptions
 * @property {string}                  [content='']    — HTML string parsed via `Node.from()` and appended as children
 * @property {Object<string, string>}  [attributes={}] — key-value pairs set via `setAttribute` on the new element
 * @property {Object<string, *>|null}  [props=null]    — state props passed to `replaceProps()` after construction
 */
/**
 * @callback StoreNotifyCallback
 * @param {ReactiveStatePendingMap} changeset — map of changed keys to their new values
 * @returns {void}
 */

/**
 * @typedef {Map<string, *>} ReactiveStatePendingMap
 */

;class SlotManager {
	/**
	 * @param {RenderableComponent & HTMLElement} hostElement
	 */
	constructor(hostElement) {
		/** @type {RenderableComponent & HTMLElement} */
		this._hostElement = hostElement;

		/** @type {Map<string, SlotAnchors>} */
		this._anchorsByKey = new Map();

		/** @type {Map<string, Node[]>} */
		this._pendingOverridesByKey = new Map();

		/** @type {Map<string, Array<{name: string, value: string}>>} */
		this._slotTemplateAttributesByKey = new Map();

		/** @type {Node[]} */
		this._originalChildren = [];
	}

	/** @returns {void} */
	captureOriginalChildren() {
		this._originalChildren = Array.from(this._hostElement.childNodes);
	}

	/** @returns {void} */
	mountSlots() {
		this._anchorsByKey.clear();

		const defaultAssignmentsByKey = this._parseOriginalChildrenIntoAssignments(this._originalChildren);
		const slotElements = Array.from(this._hostElement.querySelectorAll('slot'));

		for (const slotElement of slotElements) {
			const key = this._normalizeSlotKey(slotElement.getAttribute('name'));

			const nodesForSlot = this._resolveNodesForSlot(key, defaultAssignmentsByKey);

			this._slotTemplateAttributesByKey.set(
				key,
				Array.from(slotElement.attributes).map(a => ({name: a.name, value: a.value}))
			);

			this._decorateSlotContentNodes(key, nodesForSlot);
			this._mountSingleSlot(slotElement, key, nodesForSlot);
		}

		this._pendingOverridesByKey.clear();
	}

	/** @returns {void} */
	hydrateSlots() {
		this._anchorsByKey.clear();
		this._scanForSlotAnchors(this._hostElement);
		this._pendingOverridesByKey.clear();
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @returns {Node[]}
	 */
	read(slotName = null) {
		const key = this._normalizeSlotKey(slotName);
		return this._readMountedSlotNodes(key);
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {string} — normalized slot key
	 */
	override(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		this._pendingOverridesByKey.set(key, this._normalizeContentToNodes(content));
		return key;
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {SlotReplaceResult}
	 */
	replace(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		const nodes = this._normalizeContentToNodes(content);

		const result = this._write(key, 'replace', nodes);
		return {changed: result.changed, key};
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @param {SlotContent}   content
	 * @returns {SlotAppendResult}
	 */
	append(slotName = null, content) {
		if (content == null) throw new Error('Slot content must be provided');

		const key = this._normalizeSlotKey(slotName);
		const nodes = this._normalizeContentToNodes(content);

		const result = this._write(key, 'append', nodes);
		return {changed: result.changed, key, appendedNodes: result.appendedNodes};
	}

	/**
	 * @param {string | null} [slotName=null]
	 * @returns {SlotAnchors | undefined}
	 */
	getSlotAnchors(slotName = null) {
		return this._anchorsByKey.get(this._normalizeSlotKey(slotName));
	}

	/** @returns {IterableIterator<[string, SlotAnchors]>} */
	anchors() {
		return this._anchorsByKey.entries();
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {Map<string, Node[]>}
	 */
	_parseOriginalChildrenIntoAssignments(nodes) {
		/** @type {Map<string, Node[]>} */
		const assignmentsByKey = new Map();

		for (const {slotName, node} of this._collectSlotAssignments(nodes)) {
			const key = this._normalizeSlotKey(slotName);
			if (!assignmentsByKey.has(key)) assignmentsByKey.set(key, []);
			assignmentsByKey.get(key).push(node);
		}

		return assignmentsByKey;
	}

	/**
	 * @param {string}              key
	 * @param {Map<string, Node[]>} defaultAssignmentsByKey
	 * @returns {Node[]}
	 */
	_resolveNodesForSlot(key, defaultAssignmentsByKey) {
		const overrideNodes = this._pendingOverridesByKey.get(key);
		if (overrideNodes) return overrideNodes.slice();

		return defaultAssignmentsByKey.get(key) || [];
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {SlotAssignment[]}
	 */
	_collectSlotAssignments(nodes) {
		/** @type {SlotAssignment[]} */
		const assignments = [];

		/**
		 * @param {string | null} slotName
		 * @param {Node}          node
		 */
		const push = (slotName, node) => {
			if (node) assignments.push({slotName, node});
		};

		for (const node of nodes) {
			if (node.nodeType === Node.ELEMENT_NODE) {
				const tag = node.tagName.toLowerCase();

				if (tag === 'slot-content') {
					const wrapperSlotName = node.getAttribute('slot') || null;
					for (const child of Array.from(node.childNodes)) {
						push(child.getAttribute?.('slot') ?? wrapperSlotName, child);
					}
					continue;
				}

				if (node.hasAttribute('slot')) {
					push(node.getAttribute('slot'), node);
					continue;
				}
			}

			push(null, node);
		}

		return assignments;
	}

	/**
	 * @param {Node} parent
	 * @returns {void}
	 */
	_scanForSlotAnchors(parent) {
		const START_PREFIX = 'ws-slot:start:';

		for (let node = parent.firstChild; node; node = node.nextSibling) {
			if (node.nodeType === Node.COMMENT_NODE && node.textContent.startsWith(START_PREFIX)) {
				const key = node.textContent.slice(START_PREFIX.length);
				const end = this._findMatchingEndAnchor(node, key);
				if (end) this._anchorsByKey.set(key, {start: node, end});
				continue;
			}

			if (node.nodeType === Node.ELEMENT_NODE && !(node instanceof RenderableComponent)) {
				this._scanForSlotAnchors(node);
			}
		}
	}

	/**
	 * @param {Comment} startNode
	 * @param {string}  key
	 * @returns {Comment | null}
	 */
	_findMatchingEndAnchor(startNode, key) {
		const target = `ws-slot:end:${key}`;
		for (let node = startNode.nextSibling; node; node = node.nextSibling) {
			if (node.nodeType === Node.COMMENT_NODE && node.textContent === target) {
				return /** @type {Comment} */ (node);
			}
		}
		return null;
	}

	/**
	 * @param {Element} slotElement
	 * @param {string}  key
	 * @param {Node[]}  nodes
	 * @returns {void}
	 */
	_mountSingleSlot(slotElement, key, nodes) {
		const start = document.createComment(`ws-slot:start:${key}`);
		const end = document.createComment(`ws-slot:end:${key}`);
		const parent = slotElement.parentNode;

		parent.insertBefore(start, slotElement);
		parent.insertBefore(end, slotElement);
		slotElement.remove();

		parent.insertBefore(this._createFragment(nodes), end);

		this._anchorsByKey.set(key, {start, end});
	}

	/**
	 * @param {string} key
	 * @returns {Node[]}
	 */
	_readMountedSlotNodes(key) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) return [];

		/** @type {Node[]} */
		const collected = [];
		this._iterateBetween(anchors.start, anchors.end, (node) => collected.push(node));
		return collected;
	}

	/**
	 * @param {string}             key
	 * @param {'replace' | 'append'} mode
	 * @param {Node[]}             nodes
	 * @returns {{changed: boolean, appendedNodes: Node[]}}
	 */
	_write(key, mode, nodes) {
		if (!this._anchorsByKey.has(key)) {
			if (mode === 'replace') {
				this._pendingOverridesByKey.set(key, nodes);
			} else {
				const existing = this._pendingOverridesByKey.get(key) || [];
				this._pendingOverridesByKey.set(key, existing.concat(nodes));
			}
			return {changed: false, appendedNodes: []};
		}

		this._decorateSlotContentNodes(key, nodes);

		if (mode === 'replace') {
			this._replaceMountedSlotNodes(key, nodes);
			return {changed: true, appendedNodes: []};
		}

		this._appendMountedSlotNodes(key, nodes);
		return {changed: true, appendedNodes: nodes};
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_replaceMountedSlotNodes(key, nodes) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) throw new Error(`Slot anchors not found for key: ${key}`);

		this._iterateBetween(anchors.start, anchors.end, (node) => node.parentNode?.removeChild(node));
		anchors.end.parentNode.insertBefore(this._createFragment(nodes), anchors.end);
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_appendMountedSlotNodes(key, nodes) {
		const anchors = this._anchorsByKey.get(key);
		if (!anchors) throw new Error(`Slot anchors not found for key: ${key}`);

		anchors.end.parentNode.insertBefore(this._createFragment(nodes), anchors.end);
	}

	/**
	 * @param {Node}                 start
	 * @param {Node}                 end
	 * @param {(node: Node) => void} visitor
	 * @returns {void}
	 */
	_iterateBetween(start, end, visitor) {
		for (let node = start.nextSibling; node && node !== end;) {
			const next = node.nextSibling;
			visitor(node);
			node = next;
		}
	}

	/**
	 * @param {string | null} name
	 * @returns {string}
	 */
	_normalizeSlotKey(name) {
		return name != null ? String(name) : '__default__';
	}

	/**
	 * @param {SlotContent} content
	 * @returns {Node[]}
	 */
	_normalizeContentToNodes(content) {
		if (content == null) return [];

		if (typeof content === 'string') return Node.from(content);
		if (Array.isArray(content)) return content.filter(Boolean);
		if (content instanceof Node) return [content];

		return [document.createTextNode(String(content))];
	}

	/**
	 * @param {Node[]} nodes
	 * @returns {DocumentFragment}
	 */
	_createFragment(nodes) {
		const fragment = document.createDocumentFragment();
		for (const node of nodes) fragment.appendChild(node);
		return fragment;
	}

	/**
	 * @param {string} key
	 * @param {Node[]} nodes
	 * @returns {void}
	 */
	_decorateSlotContentNodes(key, nodes) {
		const attrs = this._slotTemplateAttributesByKey.get(key);
		if (!attrs) return;

		for (const node of nodes) {
			if (node.nodeType !== Node.ELEMENT_NODE) continue;
			App.helper.dom.merge_attributes(node, Array.from(attrs), {exclude: ['name']});
		}
	}
}

App.classes.slot_manager = SlotManager;

/**
 * @typedef {Object} SlotAnchors
 * @property {Comment} start — `<!--ws-slot:start:{key}-->` comment node
 * @property {Comment} end   — `<!--ws-slot:end:{key}-->` comment node
 */

/**
 * @typedef {Object} SlotReplaceResult
 * @property {boolean} changed — `true` if anchors existed and DOM was mutated; `false` if stored as pending override
 * @property {string}  key     — normalized slot key
 */

/**
 * @typedef {Object} SlotAppendResult
 * @property {boolean} changed       — `true` if anchors existed and DOM was mutated
 * @property {string}  key           — normalized slot key
 * @property {Node[]}  appendedNodes — the nodes actually inserted into the DOM (empty if pre-mount)
 */

/**
 * @typedef {Object} SlotAssignment
 * @property {string | null} slotName — target slot name; `null` = default slot
 * @property {Node}          node     — the DOM node assigned to this slot
 */

/**
 * @typedef {string | Node | Node[] | null} SlotContent
 * Accepted slot content formats:
 * - `string`  — parsed into DOM nodes via `Node.from()`
 * - `Node`    — single DOM node wrapped into an array
 * - `Node[]`  — array of DOM nodes used directly (falsy entries filtered)
 * - `null`    — throws; callers must guard
 */
;class TeamStructureHistory {
    #store;
    #limit;
    #mergeWindowMs;
    #past;
    #future;
    #locked;
    #lastTag;
    #lastAt;
    #listeners;
    /**
     * @param {Object} [opts]
     * @param {number} [opts.limit=100]
     * @param {number} [opts.mergeWindowMs=0]
     */
    constructor(store, opts = {}) {
        this.#store = store;
        this.#limit = opts.limit ?? 100;
        this.#mergeWindowMs = opts.mergeWindowMs ?? 0;

        /** @type {Array<Object>} */
        this.#past = [];
        /** @type {Array<Object>} */
        this.#future = [];
        this.#locked = false;

        this.#lastTag = null;
        this.#lastAt = 0;

        /** @type {Set<() => void>} */
        this.#listeners = new Set();
    }

    get canUndo() {
        return this.#past.length > 0;
    }

    get canRedo() {
        return this.#future.length > 0;
    }

    /** @returns функцию отписки */
    onChange(fn) {
        this.#listeners.add(fn);
        return () => this.#listeners.delete(fn);
    }

    #notify() {
        TeamStructureUtils.emit(this.#listeners);
    }

    /**
     * @param {string} [tag]
     */
    do(fn, tag) {
        if (this.#locked) {
            fn();
            return;
        }
        const before = this.#store.tree();
        fn();
        this.#commit(before, tag);
    }

    /**
     * @param {string} [tag]
     */
    transaction(fn, tag) {
        if (this.#locked) {
            fn();
            return;
        }
        const before = this.#store.tree();
        this.#locked = true;
        try {
            fn();
        } finally {
            this.#locked = false;
        }
        this.#commit(before, tag);
    }

    #commit(before, tag) {
        const after = this.#store.tree();
        if (after === before) return;

        const now = Date.now();
        const mergeable =
            this.#mergeWindowMs > 0 &&
            tag != null &&
            tag === this.#lastTag &&
            now - this.#lastAt <= this.#mergeWindowMs &&
            this.#past.length > 0;

        if (!mergeable) {
            this.#past.push(before);
            if (this.#past.length > this.#limit) this.#past.shift();
        }

        this.#future.length = 0;
        this.#lastTag = tag ?? null;
        this.#lastAt = now;
        this.#notify();
    }

    undo() {
        if (!this.canUndo) return;
        const current = this.#store.tree();
        const prev = this.#past.pop();
        this.#future.push(current);

        this.#locked = true;
        this.#store.setTree(prev);
        this.#locked = false;

        this.#lastTag = null;
        this.#notify();
    }

    redo() {
        if (!this.canRedo) return;
        const current = this.#store.tree();
        const next = this.#future.pop();
        this.#past.push(current);

        this.#locked = true;
        this.#store.setTree(next);
        this.#locked = false;

        this.#lastTag = null;
        this.#notify();
    }

    clear() {
        this.#past.length = 0;
        this.#future.length = 0;
        this.#lastTag = null;
        this.#notify();
    }
}


;class TeamStructureInteractions {
    static #CHILD_BAND = 32;

    static #KINDS = {
        person: { groupable: true },
        group:  { groupable: false },
    };

    static #MODES = {
        inside:        { hover: "inside", apply: (self, c) => self.#applyInside(c) },
        child:         { hover: "slot",   apply: (self, c) => self.#moveUnder(c.sourceId, c.targetId, { displaceId: c.childId }) },
        "child-left":  { hover: "slot",   apply: (self, c) => self.#moveUnder(c.sourceId, c.targetId, { order: self.#sideOrder(c.targetId, "left") }) },
        "child-right": { hover: "slot",   apply: (self, c) => self.#moveUnder(c.sourceId, c.targetId, { order: self.#sideOrder(c.targetId, "right") }) },
    };

    static #GROUP_SOURCES = [
        { prefix: "group-group_name-", field: "id_group" },
        { prefix: "group-sub-subgroup_name-", field: "id_group_sub" },
    ];
    #store;
    #history;
    #renderer;
    #$viewport;
    #selectUser;
    #editGroupName;
    #openUserMenu;
    #confirmReset;
    #canEdit;
    #offNodesAdded;
    #dragOptions;
    #slotTimer;
    #slotOpenEl;
    #sidebarTimer;
    /** @param {TeamInteractionDeps} deps */
    constructor(deps) {
        this.#store = deps.store;
        this.#history = deps.history;
        this.#renderer = deps.renderer;
        this.#$viewport = deps.$viewport;
        this.#selectUser = deps.selectUser || null;
        this.#editGroupName = deps.editGroupName || null;
        this.#openUserMenu = deps.openUserMenu || null;
        this.#confirmReset = deps.confirmReset || null;
        this.#canEdit = deps.canEdit !== false;
        this.#offNodesAdded = null;
        this.#dragOptions = this.#buildDragOptions();
    }

    static #nodeIdOf(el) {
        return String($(el).closest(".teamStructure__node").data("node-id"));
    }

    static #sourceFor(groupId) {
        return TeamStructureInteractions.#GROUP_SOURCES.find((s) => groupId.startsWith(s.prefix)) || null;
    }

    static #usersByFilter(filter) {
        const team = App.state.team;
        const fields = Object.keys(filter);
        return team.ids().filter((uid) => {
            const u = team.get(uid);
            return fields.every((f) => Number(u[f]) === Number(filter[f]));
        });
    }

    static #decodeEntities(s) {
        const d = document.createElement("textarea");
        d.innerHTML = String(s == null ? "" : s);
        return d.value;
    }

    static #isSidePanelGroupTitle(el) {
        const $el = $(el);
        return $el.is(".sidePanel__list__group__title") || $el.closest(".sidePanel__list__group__title").length;
    }

    static #findGroupMember($el) {
        if ($el.is(".teamStructure__groupMember")) return $el;
        const $m = $el.closest(".teamStructure__groupMember");
        return $m.length ? $m : null;
    }

    static #targetNodeId(el) {
        return String($(el).closest(".teamStructure__node").data("node-id") || "") || null;
    }

    init() {
        this.#bindClicks();
        if (!this.#canEdit) return;
        this.#offNodesAdded = this.#renderer.onNodesAdded((els) => this.#bindNodes(els));
        this.#bindSidebar();
        this.#bindToolbar();
    }

    reinit() {
        this.destroy();
        this.init();
    }

    destroy() {
        if (this.#offNodesAdded) this.#offNodesAdded();
        clearTimeout(this.#slotTimer);
        clearTimeout(this.#sidebarTimer);
        this.#$viewport.off(".teamStructure");
        $(document).off(".teamStructure");
        $("#teamStructure__undo, #teamStructure__redo, #teamStructure__reset").off("click.teamStructure");
    }

    #payloadFromElement(el) {
        const $el = $(el);
        if ($el.is(".teamStructure__dropZone")) {
            return {type: "existing", id: TeamStructureInteractions.#nodeIdOf($el)};
        }
        const $member = TeamStructureInteractions.#findGroupMember($el);
        if ($member) {
            return {type: "existing", id: String($member.data("node-id"))};
        }
        if ($el.is(".teamStructure__node")) {
            return {type: "existing", id: String($el.data("node-id"))};
        }
        if (TeamStructureInteractions.#isSidePanelGroupTitle($el)) {
            return this.#groupFromElement(el);
        }
        return {
            type: "person",
            userId: String($el.data("user")),
        };
    }

    #groupFromElement(el) {
        const $self = $(el).closest("sidepanel-group");
        if (!$self.length) return null;
        const selfId = String($self.attr("group-id") || "");
        const selfSource = TeamStructureInteractions.#sourceFor(selfId);
        if (!selfSource) return null;
        if (!Number.isFinite(Number(selfId.slice(selfSource.prefix.length)))) return null;

        const filter = {};
        let $cur = $self;
        while ($cur.length) {
            const id = String($cur.attr("group-id") || "");
            const src = TeamStructureInteractions.#sourceFor(id);
            if (src) {
                const rawId = Number(id.slice(src.prefix.length));
                if (Number.isFinite(rawId)) filter[src.field] = rawId;
            }
            $cur = $cur.parent().closest("sidepanel-group");
        }

        const userIds = TeamStructureInteractions.#usersByFilter(filter);
        if (!userIds.length) return null;
        const label = TeamStructureInteractions.#decodeEntities($self.attr("group-title")).trim() || "Group";
        return {type: "group", label, userIds};
    }

    /**
     * @returns {"inside"|"child"}
     */
    #dropMode(event, $target) {
        const zoneMode = $target.data("drop-mode");
        if (zoneMode) return zoneMode;
        if (!$target.length) return "child";
        const node = this.#store.node(String($target.data("node-id")));
        if (!node || node.kind !== "group") return "child";
        const box = $target[0].getBoundingClientRect();
        const y = (event.originalEvent ? event.originalEvent.clientY : event.clientY) - box.top;
        return y < box.height - TeamStructureInteractions.#CHILD_BAND ? "inside" : "child";
    }

    #canDropInside(sourceNode, targetNode) {
        if (!sourceNode || !targetNode) return false;
        const sk = TeamStructureInteractions.#KINDS[sourceNode.kind];
        return !!(sk && sk.groupable) && !!TeamStructureInteractions.#KINDS[targetNode.kind];
    }


    #applyDrop(payload, targetId, mode, childId) {
        this.#history.transaction(() => {
            let target = targetId ? this.#store.node(targetId) : null;
            if (target && target.memberOf) {
                targetId = target.memberOf;
                target = this.#store.node(targetId);
                childId = null;
            }

            const sourceId = payload.type === "person"
                ? this.#store.addPerson({userId: payload.userId, parentId: null})
                : payload.type === "group"
                    ? this.#materializeGroup(payload.userIds, payload.label)
                    : payload.id;
            const source = this.#store.node(sourceId);
            if (!source) return;

            if (!target || sourceId === targetId) {
                if (source.memberOf) {
                    this.#extractFromGroup(sourceId);
                } else if (source.parentId === null) {
                    const roots = this.#store.roots().filter((r) => r !== sourceId);
                    if (roots.length) this.#linkUnder(roots[0], sourceId);
                }
                return;
            }

            const m = TeamStructureInteractions.#MODES[mode] || TeamStructureInteractions.#MODES.child;
            m.apply(this, {sourceId, targetId, childId, source, target});
        });
    }

    #materializeGroup(userIds, label) {
        const groupId = this.#store.addGroup({label: label || "Group", parentId: null});
        userIds.forEach((uid, i) => this.#store.addPerson({userId: String(uid), memberOf: groupId, order: i}));
        return groupId;
    }

    #applyInside(c) {
        if (c.target.kind === "group") {
            this.#setMember(c.sourceId, c.targetId);
            return;
        }
        if (this.#canDropInside(c.source, c.target)) {
            this.#groupTwo(c.target, c.source);
            return;
        }
        this.#moveUnder(c.sourceId, c.targetId, {displaceId: c.childId});
    }

    #linkUnder(parentId, childId, opts = {}) {
        if (parentId === childId) return;
        if (this.#store.subtree(childId).has(parentId)) return;
        const displace = opts.displaceId ? this.#store.node(opts.displaceId) : null;
        const order = displace ? displace.order : opts.order;
        this.#store.reparent({id: childId, newParentId: parentId, order});
        if (displace && displace.id !== childId) {
            this.#store.reparent({id: displace.id, newParentId: childId});
        }
    }

    #moveUnder(sourceId, targetId, opts = {}) {
        const source = this.#store.node(sourceId);
        const target = this.#store.node(targetId);
        if (!source || !target || sourceId === targetId) return;
        const formerGroupId = source.memberOf || null;
        this.#stitchGap(sourceId, source.memberOf ? null : source.parentId);
        this.#linkUnder(targetId, sourceId, opts);
        if (formerGroupId) this.#maybeDissolveGroup(formerGroupId);
    }

    #stitchGap(nodeId, formerParentId) {
        const children = this.#store.children(nodeId);
        if (!children.length) return;
        if (formerParentId !== null) {
            for (const c of children) this.#store.reparent({id: c.id, newParentId: formerParentId});
        } else {
            const [first, ...rest] = children;
            this.#store.reparent({id: first.id, newParentId: null});
            for (const c of rest) this.#store.reparent({id: c.id, newParentId: first.id});
        }
    }

    #sideOrder(targetId, side) {
        const kids = this.#store.children(targetId);
        if (side === "left") return (kids.length ? kids[0].order : 0) - 1;
        return (kids.length ? kids[kids.length - 1].order : -1) + 1;
    }

    #moveIntoGroup(id, groupId) {
        const node = this.#store.node(id);
        if (!node || node.kind !== "person") return null;
        const group = this.#store.node(groupId);
        if (!group || group.kind !== "group") return null;
        if (node.memberOf === groupId) return null;
        const formerGroupId = node.memberOf || null;
        this.#stitchGap(id, node.memberOf ? null : node.parentId);
        this.#store.setMember({id, groupId});
        return formerGroupId !== groupId ? formerGroupId : null;
    }

    #setMember(id, groupId) {
        const former = this.#moveIntoGroup(id, groupId);
        if (former) this.#maybeDissolveGroup(former);
    }

    #extractFromGroup(id) {
        const node = this.#store.node(id);
        if (!node || !node.memberOf) return;
        const group = this.#store.node(node.memberOf);
        if (!group) return;
        this.#store.unsetMember({id, parentId: group.parentId});
        this.#linkUnder(id, group.id);
        this.#maybeDissolveGroup(group.id);
    }

    /**
     * @param {string} groupId
     */
    #maybeDissolveGroup(groupId) {
        const group = this.#store.node(groupId);
        if (!group || group.kind !== "group") return;
        const members = this.#store.members(groupId);
        if (members.length > 1) return;

        const parentId = group.parentId;
        const order = group.order;
        const groupChildren = this.#store.children(groupId);

        if (members.length === 1) {
            const member = members[0];
            this.#store.unsetMember({id: member.id, parentId, order});
            for (const c of groupChildren) this.#store.reparent({id: c.id, newParentId: member.id});
        } else if (groupChildren.length) {
            if (parentId) {
                for (const c of groupChildren) this.#store.reparent({id: c.id, newParentId: parentId});
            } else {
                const [first, ...rest] = groupChildren;
                this.#store.reparent({id: first.id, newParentId: null, order});
                for (const c of rest) this.#store.reparent({id: c.id, newParentId: first.id});
            }
        }
        this.#store.deleteSubtree({id: groupId});
    }

    #groupAtSlot(node) {
        const children = this.#store.children(node.id);
        const groupId = this.#store.addGroup({label: "Group", parentId: node.parentId, order: node.order});
        const healTo = node.parentId !== null ? node.parentId : groupId;
        for (const c of children) this.#store.reparent({id: c.id, newParentId: healTo});
        this.#store.setMember({id: node.id, groupId});
        return groupId;
    }

    #groupTwo(targetPerson, sourcePerson) {
        const sourceId = sourcePerson.id;
        if (sourceId === targetPerson.id) return;
        const target = this.#store.node(targetPerson.id);
        if (!target || target.kind !== "person") return;
        const groupId = target.memberOf || this.#groupAtSlot(target);
        const former = this.#moveIntoGroup(sourceId, groupId);
        if (former) this.#maybeDissolveGroup(former);
    }

    #deleteSubtree(id) {
        const node = this.#store.node(id);
        const formerGroupId = node && node.memberOf ? node.memberOf : null;
        this.#history.do(() => {
            this.#store.deleteSubtree({id});
            if (formerGroupId) this.#maybeDissolveGroup(formerGroupId);
        }, "delete");
    }

    #buildDragOptions() {
        const self = this;
        return {
            opacity: 1,
            target: "#teamStructure__empty, #teamStructure__trash, .teamStructure__dropZone",
            tolerance: "pointer",
            renewTarget: true,
            autoscroll: false,
            target_self: false,

            before_start: function (o) {
                const el = o.elem && o.elem[0];
                if (el && TeamStructureInteractions.#isSidePanelGroupTitle(el)) {
                    return !!self.#groupFromElement(el);
                }
                return true;
            },

            clone: function (o, $clone) {
                const replacer = self.#makeDragReplacer(o.originalElem || o.elem);
                if (replacer && replacer.length) {
                    const r = replacer[0];
                    $clone.empty().append(replacer.children());
                    $clone.attr("class", replacer.attr("class") + " drag_clone");
                    if (r.style.width) $clone[0].style.width = r.style.width;
                    if (r.style.height) $clone[0].style.height = r.style.height;
                }
                const w = $clone.outerWidth() || 310;
                const h = $clone.outerHeight() || 40;
                $clone.css({left: o.prevX - w / 2, top: o.prevY - h / 2, transform: ''});
                o.left = o.prevX - w / 2;
                o.top = o.prevY - h / 2;
            },

            start: function (o) {
                o.originalElem.addClass("sidePanel__list__item-dragThis");
                self.#markDragSource(o.originalElem);
                if (App.state && App.state.drag) App.state.drag.start();
                $("body").addClass("body-dragInProgress").addClass("grabbing");
                $("#teamStructure__panel").addClass("teamStructure__panel--withDrag");
                o.prepareTargetMatrix();
            },

            move: function (o, event) {
                $(".teamStructure--dragHover, .teamStructure__node--dragHoverInside, .teamStructure__dropZone--dragHoverSlot")
                    .removeClass("teamStructure--dragHover teamStructure__node--dragHoverInside teamStructure__dropZone--dragHoverSlot");
                const t = o.currentTarget;
                if (!t || !t.length) return;
                if (t.is("#teamStructure__trash, #teamStructure__empty")) {
                    t.addClass("teamStructure--dragHover");
                    return;
                }
                const $node = t.closest(".teamStructure__node");
                const $hl = $node.length ? $node : t;
                $hl.addClass("teamStructure--dragHover");
                const m = TeamStructureInteractions.#MODES[self.#dropMode(event, t)];
                if (m && m.hover === "inside") $hl.addClass("teamStructure__node--dragHoverInside");
                else t.addClass("teamStructure__dropZone--dragHoverSlot");
            },

            end: function (o) {
                $("body").removeClass("body-dragInProgress").removeClass("grabbing");
                o.elem.removeClass("sidePanel__list__item-dragThis");
                $("#teamStructure__panel").removeClass("teamStructure__panel--withDrag");
                self.#clearDragSource();
                if (App.state && App.state.drag) setTimeout(App.state.drag.stop, 100);
                $(".teamStructure--dragHover, .teamStructure__node--dragHoverInside, .teamStructure__dropZone--dragHoverSlot")
                    .removeClass("teamStructure--dragHover teamStructure__node--dragHoverInside teamStructure__dropZone--dragHoverSlot");
                self.#swallowGroupClick(o.elem);
            },

            drop: function (o, t, e) {
                setTimeout(function () {
                    if (App.state && App.state.drag) App.state.drag.stop();
                    $("body").removeClass("body-dragInProgress");
                }, 100);
                o.elem.removeClass("sidePanel__list__item-dragThis");
                t.removeClass("teamStructure--dragHover teamStructure__node--dragHoverInside teamStructure__dropZone--dragHoverSlot");
                self.#clearDragSource();

                const payload = self.#payloadFromElement(o.elem);
                if (!payload) return;

                if (t.is("#teamStructure__trash")) {
                    if (payload.type === "existing") self.#deleteSubtree(payload.id);
                    return;
                }

                if (payload.type === "existing" && String(t.data("node-id")) === payload.id) return;

                const targetId = TeamStructureInteractions.#targetNodeId(t);
                const childId = t.attr("data-child-id") || null;
                self.#applyDrop(payload, targetId, self.#dropMode(e, t), childId);
            },
        };
    }

    /**
     * @returns {JQuery|null}
     */
    #makeDragReplacer(sourceEl) {
        const payload = this.#payloadFromElement(sourceEl);
        if (!payload) return null;

        if (payload.type === "group") {
            const $chip = $('<div class="sidePanel__list__item flex_row"><span class="in flex_content flex_overflow"></span></div>');
            $chip.find(".in").text(payload.label);
            return $chip;
        }

        const $el = $(sourceEl);
        const isMember = !!TeamStructureInteractions.#findGroupMember($el);

        if (payload.type === "existing" && !isMember) {
            const $existing = $el.closest(".teamStructure__node");
            if ($existing.length) {
                const $clone = $existing.clone();
                $clone[0].style.transform = "";
                $clone.css("position", "absolute")
                    .removeClass("teamStructure--dragHover teamStructure__node--dragHoverInside sidePanel__list__item-dragThis");
                $clone.find(".teamStructure__dropZones").remove();
                return $clone;
            }
        }

        const node = payload.type === "existing" ? this.#store.node(payload.id) : null;
        const userId = node ? node.userId : payload.userId;
        const format = node ? node.format : "raw";
        return this.#renderer.buildNodePreview
            ? $(this.#renderer.buildNodePreview(String(userId || ""), format || "raw"))
            : null;
    }

    /**
     * @param {JQuery|HTMLElement} $el
     */
    #markDragSource($el) {
        const el = ($el && $el[0]) ? $el[0] : $el;
        const payload = this.#payloadFromElement(el);
        if (!payload || payload.type !== "existing") return;
        const node = this.#store.node(payload.id);
        if (!node || node.memberOf || !node.parentId) return;
        this.#$viewport.find(`.teamStructure__dropZone--child[data-child-id="${payload.id}"]`).addClass("teamStructure__dropZone--dropSelf");
    }

    #clearDragSource() {
        this.#$viewport.find(".teamStructure__dropZone--dropSelf").removeClass("teamStructure__dropZone--dropSelf");
    }

    #swallowGroupClick($el) {
        const el = $el && $el[0];
        if (!el) return;
        if (!TeamStructureInteractions.#isSidePanelGroupTitle(el)) return;
        const stop = (ev) => {
            if (ev.target && ev.target.closest && ev.target.closest(".sidePanel__list__group__title")) {
                ev.stopPropagation();
                ev.preventDefault();
            }
        };
        document.addEventListener("click", stop, true);
        setTimeout(() => document.removeEventListener("click", stop, true), 0);
    }

    #bindNodes(els) {
        if (!els || !els.length) return;
        const self = this;
        const bindDrag = ($set) => {
            $set.each(function () {
                if (this.dynamic) return;
                $(this).dragable(self.#dragOptions)
                    .find("a").addBack().on("dragstart", function () {
                    return false;
                });
            });
        };

        bindDrag($(els).filter(".teamStructure__node"));

        const $members = $(els).find(".teamStructure__groupMember");
        $members.off("mousedown.teamStructure").on("mousedown.teamStructure", function (e) {
            e.stopPropagation();
        });
        bindDrag($members);
    }

    #openSlots(el) {
        clearTimeout(this.#slotTimer);
        if (this.#slotOpenEl && this.#slotOpenEl !== el) this.#slotOpenEl.classList.remove("teamStructure__node--slotsOpen");
        this.#slotOpenEl = el;
        el.classList.add("teamStructure__node--slotsOpen");
    }

    #scheduleCloseSlots(el) {
        clearTimeout(this.#slotTimer);
        this.#slotTimer = setTimeout(() => {
            el.classList.remove("teamStructure__node--slotsOpen");
            if (this.#slotOpenEl === el) this.#slotOpenEl = null;
        }, 280);
    }

    #bindSidebar() {
        this.#bindSidebarOnce();
        clearTimeout(this.#sidebarTimer);
        this.#sidebarTimer = App.helper.nextTick(() => this.#bindSidebarOnce());
    }

    #bindSidebarOnce() {
        $(".sidePanel__list__item, .sidePanel__list__group__title")
            .once_by_criteria("team_structure")
            .dragable(this.#dragOptions)
            .find("a").addBack().on("dragstart", function () {
            return false;
        });
    }

    #onSlotClick(slotEl) {
        if (!this.#selectUser) return;
        const $slot = $(slotEl);
        const targetId = TeamStructureInteractions.#targetNodeId($slot);
        const mode = $slot.attr("data-drop-mode");
        const childId = $slot.attr("data-child-id") || null;
        this.#selectUser(slotEl, (ids) => this.#dropPicked(ids, targetId, mode, childId));
    }

    #dropPicked(ids, targetId, mode, childId) {
        const clean = (Array.isArray(ids) ? ids : [ids]).map((x) => String(x)).filter((x) => x !== "");
        if (!clean.length) return;
        const payload = clean.length === 1
            ? {type: "person", userId: clean[0]}
            : {type: "group", userIds: clean};
        this.#applyDrop(payload, targetId, mode, childId);
    }

    #bindClicks() {
        const self = this;
        const openCard = function () {
            const id = String($(this).data("node-id") || TeamStructureInteractions.#nodeIdOf(this));
            const node = self.#store.node(id);
            if (!node || !node.userId || !self.#openUserMenu) return;
            self.#openUserMenu(this, node.userId);
        };
        this.#$viewport
            .on("click.teamStructure", ".teamStructure__nodeContent", openCard)
            .on("click.teamStructure", ".teamStructure__groupMember", openCard);

        if (!this.#canEdit) {
            this.#$viewport.on("dragstart.teamStructure", ".teamStructure__node", () => false);
            return;
        }

        this.#$viewport
            .on("click.teamStructure", ".teamStructure__empty", function () {
                if (!self.#selectUser) return;
                self.#selectUser(this, (ids) => self.#dropPicked(ids, null, "child", null));
            })
            .on("mouseenter.teamStructure", ".teamStructure__node", function () {
                self.#openSlots(this);
            })
            .on("mouseleave.teamStructure", ".teamStructure__node", function () {
                self.#scheduleCloseSlots(this);
            })
            .on("click.teamStructure", ".teamStructure__dropZone--child, .teamStructure__dropZone--side", function (e) {
                e.stopPropagation();
                self.#onSlotClick(this);
            })
            .on("click.teamStructure", ".teamStructure__formatToggle", function (event) {
                event.stopPropagation();
                const id = TeamStructureInteractions.#nodeIdOf(this);
                const node = self.#store.node(id);
                if (!node) return;
                const F = self.#store.formats();
                const next = F[(F.indexOf(node.format) + 1) % F.length];
                self.#history.do(() => self.#store.setFormat({id, format: next}), "format");
            })
            .on("click.teamStructure", ".teamStructure__groupToggle", function (event) {
                event.stopPropagation();
                const id = TeamStructureInteractions.#nodeIdOf(this);
                if (!self.#store.node(id)) return;
                self.#history.do(() => self.#store.toggleCollapse({id}), "collapse");
            })
            .on("click.teamStructure", ".teamStructure__groupName span", function (event) {
                event.stopPropagation();
                const id = TeamStructureInteractions.#nodeIdOf(this);
                const node = self.#store.node(id);
                if (!node || node.kind !== "group" || !self.#editGroupName) return;
                self.#editGroupName(this, node.label, (value) => {
                    const trimmed = String(value == null ? "" : value).trim();
                    if (!trimmed) return;
                    self.#history.do(() => self.#store.renameGroup({id, label: trimmed}), "rename");
                });
            });
    }

    #bindToolbar() {
        const self = this;
        $("#teamStructure__reset").off("click.teamStructure").on("click.teamStructure", function () {
            const doReset = () => self.#history.do(() => self.#store.hydrate([]), "reset");
            if (self.#confirmReset) self.#confirmReset(this, doReset);
            else doReset();
        });
        $("#teamStructure__undo").off("click.teamStructure").on("click.teamStructure", function () {
            self.#history.undo();
        });
        $("#teamStructure__redo").off("click.teamStructure").on("click.teamStructure", function () {
            self.#history.redo();
        });
        $(document).off("keydown.teamStructure").on("keydown.teamStructure", function (e) {
            const mod = e.ctrlKey || e.metaKey;
            if (!mod || e.key.toLowerCase() !== "z") return;
            e.preventDefault();
            if (e.shiftKey) self.#history.redo(); else self.#history.undo();
        });
    }
}

/**
 * @typedef {Object} TeamInteractionDeps
 * @property {Store}  store       StructureStore
 * @property {Object} history     StructureHistory
 * @property {Object} renderer    StructureRenderer (onNodesAdded)
 * @property {JQuery} $viewport   #teamStructure__viewport
 */

;class TeamStructureLayout {
    static DEFAULT_CONFIG = {
        rankdir: "TB",
        nodesep: 48,
        ranksep: 48,
        marginx: 48,
        marginy: 20,
        treeGap: 64,
    };
    #config;
    /** @type {string|null} хеш дерева, для которого посчитан #lastResult */
    #lastHash;
    /** @type {Object|null} закешированный результат последнего compute() */
    #lastResult;

    constructor(config = {}) {
        this.#config = { ...TeamStructureLayout.DEFAULT_CONFIG, ...config };
        this.#lastHash = null;
        this.#lastResult = null;
    }


    /**
     * @param {Object} tree — { byId, rootIds } из Store
     * @returns {{nodes:Object, edges:Array, visible:Set, bounds:Object}}
     */
    compute(tree, sizeOf) {
        const h = TeamStructureLayout.#structuralHash(tree, sizeOf);
        if (h === this.#lastHash && this.#lastResult) return this.#lastResult;
        this.#lastHash = h;
        this.#lastResult = TeamStructureLayout.#layout(tree, sizeOf, this.#config);
        return this.#lastResult;
    }

    invalidate() {
        this.#lastHash = null;
        this.#lastResult = null;
    }

    static #indexChildren(tree) {
        const map = { __root__: [] };
        for (const id in tree.byId) {
            if (tree.byId[id].memberOf) continue;
            const key = tree.byId[id].parentId === null ? "__root__" : tree.byId[id].parentId;
            (map[key] || (map[key] = [])).push(tree.byId[id]);
        }
        for (const key in map) {
            map[key].sort((a, b) =>
                a.order !== b.order ? a.order - b.order : (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
            );
        }
        return map;
    }

    static #computeVisible(tree, childrenByParent) {
        const visible = new Set();
        const roots = childrenByParent["__root__"] || [];
        const stack = roots.slice();
        while (stack.length) {
            const node = stack.pop();
            if (node.memberOf) continue;

            visible.add(node.id);
            for (const k of childrenByParent[node.id] || []) stack.push(k);
        }
        return visible;
    }

    static #visibleRoots(childrenByParent, visible) {
        return (childrenByParent["__root__"] || []).filter((r) => visible.has(r.id)).map((r) => r.id);
    }

    /**
     * @returns {{ids:Array, edges:Array, centers:Object, sizes:Object}}
     */
    static #layoutOneTree(rootId, tree, childrenByParent, visible, sizeOf, config) {
        const g = new dagre.graphlib.Graph();
        g.setGraph(config);
        g.setDefaultEdgeLabel(() => ({}));

        const ids = [];
        const edges = [];
        const sizes = {};
        const stack = [rootId];
        while (stack.length) {
            const id = stack.pop();
            if (!visible.has(id)) continue;
            ids.push(id);
            const node = tree.byId[id];
            const s = sizeOf(node);
            sizes[id] = s;
            g.setNode(id, { width: s.width, height: s.height });

            const kids = childrenByParent[id] || [];
            for (let i = kids.length - 1; i >= 0; i--) {
                if (!visible.has(kids[i].id)) continue;
                edges.push([id, kids[i].id]);
                stack.push(kids[i].id);
            }
        }
        for (const [from, to] of edges) g.setEdge(from, to);

        dagre.layout(g);

        const centers = {};
        for (const id of ids) {
            const p = g.node(id);
            centers[id] = { x: p.x, y: p.y };
        }

        TeamStructureLayout.#alignRanksToTop(tree, childrenByParent, visible, centers, sizes, rootId, ids);
        return { ids, edges, centers, sizes };
    }

    static #alignRanksToTop(tree, childrenByParent, visible, centers, sizes, rootId, ids) {
        const depth = {};
        const queue = [{ id: rootId, d: 0 }];
        while (queue.length) {
            const { id, d } = queue.shift();
            if (depth[id] !== undefined && depth[id] <= d) continue;
            depth[id] = d;
            for (const k of childrenByParent[id] || []) {
                if (visible.has(k.id)) queue.push({ id: k.id, d: d + 1 });
            }
        }

        const rankTop = {};
        for (const id of ids) {
            const top = centers[id].y - sizes[id].height / 2;
            const r = depth[id] ?? 0;
            rankTop[r] = rankTop[r] === undefined ? top : Math.min(rankTop[r], top);
        }
        for (const id of ids) {
            const r = depth[id] ?? 0;
            centers[id].y = rankTop[r] + sizes[id].height / 2;
        }
    }

    /**
     * @returns {{nodes:Object<string,{x,y,w,h}>, edges:Array<{from,to}>, visible:Set, bounds:Object}}
     */
    static #layout(tree, sizeOf, cfg) {
        const childrenByParent = TeamStructureLayout.#indexChildren(tree);
        const visible = TeamStructureLayout.#computeVisible(tree, childrenByParent);
        const rootIds = TeamStructureLayout.#visibleRoots(childrenByParent, visible);
        const { nodes, edges, bounds } =
            TeamStructureLayout.#placeForest(rootIds, tree, childrenByParent, visible, sizeOf, cfg);
        return { nodes, edges, visible, bounds };
    }

    /**
     * @returns {{nodes:Object<string,{x,y,w,h}>, edges:Array<{from,to}>, bounds:Object}}
     */
    static #placeForest(rootIds, tree, childrenByParent, visible, sizeOf, cfg) {
        const nodes = {};
        const edges = [];
        let cursorX = cfg.marginx;
        const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

        for (const rootId of rootIds) {
            const sub = TeamStructureLayout.#layoutOneTree(rootId, tree, childrenByParent, visible, sizeOf, cfg);
            const local = TeamStructureLayout.#treeBounds(sub);

            const shiftX = cursorX - local.minX;
            const shiftY = cfg.marginy - local.minY;

            for (const id of sub.ids) {
                const c = sub.centers[id], s = sub.sizes[id];
                const x = c.x + shiftX - s.width / 2;
                const y = c.y + shiftY - s.height / 2;
                nodes[id] = { x, y, w: s.width, h: s.height };
                bounds.minX = Math.min(bounds.minX, x);
                bounds.minY = Math.min(bounds.minY, y);
                bounds.maxX = Math.max(bounds.maxX, x + s.width);
                bounds.maxY = Math.max(bounds.maxY, y + s.height);
            }

            for (const [from, to] of sub.edges) edges.push({ from, to });

            cursorX += (local.maxX - local.minX) + cfg.treeGap;
        }

        if (!rootIds.length) { bounds.minX = 0; bounds.minY = 0; bounds.maxX = 1; bounds.maxY = 1; }
        bounds.width = Math.max(bounds.maxX - bounds.minX, 1);
        bounds.height = Math.max(bounds.maxY - bounds.minY, 1);
        return { nodes, edges, bounds };
    }

    /**
     * @returns {{minX:number, maxX:number, minY:number, maxY:number}}
     */
    static #treeBounds(sub) {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const id of sub.ids) {
            const c = sub.centers[id], s = sub.sizes[id];
            minX = Math.min(minX, c.x - s.width / 2);
            maxX = Math.max(maxX, c.x + s.width / 2);
            minY = Math.min(minY, c.y - s.height / 2);
            maxY = Math.max(maxY, c.y + s.height / 2);
        }
        return { minX, maxX, minY, maxY };
    }

    static #structuralHash(tree, sizeOf) {
        const parts = [];
        const ids = Object.keys(tree.byId).sort();
        for (const id of ids) {
            const n = tree.byId[id];
            const s = sizeOf(n);
            parts.push(id + ":" + (n.parentId ?? "-") + ":" + n.order + ":" + (n.collapsed ? "1" : "0") + ":" + s.width + "x" + s.height);
        }
        return parts.join("|");
    }
}

;class TeamStructureRenderer {
    static #SVG_NS = "http://www.w3.org/2000/svg";

    static #SIZES = {
        user: {width: 310, height: 40},
        avatar: {width: 210, height: 96},
        group: {width: 365, headerH: 40, rowH: 32, minH: 60, padB: 14},
    };

    static #KINDS = {
        person: {
            css: "teamStructure__node--user",
            container: false,
            size: (self, node) => node.format === "avatar"
                ? {...TeamStructureRenderer.#SIZES.avatar}
                : {...TeamStructureRenderer.#SIZES.user},
            fill: (self, el, node) => self.#fillPerson(el, node),
            sig: (self, node) => `u|${node.format}|${self.#userSig(node.userId)}`,
        },
        group: {
            css: "teamStructure__node--group",
            container: true,
            size: (self, node) => self.#groupSize(node),
            fill: (self, el, node) => self.#fillGroup(el, node),
            sig: (self, node) => self.#groupSig(node),
        },
    };
    #store;
    #team;
    #dom;
    #layout;
    #branchOn;
    /** @type {Map<string,HTMLElement>} id узла → его DOM-элемент (для диффинга) */
    #nodeEls;
    /** @type {Map<string,SVGPathElement>} ключ ребра from>to → его <path> */
    #edgeEls;
    /** @type {Map<string,string>} ключ ребра → последний нарисованный d (кеш) */
    #edgeD;
    /** @type {Set<string>} userId, заказанные у team-стора, но ещё не приехавшие */
    #pendingUsers;
    /** @type {number} горизонтальный масштаб (1 = без сжатия) */
    #scaleX;
    #unsubs;
    #nodeAddedSubs;
    /** @type {HTMLElement[]} узлы, созданные с нуля в текущем проходе render */
    #addedThisPass;
    /** @type {HTMLElement[]} группы, тело которых перестроено в текущем проходе */
    #rebuiltThisPass;
    #resizeHandler;

    /** @param {TeamStructureRendererDeps} deps */
    constructor(deps) {
        this.#store = deps.store;
        this.#team = deps.team;
        this.#dom = deps.dom;
        this.#branchOn = deps.branchingEnabled !== false;

        this.#layout = deps.layout;
        this.#nodeEls = new Map();
        this.#edgeEls = new Map();
        this.#edgeD = new Map();
        this.#pendingUsers = new Set();

        this.#scaleX = 1;
        this.#unsubs = [];

        this.#nodeAddedSubs = new Set();
        this.#addedThisPass = [];
        this.#rebuiltThisPass = [];

        this._sizeOf = this._sizeOf.bind(this);
        this.render = this.render.bind(this);
    }

    /**
     * @returns {() => void} отписка
     */
    onNodesAdded(fn) {
        this.#nodeAddedSubs.add(fn);
        return () => this.#nodeAddedSubs.delete(fn);
    }

    buildNodePreview(userId, format = "raw") {
        const el = document.createElement("article");
        el.className = `teamStructure__node teamStructure__node--user teamStructure__node--format-${format}`;
        const size = format === "avatar" ? TeamStructureRenderer.#SIZES.avatar : TeamStructureRenderer.#SIZES.user;
        el.style.width = `${size.width}px`;
        el.style.height = `${size.height}px`;
        this.#fillPerson(el, {userId: String(userId || ""), format});
        return el;
    }


    mount() {
        this.#unsubs.push(this.#store.$watch("tree", this.render));
        if (this.#team && typeof this.#team.$subscribe === "function") {
            this.#unsubs.push(this.#team.$subscribe((changeset) => this.#onTeamChange(changeset)));
        }
        this.#resizeHandler = () => this.render();
        window.addEventListener("resize", this.#resizeHandler);
        this.render();
    }

    destroy() {
        this.#unsubs.forEach((off) => off && off());
        this.#unsubs = [];
        window.removeEventListener("resize", this.#resizeHandler);
        this.#nodeEls.clear();
        this.#edgeEls.clear();
        this.#edgeD.clear();
        this.#pendingUsers.clear();
        this.#dom.nodes.replaceChildren();
        while (this.#dom.edges.firstChild) this.#dom.edges.removeChild(this.#dom.edges.firstChild);
    }

    #kind(node) {
        return TeamStructureRenderer.#KINDS[node && node.kind] || TeamStructureRenderer.#KINDS.person;
    }

    #inlineMembers(node) {
        if (!this.#kind(node).container) return [];
        return this.#store.members(node.id);
    }

    _sizeOf(node) {
        return this.#kind(node).size(this, node);
    }

    #groupSize(node) {
        if (node.collapsed) return {
            width: TeamStructureRenderer.#SIZES.group.width,
            height: TeamStructureRenderer.#SIZES.group.minH
        };
        const rows = this.#inlineMembers(node).length;
        const h = Math.max(TeamStructureRenderer.#SIZES.group.minH, TeamStructureRenderer.#SIZES.group.headerH + rows * TeamStructureRenderer.#SIZES.group.rowH + TeamStructureRenderer.#SIZES.group.padB);
        return {width: TeamStructureRenderer.#SIZES.group.width, height: h};
    }

    #groupSig(node) {
        const members = this.#inlineMembers(node)
            .map((m) => m.id + ":" + this.#userSig(m.userId)).join(",");
        return `g|${node.label}|${node.collapsed ? 1 : 0}|${members}`;
    }


    render() {
        const tree = this.#store.tree();
        if (this.#isEmpty(tree)) return this.#renderEmpty();
        this.#dom.empty.style.display = "none";

        const layout = this.#layout.compute(tree, this._sizeOf);
        const view = this.#fitViewport(layout.bounds);
        this.#resizeStage(view);

        const placed = this.#project(layout.nodes, layout.bounds, view);
        this.#reconcileNodes(layout.visible, placed, tree);
        this.#positionDropSlots(layout.edges, placed, layout.visible);
        this.#reconcileEdges(this.#screenEdges(layout, view));
    }

    #positionDropSlots(edges, placed, visible) {
        const R = (v) => App.helper.math.round(v, 2);
        const kidsOf = new Map();
        for (const e of edges) {
            if (!placed[e.from] || !placed[e.to]) continue;
            if (!kidsOf.has(e.from)) kidsOf.set(e.from, []);
            kidsOf.get(e.from).push(e.to);
        }
        const ids = [];
        for (const id of visible) if (placed[id]) ids.push(id);
        const branchOn = this.#branchOn;
        for (const id of ids) {
            const el = this.#nodeEls.get(id), p = placed[id];
            if (!el || !p) continue;
            const host = el.querySelector(".teamStructure__dropZones");
            if (!host) continue;
            this.#reconcileSlots(host, this.#desiredSlots(p, kidsOf.get(id) || [], placed, ids, id, branchOn, R));
        }
    }

    #desiredSlots(p, kids, placed, ids, id, branchOn, R) {
        const list = [];
        const childEntries = kids.length
            ? kids.map((cid) => ({ key: "child:" + cid, childId: cid }))
            : [{ key: "child:__add__", childId: null }];
        for (const e of childEntries) {
            const tid = e.childId;
            list.push({ key: e.key, mode: "child", childId: tid, css: "teamStructure__dropZone teamStructure__dropZone--child", place: (slot) => {
                const t = tid ? placed[tid] : null;
                let x, y;
                if (t) {
                    const sx = p.left + p.w / 2, sy = p.top + p.h;
                    const ex = t.left + t.w / 2, ey = t.top;
                    const my = sy + Math.max(20, (ey - sy) / 2);
                    x = (sx + ex) / 2 - p.left;
                    y = (sy + 6 * my + ey) / 8 - p.top;
                } else {
                    x = p.w / 2;
                    y = p.h + 24;
                }
                slot.style.left = `${R(x)}px`;
                slot.style.top = `${R(y)}px`;
            }});
        }
        if (branchOn && kids.length) {
            const gaps = this.#sideGaps(p, id, ids, placed);
            const sides = [
                { key: "side:left", mode: "child-left", css: "teamStructure__dropZone teamStructure__dropZone--side teamStructure__dropZone--left", dir: -1, gap: gaps.left, edge: p.left },
                { key: "side:right", mode: "child-right", css: "teamStructure__dropZone teamStructure__dropZone--side teamStructure__dropZone--right", dir: 1, gap: gaps.right, edge: p.left + p.w },
            ];
            for (const s of sides) {
                list.push({ key: s.key, mode: s.mode, childId: null, css: s.css, place: (slot) => {
                    const half = s.gap / 2;
                    const w = Math.min(42, half - 8);
                    if (w < 6) { slot.style.display = "none"; return; }
                    slot.style.display = "";
                    const off = Math.min(30, half - w / 2 - 2);
                    slot.style.left = `${R(s.edge + s.dir * off - p.left)}px`;
                    slot.style.top = `${R(p.h / 2)}px`;
                    slot.style.width = `${R(w)}px`;
                    slot.style.height = "22px";
                }});
            }
        }
        return list;
    }

    #sideGaps(p, id, ids, placed) {
        const xL = p.left, xR = p.left + p.w, yT = p.top, yB = p.top + p.h;
        let leftEdge = -Infinity, rightEdge = Infinity;
        for (const oid of ids) {
            if (oid === id) continue;
            const q = placed[oid];
            if (q.top >= yB || q.top + q.h <= yT) continue;
            if (q.left + q.w <= xL) leftEdge = Math.max(leftEdge, q.left + q.w);
            else if (q.left >= xR) rightEdge = Math.min(rightEdge, q.left);
        }
        return { left: xL - leftEdge, right: rightEdge - xR };
    }

    #reconcileSlots(host, desired) {
        const existing = new Map();
        host.querySelectorAll(".teamStructure__dropZone--child, .teamStructure__dropZone--side").forEach((s) => existing.set(s.dataset.slotKey, s));
        const live = new Set();
        for (const d of desired) {
            live.add(d.key);
            let slot = existing.get(d.key);
            if (!slot) {
                slot = document.createElement("div");
                slot.className = d.css;
                slot.dataset.dropMode = d.mode;
                slot.dataset.slotKey = d.key;
                host.appendChild(slot);
            }
            if (d.childId) slot.dataset.childId = d.childId;
            else slot.removeAttribute("data-child-id");
            d.place(slot);
        }
        for (const [key, slot] of existing) if (!live.has(key)) slot.remove();
    }

    #isEmpty(tree) {
        return Object.keys(tree.byId).length === 0;
    }

    #renderEmpty() {
        this.#dom.empty.style.display = "";
        this.#reconcileNodes(new Set(), {});
        this.#reconcileEdges([]);
        this.#dom.stage.style.height = "100%";
    }

    /**
     * @returns {{scaleX:number, offsetX:number, offsetY:number, stageWidth:number, stageHeight:number}}
     */
    #fitViewport(bounds) {
        const room = Math.max(this.#dom.viewport.clientWidth - 56, 160);
        const scaleX = App.helper.math.clamp(room / bounds.width, 0.18, 1);
        const stageWidth = Math.max(this.#dom.viewport.clientWidth, bounds.width * scaleX + 56);
        const offsetX = Math.max(28, (stageWidth - bounds.width * scaleX) / 2);
        const offsetY = 20;
        const stageHeight = bounds.height + 90;
        this.#scaleX = scaleX;
        return {scaleX, offsetX, offsetY, stageWidth, stageHeight};
    }

    #resizeStage(view) {
        this.#dom.stage.style.width = `${view.stageWidth}px`;
        this.#dom.stage.style.height = `${view.stageHeight}px`;
        const edgeSvg = this.#dom.edges;
        edgeSvg.setAttribute("width", view.stageWidth);
        edgeSvg.setAttribute("height", view.stageHeight);
        edgeSvg.setAttribute("viewBox", `0 0 ${view.stageWidth} ${view.stageHeight}`);
    }

    /**
     * @returns {Object<string,{left:number,top:number,w:number,h:number}>}
     */
    #project(nodes, bounds, view) {
        const placed = {};
        for (const id of Object.keys(nodes)) {
            const n = nodes[id];
            placed[id] = {
                left: (n.x - bounds.minX) * view.scaleX + view.offsetX,
                top: n.y - bounds.minY + view.offsetY,
                w: n.w * view.scaleX,
                h: n.h,
            };
        }
        return placed;
    }

    /**
     * @returns {Array<{key:string, d:string}>}
     */
    #screenEdges(layout, view) {
        const {edges, nodes, bounds} = layout;
        const out = [];
        for (const e of edges) {
            const f = nodes[e.from], t = nodes[e.to];
            if (!f || !t) continue;
            const sx = (f.x + f.w / 2 - bounds.minX) * view.scaleX + view.offsetX;
            const sy = (f.y - bounds.minY) + view.offsetY + f.h;
            const ex = (t.x + t.w / 2 - bounds.minX) * view.scaleX + view.offsetX;
            const ey = (t.y - bounds.minY) + view.offsetY;
            out.push({key: `${e.from}>${e.to}`, d: App.helper.math.vConnectorPath(sx, sy, ex, ey)});
        }
        return out;
    }

    /**
     * @param {Object} placed — id → {left, top, w, h} в экранных пикселях
     */
    #reconcileNodes(visible, placed, tree) {
        this.#addedThisPass.length = 0;
        this.#rebuiltThisPass.length = 0;

        for (const [id, el] of this.#nodeEls) {
            if (!visible.has(id)) {
                el.remove();
                this.#nodeEls.delete(id);
            }
        }
        if (!tree) {
            this.#flushAdded();
            return;
        }

        for (const id of visible) {
            const node = tree.byId[id];
            if (!node) continue;
            let el = this.#nodeEls.get(id);
            if (!el) {
                el = this.#createNode(node);
                this.#nodeEls.set(id, el);
                this.#dom.nodes.appendChild(el);
                this.#addedThisPass.push(el);
            }
            this.#updateNode(el, node);
            const p = placed[id];
            if (p) {
                el.style.transform = `translate(${App.helper.math.round(p.left, 2)}px, ${App.helper.math.round(p.top, 2)}px)`;
                el.style.width = `${App.helper.math.round(p.w, 2)}px`;
                el.style.height = `${App.helper.math.round(p.h, 2)}px`;
            }
        }

        this.#flushAdded();
    }

    #flushAdded() {
        const batch = this.#addedThisPass.concat(this.#rebuiltThisPass);
        if (!batch.length || !this.#nodeAddedSubs.size) return;
        TeamStructureUtils.emit(this.#nodeAddedSubs, batch);
    }

    #createNode(node) {
        const el = document.createElement("article");
        el.dataset.nodeId = node.id;
        el.setAttribute("draggable", "true");
        return el;
    }

    #updateNode(el, node) {
        const k = this.#kind(node);
        const cls = [
            "teamStructure__node",
            k.css,
            `teamStructure__node--format-${node.format || "raw"}`,
            k.container && node.collapsed ? "teamStructure__node--collapsed" : "",
            this.#scaleX < 0.82 ? "teamStructure__node--compact" : "",
            this.#scaleX < 0.46 ? "teamStructure__node--tiny" : "",
            el.classList.contains("teamStructure__node--slotsOpen") ? "teamStructure__node--slotsOpen" : "",
        ].filter(Boolean).join(" ");
        if (el.className !== cls) el.className = cls;

        const sig = this.#contentSig(node);
        if (el._sig === sig) return;
        el._sig = sig;

        el.replaceChildren();
        k.fill(this, el, node);
        el.appendChild(this.#dropZones(node));

        if (k.container && !this.#addedThisPass.includes(el)) {
            this.#rebuiltThisPass.push(el);
        }
    }

    #contentSig(node) {
        return this.#kind(node).sig(this, node);
    }

    /**
     * @returns {string}
     */
    #userSig(userId) {
        if (!userId) return "?";
        if (!this.#hasUser(userId)) {
            this.#wantUser(userId);
            return "loading";
        }
        const u = this.#team.get(userId);
        return (u.name || "") + "|" + (u.avatar || "") + "|" + (u.title || "");
    }

    /** @returns {boolean} */
    #hasUser(userId) {
        if (!this.#team) return false;
        if (this.#team.exists && this.#team.exists(userId)) return true;
        const u = this.#team.get(userId);
        return !!(u && (u.name || u.avatar));
    }

    #wantUser(userId) {
        this.#pendingUsers.add(String(userId));
        if (this.#team && this.#team.ensure) this.#team.ensure(userId);
    }


    #fillPerson(el, node) {
        const u = this.#user(node.userId);
        const content = document.createElement("div");
        content.className = "teamStructure__nodeContent";
        content.appendChild(this.#avatar(u, 28));

        const label = document.createElement("div");
        label.className = "teamStructure__nodeLabel";
        label.appendChild(this.#nameAndTitle(u));
        content.appendChild(label);
        el.appendChild(content);

        const fmt = document.createElement("span");
        fmt.className = "teamStructure__formatToggle";
        fmt.dataset.title = "Change format";
        fmt.textContent = "Aa";
        el.appendChild(fmt);
    }

    #fillGroup(el, node) {
        const header = document.createElement("div");
        header.className = "teamStructure__groupHeader";
        header.title = "Click group name to change format";

        const name = document.createElement("strong");
        name.className = "teamStructure__groupName text_overflow";
        const nameSpan = document.createElement("span");
        nameSpan.textContent = node.label;
        name.appendChild(nameSpan);
        header.appendChild(name);

        const toggle = document.createElement("span");
        toggle.className = "teamStructure__groupToggle";
        toggle.dataset.title = "Collapse or expand";
        const icon = document.createElement("ws-icon");
        icon.setAttribute("name", node.collapsed ? "arrows/chevron_expand_sm" : "arrows/chevron_collapse_sm");
        toggle.appendChild(icon);
        header.appendChild(toggle);
        el.appendChild(header);

        const members = this.#inlineMembers(node);

        const count = document.createElement("div");
        count.className = "teamStructure__collapsedCount";
        count.textContent = `${members.length} people`;
        el.appendChild(count);

        const list = document.createElement("div");
        list.className = "teamStructure__groupMembers";
        if (!members.length) {
            list.appendChild(this.#span("teamStructure__nodeTitle", "Drop people inside"));
        } else {
            for (const m of members) list.appendChild(this.#memberRow(m));
        }
        el.appendChild(list);
    }

    #memberRow(node) {
        const u = this.#user(node.userId);
        const row = document.createElement("div");
        row.className = "teamStructure__groupMember";
        row.dataset.nodeId = node.id;
        row.appendChild(this.#avatar(u, 24));
        row.appendChild(this.#nameAndTitle(u));
        return row;
    }

    /**
     * @returns {HTMLElement}
     */
    #dropZones(node) {
        const isGroup = this.#kind(node).container;
        const wrap = document.createElement("div");
        wrap.className = isGroup ? "teamStructure__dropZones teamStructure__dropZones--group" : "teamStructure__dropZones";
        wrap.setAttribute("aria-hidden", "true");
        const inside = document.createElement("div");
        inside.className = "teamStructure__dropZone teamStructure__dropZone--inside";
        inside.dataset.dropMode = "inside";
        if (!isGroup) inside.textContent = "Inside";
        wrap.appendChild(inside);
        return wrap;
    }


    /**
     * @param {Array<{key:string, d:string}>} screenEdges
     */
    #reconcileEdges(screenEdges) {
        const live = new Set();
        for (const e of screenEdges) {
            live.add(e.key);
            let path = this.#edgeEls.get(e.key);
            if (!path) {
                path = document.createElementNS(TeamStructureRenderer.#SVG_NS, "path");
                path.setAttribute("class", "teamStructure__edge");
                this.#dom.edges.appendChild(path);
                this.#edgeEls.set(e.key, path);
            }
            if (this.#edgeD.get(e.key) !== e.d) {
                path.setAttribute("d", e.d);
                this.#edgeD.set(e.key, e.d);
            }
        }
        for (const [key, path] of this.#edgeEls) {
            if (!live.has(key)) {
                path.remove();
                this.#edgeEls.delete(key);
                this.#edgeD.delete(key);
            }
        }
    }


    /**
     * @param {Map|Set} changeset
     */
    #onTeamChange(changeset) {
        if (!this.#pendingUsers.size) return;
        let touched = false;
        for (const key of changeset.keys ? changeset.keys() : []) {
            if (this.#pendingUsers.has(String(key))) {
                this.#pendingUsers.delete(String(key));
                touched = true;
            }
        }
        if (touched) this.render();
    }


    /**
     * @returns {{name:string, avatar:string, title:string}}
     */
    #user(userId) {
        if (!userId) return {name: "Unnamed", avatar: "", title: ""};
        if (!this.#hasUser(userId)) {
            this.#wantUser(userId);
            return {name: "…", avatar: "", title: ""};
        }
        return this.#team.get(userId);
    }

    /**
     * @param {number} size
     * @returns {HTMLElement}
     */
    #avatar(user, size) {
        if (user && typeof user.avatar === "string" && user.avatar.trim().startsWith("<")) {
            const holder = document.createElement("span");
            holder.innerHTML = user.avatar;
            const node = holder.firstElementChild || holder;
            node.classList && node.classList.add("avatar");
            return node;
        }
        const img = document.createElement("img");
        img.className = "avatar";
        img.width = size;
        img.height = size;
        img.setAttribute("draggable", "false");
        img.alt = "";
        img.src = (user && user.avatar) || this.#fallbackAvatar(user && user.name);
        return img;
    }

    #span(cls, text) {
        const s = document.createElement("span");
        s.className = cls;
        s.textContent = text || "";
        return s;
    }

    #nameAndTitle(user) {
        const frag = document.createDocumentFragment();
        frag.appendChild(this.#span("teamStructure__nodeName text_overflow", user.name || "Unnamed"));
        frag.appendChild(this.#span("teamStructure__nodeTitle text_overflow", user.title || ""));
        return frag;
    }

    /**
     * @returns {string} data:image/svg+xml,...
     */
    #fallbackAvatar(name) {
        const ch = encodeURIComponent((name || "?").charAt(0));
        return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect width='96' height='96' rx='48' fill='%23dce3ea'/%3E%3Ctext x='48' y='56' text-anchor='middle' font-family='Arial' font-size='30' fill='%23717d89'%3E" + ch + "%3C/text%3E%3C/svg%3E";
    }
}

/**
 *  @typedef {Object} TeamStructureRendererDeps
 *  @property {Store}  store
 *  @property {Object} layout
 *  @property {Object} team
 *  @property {Object} dom
 */

;class TeamStructureTransport {
    #store;
    #history;
    #adapter;
    #scopeId;
    #debounceMs;
    #applyingRemote;
    #inFlight;
    #statusSubs;
    #unsub;
    #pendingAfterFlight;
    #debouncedSave;
    #autosave;
    #status;
    #dirty;
    #onTreeChange;

    /** @param {TeamStructureTransportOptions} opts */
    constructor(opts) {
        this.#store = opts.store;
        this.#history = opts.history || null;
        this.#adapter = opts.adapter || {
            load: () => Promise.resolve([]),
            save: () => Promise.resolve(),
        };
        this.#scopeId = opts.scopeId ?? null;
        this.#debounceMs = opts.debounceMs ?? 800;
        this.#autosave = opts.autosave !== false;

        /**
         * @type {"idle"|"saving"|"saved"|"error"}
         */
        this.#status = "idle";
        this.#dirty = false;
        this.#applyingRemote = false;
        this.#inFlight = null;
        this.#pendingAfterFlight = false;

        /** @type {Set<(status: string, dirty: boolean) => void>} */
        this.#statusSubs = new Set();
        this.#unsub = null;

        this.#onTreeChange = () => {
            if (this.#applyingRemote) return;
            this.#setStatus("idle", true);
            if (!this.#autosave) return;
            this.#debouncedSave();
        };

        const flush = () => this.#flush();
        this.#debouncedSave = App.helper.debounce(flush, this.#debounceMs);
    }


    mount() {
        if (this.#autosave) {
            this.#unsub = this.#store.$watch("tree", this.#onTreeChange);
        }
    }

    destroy() {
        if (this.#unsub) this.#unsub();
        this.#unsub = null;
        this.#debouncedSave.reset();
        this.#statusSubs.clear();
    }


    get status() {
        return this.#status;
    }

    get dirty() {
        return this.#dirty;
    }

    /** @param {(status: string, dirty: boolean) => void} fn */
    onStatus(fn) {
        this.#statusSubs.add(fn);
        return () => this.#statusSubs.delete(fn);
    }

    #setStatus(status, dirty) {
        this.#status = status;
        if (dirty !== undefined) this.#dirty = dirty;
        TeamStructureUtils.emit(this.#statusSubs, this.#status, this.#dirty);
    }


    /**
     * @returns {Promise<boolean>}
     */
    load() {
        this.#setStatus("idle", false);
        return Promise.resolve(this.#adapter.load(this.#scopeId))
            .then((nodes) => {
                this.#applyingRemote = true;
                try {
                    this.#store.hydrate(Array.isArray(nodes) ? nodes : []);
                    if (this.#history) this.#history.clear();
                } finally {
                    queueMicrotask(() => {
                        this.#applyingRemote = false;
                    });
                }
                this.#setStatus("saved", false);
                return true;
            })
            .catch((err) => {
                console.error("[team_structure] load failed", err);
                this.#setStatus("error");
                return false;
            });
    }


    serialize() {
        return this.#store.serialize();
    }

    /**
     * @returns {Promise<boolean>}
     */
    save() {
        this.#debouncedSave.reset();
        return this.#flush();
    }

    #flush() {
        if (this.#inFlight) {
            this.#pendingAfterFlight = true;
            return this.#inFlight;
        }
        const nodes = this.serialize();
        this.#setStatus("saving");
        this.#inFlight = Promise.resolve(this.#adapter.save(this.#scopeId, nodes))
            .then(() => {
                this.#inFlight = null;
                if (this.#pendingAfterFlight) {
                    this.#pendingAfterFlight = false;
                    return this.#flush();
                }
                this.#setStatus("saved", false);
                return true;
            })
            .catch((err) => {
                this.#inFlight = null;
                this.#pendingAfterFlight = false;
                console.error("[team_structure] save failed", err);
                this.#setStatus("error", true);
                return false;
            });
        return this.#inFlight;
    }
}

/**
 * @typedef TeamStructureTransportAdapter
 * @property {(scopeId: *) => Promise<Array<Object>>}        load
 * @property {(scopeId: *, nodes: Array<Object>) => Promise<void>} save
 *
 * @typedef TeamStructureTransportOptions
 * @property {TeamStructureTransportAdapter} adapter
 * @property {Store}    store
 * @property {Object}  [history]
 * @property {*}       [scopeId]
 * @property {number}  [debounceMs=800]
 * @property {boolean} [autosave=true]
 */
;const TeamStructureUtils = {
    emit(callbacks, ...args) {
        for (const fn of callbacks) {
            try {
                fn(...args);
            } catch (e) {
                console.error(e);
            }
        }
    },
    transport: {
        /**
         *  @param {string} [keyPrefix]
         *  @returns {{ load: Function, save: Function }}
         */
        createLocalStorageAdapter(keyPrefix = "team-structure") {
            const keyFor = (scopeId) => (scopeId == null ? keyPrefix : `${keyPrefix}:${scopeId}`);
            return {
                load(scopeId) {
                    try {
                        const val = App.helper.localStorage.get(keyFor(scopeId), null);
                        const nodes = Array.isArray(val) ? val : (val && val.nodes) || [];
                        return Promise.resolve(nodes);
                    } catch (e) {
                        return Promise.reject(e);
                    }
                },
                save(scopeId, nodes) {
                    try {
                        App.helper.localStorage.set(keyFor(scopeId), nodes);
                        return Promise.resolve();
                    } catch (e) {
                        return Promise.reject(e);
                    }
                },
            };
        },
        /**
         * @param {{ loadUrl?: string, saveUrl?: string }} [config]
         * @returns {{ load: Function, save: Function }}
         */
        createServerAdapter(config = {}) {
            return {
                load(scopeId) {
                    return $.ajax({ url: config.loadUrl, data: { scope: scopeId }, dataType: "json" })
                      .then(res => res.nodes || []);
                },
                save(scopeId, nodes) {
                    return $.ajax({ url: config.saveUrl, method: "PUT",
                      contentType: "application/json",
                      data: JSON.stringify({ scope: scopeId, nodes }) });
                },
            };
        }
    },
}
;class FailException extends Error {}
class SkipException extends Error {}

class ValidationException extends Error {
	/** @param {Record<string, string[]>} errors */
	constructor(errors) {
		super('Validation failed');
		this.errors = errors;
	}
}
;class Rule extends FillableMixin() {
	static ATTRIBUTE = 'attribute';
	static MESSAGES_NAMESPACE = 'validator_messages';

	/** @type {RuleMessages} */
	messages;

	constructor(params = {}) {
		super(params);
		this.messages = this.messages ?? {};
	}

	static make(params = {}) {
		return new this(params);
	}

	/** @param {RuleMessages} messages */
	setMessages(messages) {
		this.messages = { ...this.messages, ...messages };
		return this;
	}

	apply(value) {
		throw new Error('Rule.apply() must be implemented');
	}

	/**
	 * @param {ValidatorMessagesLocaleKey} key
	 * @param {Record<string, string|number>} [params]
	 */
	message(key, params = {}) {
		let template;

		if (key.includes(' ')) {
			template = key;
		} else {
			const fullKey = key.includes('.') ? key : `${Rule.MESSAGES_NAMESPACE}.${key}`;
			template = this.messages[fullKey] ?? this.messages[key] ?? App.i18n.t(fullKey) ?? 'invalid value';
		}

		let attribute = this.messages[Rule.ATTRIBUTE];
		let localParams = params;
		if (attribute === undefined && params[Rule.ATTRIBUTE] !== undefined) {
			attribute = params[Rule.ATTRIBUTE];
			localParams = { ...params };
			delete localParams[Rule.ATTRIBUTE];
		}

		for (const k in localParams) {
			template = template.replaceAll(`:${k}:`, String(localParams[k]));
		}

		template = attribute
			? template.replaceAll(' :attr: ', ` "${attribute}" `)
			: template.replaceAll(' :attr: ', ' ');

		return template;
	}

	skip(reason = '') {
		throw new SkipException(reason);
	}

	/**
	 * @param {ValidatorMessagesLocaleKey} [message]
	 * @param {Record<string, string|number>} [params]
	 */
	fail(message = '', params = {}) {
		throw new FailException(this.message(message, params));
	}
}
/**
 * @typedef {Extract<keyof ValidatorMessagesLocale, string>
 *   | `validator_messages.${Extract<keyof ValidatorMessagesLocale, string>}`
 *   | (string & {})
 * } ValidatorMessagesLocaleKey
 */

/**
 * @typedef {Partial<Record<ValidatorMessagesLocaleKey, string>> & { attribute?: string }} RuleMessages
 */
/**
 * @typedef {{ rule: string, params?: Record<string, any>, serverOnly?: boolean }} RuleSpec
 */

/**
 * @typedef {{
 *   version?: string,
 *   rules: Record<string, RuleSpec[]>,
 *   messages?: Record<string, RuleMessages>
 * }} ValidationSchema
 */
;class RequiredRule extends Rule {
	apply(value) {
		if (value === null || value === '') this.fail('required');
		if (typeof value === 'string' && value.trim() === '') this.fail('required');
		return value;
	}
}

class NullableRule extends Rule {
	apply(value) {
		if (value === null || value === '') this.skip('null');
		return value;
	}
}

/**
 * @property {number|null} max
 * @property {number|null} min
 */
class StringRule extends Rule {
	apply(value) {
		if (typeof value !== 'number' && typeof value !== 'string') this.fail('string');

		value = String(value).trim();

		let length = 0;
		for (const _ of value) length++;

		if (this.max != null && length > this.max) this.fail('string_max', { max: this.max });
		if (this.min != null && length < this.min) this.fail('string_min', { min: this.min });

		return value;
	}
}

/**
 * @property {number|null} max
 * @property {number|null} min
 */
class NumberRule extends Rule {
	apply(value) {
		if (typeof value === 'string') {
			if (value.trim() === '' || !Number.isFinite(Number(value))) this.fail('numeric');
		} else if (typeof value !== 'number' || !Number.isFinite(value)) {
			this.fail('numeric');
		}

		value = Number(value);

		if (this.max != null && value > this.max) this.fail('numeric_max', { max: this.max });
		if (this.min != null && value < this.min) this.fail('numeric_min', { min: this.min });

		return value;
	}
}

/**
 * @property {number|null} max
 * @property {number|null} min
 */
class IntRule extends Rule {
	apply(value) {
		if (typeof value === 'string') {
			if (value.trim() === '' || !Number.isFinite(Number(value))) this.fail('integer');
		} else if (typeof value !== 'number' || !Number.isInteger(value)) {
			this.fail('integer');
		}

		value = parseInt(value, 10);

		if (this.max != null && value > this.max) this.fail('numeric_max', { max: this.max });
		if (this.min != null && value < this.min) this.fail('numeric_min', { min: this.min });

		return value;
	}
}

class BooleanRule extends Rule {
	apply(value) {
		if (typeof value === 'boolean') return value;

		if (typeof value === 'string') {
			const lower = value.toLowerCase();
			if (lower === 'true' || lower === '1') return true;
			if (lower === 'false' || lower === '0') return false;
		}

		if (value === 0 || value === 1) return Boolean(value);

		this.fail('bool');
	}
}

/**
 * @property {RegExp|string} pattern
 */
class RegexRule extends Rule {
	apply(value) {
		if (typeof value !== 'string' && typeof value !== 'number') this.fail('format');

		const pattern = this.pattern instanceof RegExp ? this.pattern : new RegExp(this.pattern);
		if (!pattern.test(String(value))) this.fail('format');

		return value;
	}
}

class EmailRule extends Rule {
	static MAX_LENGTH = 250;
	static #REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

	apply(value) {
		if (typeof value !== 'string') this.fail('string');

		let length = 0;
		for (const _ of value) length++;

		if (length > EmailRule.MAX_LENGTH) this.fail('string_max', { max: EmailRule.MAX_LENGTH });
		if (!EmailRule.#REGEX.test(value)) this.fail('email');

		return value;
	}
}

/**
 * @property {number|null} max
 * @property {number|null} min
 * @property {boolean} distinct
 */
class ArrayRule extends Rule {
	apply(value) {
		if (!Array.isArray(value)) this.fail('array');

		const len = value.length;

		if (this.max != null && len > this.max) this.fail('array_max', { max: this.max });
		if (this.min != null && len < this.min) this.fail('array_min', { min: this.min });

		if (this.distinct) {
			const seen = new Set();
			for (let i = 0; i < len; i++) {
				if (seen.has(value[i])) this.fail('distinct');
				seen.add(value[i]);
			}
		}

		return value;
	}
}

/**
 * @property {Array<*>} values
 */
class InArrayRule extends Rule {
	apply(value) {
		const values = this.values;
		for (let i = 0; i < values.length; i++) {
			if (values[i] === value) return value;
		}
		this.fail('enum');
	}
}

/**
 * @property {Array<string|number>} values
 * @property {boolean} multiple
 * @property {boolean} is_integer
 */
class EnumRule extends Rule {
	apply(value) {
		if (value === null) this.fail('invalid');

		let arr;
		if (typeof value === 'string' && value !== '') arr = value.split(',');
		else if (Array.isArray(value)) arr = value;
		else this.fail('invalid');

		const out = [];
		for (let i = 0; i < arr.length; i++) {
			const raw = arr[i];
			const v = this.is_integer ? parseInt(raw, 10) : String(raw).trim();
			if (this.is_integer) {
				if (Number.isFinite(v) && v > 0) out.push(v);
			} else {
				if (v !== '') out.push(v);
			}
		}

		if (out.length > 1 && !this.multiple) this.fail('invalid');

		const allowed = this.values;
		for (let i = 0; i < out.length; i++) {
			let found = false;
			for (let j = 0; j < allowed.length; j++) {
				if (allowed[j] === out[i]) { found = true; break; }
			}
			if (!found) this.fail('enum');
		}

		return this.multiple ? out : out[0];
	}
}

/**
 * @property {Record<string, Array<Rule|typeof Rule>>} rules
 * @property {Record<string, RuleMessages>} attributes
 */
class EachRule extends Rule {
	apply(value) {
		if (!Array.isArray(value)) this.fail('array');

		for (let i = 0; i < value.length; i++) {
			const item = value[i];
			const data = {};
			const rules = {};
			const messages = {};

			for (const field in this.rules) {
				data[field] = item != null ? item[field] : undefined;
				rules[field] = this.rules[field] ?? [NullableRule];
				messages[field] = { ...(this.attributes?.[field] ?? {}) };

				if (messages[field][Rule.ATTRIBUTE]) {
					messages[field][Rule.ATTRIBUTE] += ' #' + (i + 1);
				} else {
					messages[field][Rule.ATTRIBUTE] = '#' + (i + 1);
				}
			}

			const validator = Validator.make(data, rules, messages);

			if (!validator.validate()) {
				const errors = validator.getErrors();
				for (const key in errors) {
					if (key in this.rules) this.fail(errors[key][0]);
				}
				this.fail('invalid');
			}

			const validated = validator.validated();
			for (const field in this.rules) {
				if (item != null) item[field] = validated[field];
			}
		}

		return value;
	}
}

class DateRule extends Rule {
	apply(value) {
		if (typeof value !== 'string') this.fail('date');

		const ts = Date.parse(value);
		if (Number.isNaN(ts)) this.fail('date');

		return Math.floor(ts / 1000);
	}
}

class TimeRule extends Rule {
	static #RX_HHMM = /^(\d\d?):?(\d\d)$/;
	static #RX_HHMM_PM = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
	static #RX_HH_PM = /^(\d{1,2})\s*(AM|PM)$/i;

	apply(value) {
		if (typeof value !== 'string') this.fail('time');

		let h, m, match;

		if ((match = value.match(TimeRule.#RX_HHMM))) {
			h = +match[1]; m = +match[2];
		} else if ((match = value.match(TimeRule.#RX_HHMM_PM))) {
			h = +match[1] % 12; m = +match[2];
			if (match[3].toUpperCase() === 'PM') h += 12;
		} else if ((match = value.match(TimeRule.#RX_HH_PM))) {
			h = +match[1] % 12; m = 0;
			if (match[2].toUpperCase() === 'PM') h += 12;
		} else {
			this.fail('time');
		}

		if (h < 0 || h > 23 || m < 0 || m > 59) this.fail('time');

		return h * 3600 + m * 60;
	}
}

/**
 * @property {object|null} schema
 */
class JsonRule extends Rule {
	static MAX_DEPTH = 32;

	apply(value) {
		let json = null;

		if (typeof value === 'string') {
			try { json = JSON.parse(value); }
			catch (e) { this.fail('json'); }
		} else if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
			json = value;
		}

		if (json === null) this.fail('json');

		if (this.schema && !JsonRule.isValidJson(json, this.schema)) this.fail('format');

		return json;
	}

	static isValidJson(data, schema, path = '') {
		return JsonRule.validateJson(data, schema, path).length === 0;
	}

	static validateJson(data, schema, path = '') {
		const errors = [];
		const type = schema.type ?? null;

		if (type && !JsonRule._checkType(data, type)) {
			errors.push(`${path}: expected ${type}`);
			return errors;
		}

		if (type === 'object') {
			const required = schema.required ?? [];
			const properties = schema.properties ?? {};

			for (let i = 0; i < required.length; i++) {
				if (!(required[i] in data)) errors.push(`${path}.${required[i]} is required`);
			}

			for (const key in properties) {
				if (key in data) {
					const sub = JsonRule.validateJson(data[key], properties[key], `${path}.${key}`);
					for (let i = 0; i < sub.length; i++) errors.push(sub[i]);
				}
			}
		}

		if (type === 'array' && schema.items) {
			for (let i = 0; i < data.length; i++) {
				const sub = JsonRule.validateJson(data[i], schema.items, `${path}[${i}]`);
				for (let j = 0; j < sub.length; j++) errors.push(sub[j]);
			}
		}

		return errors;
	}

	static _checkType(value, type) {
		if (type === 'any') return true;
		if (type === 'string') return typeof value === 'string';
		if (type === 'integer') return Number.isInteger(value);
		if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
		if (type === 'boolean') return typeof value === 'boolean';
		if (type === 'array') return Array.isArray(value);
		if (type === 'object') return value !== null && typeof value === 'object' && !Array.isArray(value);
		if (type === 'null') return value === null;
		return false;
	}
}

/**
 * @property {boolean} strict
 */
class IdRule extends Rule {
	static MAX_ID = 999999999;

	constructor(params = {}) {
		super(params);
		this.strict = this.strict ?? true;
	}

	apply(value) {
		if (typeof value === 'string') {
			if (value.trim() !== '' && Number.isFinite(Number(value))) {
				value = parseInt(value, 10);
			} else if (!this.strict) {
				const parsed = parseInt(value, 10);
				if (parsed > 0) value = parsed;
			}
		}

		if (!IdRule.isId(value)) this.fail('id');
		return value;
	}

	static isId(id) {
		return Number.isInteger(id) && id > 0 && id < IdRule.MAX_ID;
	}
}

/**
 * @property {number} max
 * @property {boolean} strict
 */
class IdsRule extends Rule {
	constructor(params = {}) {
		super(params);
		this.max = this.max ?? 999;
		this.strict = this.strict ?? true;
	}

	apply(value) {
		let arr;

		if (typeof value === 'string') {
			const parts = value.split(',');
			arr = new Array(parts.length);
			for (let i = 0; i < parts.length; i++) {
				const p = parts[i];
				if (Number.isFinite(Number(p)) || !this.strict) arr[i] = parseInt(p, 10);
				else arr[i] = 0;
			}
		} else if (Array.isArray(value)) {
			arr = new Array(value.length);
			for (let i = 0; i < value.length; i++) {
				const v = value[i];
				if (typeof v === 'string' && Number.isFinite(Number(v))) {
					arr[i] = parseInt(v, 10);
				} else if (!this.strict && typeof v === 'string' && parseInt(v, 10) > 0) {
					arr[i] = parseInt(v, 10);
				} else {
					arr[i] = v;
				}
			}
		} else {
			this.fail('invalid');
		}

		if (arr.length > this.max) this.fail('array_max', { max: this.max });

		for (let i = 0; i < arr.length; i++) {
			if (!IdRule.isId(arr[i])) this.fail('id');
		}

		const seen = new Set();
		const out = [];
		for (let i = 0; i < arr.length; i++) {
			if (!seen.has(arr[i])) { seen.add(arr[i]); out.push(arr[i]); }
		}
		return out;
	}
}

class UrlRule extends Rule {
	apply(value) {
		try {
			const url = new URL(value);

			if (this.https_only && url.protocol !== 'https:') {
				this.fail('url_https_only');
			}
		} catch (e) {

			if (e instanceof FailException) {
				throw e;
			}

			this.fail('url');
		}
	}
}
;class Validator {
	/**
	 * @param {Record<string, any>} data
	 * @param {Record<string, Array<Rule|typeof Rule>>} rules
	 * @param {Record<string, RuleMessages>} [messages]
	 */
	constructor(data, rules, messages = {}) {
		this.data = data;
		this.rules = rules;
		this.messages = messages;
		/** @type {Record<string, string[]>} */
		this.errors = {};
		this._validated = false;
		/** @type {Record<string, any>|null} */
		this._validatedData = null;
	}

	static RULE_REGISTRY = Object.freeze({
		required: RequiredRule,
		nullable: NullableRule,
		string: StringRule,
		number: NumberRule,
		int: IntRule,
		boolean: BooleanRule,
		regex: RegexRule,
		email: EmailRule,
		array: ArrayRule,
		inarray: InArrayRule,
		enum: EnumRule,
		each: EachRule,
		date: DateRule,
		time: TimeRule,
		json: JsonRule,
		id: IdRule,
		ids: IdsRule,
		url: UrlRule,
	});

	/**
	 * @param {Record<string, any>} data
	 * @param {ValidationSchema} schema
	 * @param {Record<string, typeof Rule>} [registry]
	 */
	static fromSchema(data, schema, registry = {}) {
		const rules = Object.create(null);
		for (const field in schema.rules) {
			const specs = schema.rules[field];
			const list = [];
			for (let i = 0; i < specs.length; i++) {
				const spec = specs[i];
				const Ctor = registry[spec.rule] ?? Validator.RULE_REGISTRY[spec.rule];
				if (!Ctor) continue;
				list.push(new Ctor(spec.params || {}));
			}
			rules[field] = list;
		}
		return new Validator(data, rules, schema.messages || {});
	}

	/**
	 * @param {Record<string, any>} data
	 * @param {Record<string, Array<Rule|typeof Rule>>} rules
	 * @param {Record<string, RuleMessages>} [messages]
	 */
	static make(data, rules, messages = {}) {
		return new this(data, rules, messages);
	}

	validate(throwOnFail = false) {
		const validated = {};

		outer: for (const field in this.rules) {
			const fieldRules = this.rules[field];
			let value = this.data[field] ?? null;

			for (let i = 0; i < fieldRules.length; i++) {
				const rule = this._buildRule(fieldRules[i]);
				const messagesOverride = this.messages[field] ?? null;
				if (messagesOverride) rule.setMessages(messagesOverride);

				try {
					value = rule.apply(value);
				} catch (e) {
					if (e instanceof SkipException) {
						validated[field] = value;
						continue outer;
					}
					if (e instanceof FailException) {
						this.errors[field] ??= [];
						this.errors[field].push(e.message);
						continue outer;
					}
					throw e;
				}
			}

			validated[field] = value;
		}

		this._validated = true;
		this._validatedData = validated;

		if (throwOnFail && this.fails()) {
			throw new ValidationException(this.errors);
		}

		return !this.fails();
	}

	fails() {
		this._ensureValidated();
		return Object.keys(this.errors).length > 0;
	}

	/**
	 * @param {string} [field]
	 * @return {string[]|Record<string, string[]>}
	 */
	getErrors(field) {
		this._ensureValidated();
		return field ? (this.errors[field] ?? []) : this.errors;
	}

	validated() {
		this._ensureValidated();
		const result = {};
		for (const field in this._validatedData) {
			if (this.errors[field] === undefined) {
				result[field] = this._validatedData[field];
			}
		}
		return result;
	}

	getData() {
		return this.data;
	}

	_buildRule(rule) {
		if (rule instanceof Rule) return rule;
		if (typeof rule === 'function' && rule.prototype instanceof Rule) {
			return new rule();
		}
		throw new Error('Invalid rule provided.');
	}

	_ensureValidated() {
		if (!this._validated) {
			throw new Error('You must call validate() before accessing this function.');
		}
	}
}
;const deferred = () => {
	let resolve, reject;
	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return {promise, resolve, reject};
};
const once = (fn) => {
	let fired = false;
	return (...a) => {
		if (fired) return;
		fired = true;
		return fn(...a);
	};
};

class Step {
	constructor(name, {mount, run, unmount, rollback, condition}) {
		this.name = name;
		this.mount = mount || (({resolve}) => resolve());
		this.run = run || (() => ({}));
		this.unmount = unmount || (() => {
		});
		this.rollback = rollback || (() => {
		});
		this.condition = condition || (() => true);
	}
}

class AbstractWorkflow {
	constructor(initial = {}) {
		this.ctx = initial;
		this.steps = this.define();
		this.executed = [];
	}

	define() {
		throw new Error("Method 'define()' must be implemented.");
	}


	static run(initial = {}) {
		const workflow = new this(initial);
		return workflow.run();
	}

	async run() {
		try {
			for (const step of this.steps) {
				this.executed.push(step);
				if (!(step.condition({ctx: this.ctx}))) {
					console.debug(`Skipping step: ${step.name}`);
					continue;
				}

				const d = deferred();

				const control = {
					resolve: once((val) => {
						if (typeof val !== 'undefined') this.ctx[step.name] = val;
						d.resolve(val);
					}),
					reject: once((err) => d.reject(err || new Error('Step rejected'))),
				};

				const mountedResult = await new Promise((res, rej) => {
					step.mount({ctx: this.ctx, resolve: res, reject: control.reject, control});
				});

				step.run({ctx: this.ctx, mountedResult, resolve: control.resolve, reject: control.reject, control});

				await d.promise;

				await step.unmount({ctx: this.ctx, control});
			}
			return this.ctx;
		} catch (err) {
			await this.rollback(err);
			console.debug('Workflow failed:', err);
		}
	}

	async rollback(err) {
		console.debug("Rollback workflow due to error:", err);
		for (let i = this.executed.length - 1; i >= 0; i--) {
			const step = this.executed[i];
			try {
				await step.rollback({ctx: this.ctx, error: err});
				delete this.ctx[step.name];
			} catch (e) {
				console.debug(`Error during rollback of step ${step.name}:`, e);
			}
		}
	}
}


App.classes.workflows.step = Step;
App.classes.workflows.workflow = AbstractWorkflow;
;class ExportReportWorkflow extends AbstractWorkflow {
	define() {
		return [
			new Step("url", {
				condition({ctx}) {
					return !ctx.url;
				},
				run({resolve}) {
					resolve(App.helper.endpoint.ajax())
				}
			}),
			new Step("data", {
				condition({ctx}) {
					return !ctx.format || !ctx.type
				},
				mount({resolve, reject, ctx}) {
					const urlParams = new URLSearchParams({
						specials: ctx.special_formats ||  []
					});
					App.modal.show({
						url: ctx.url + '?' + App.actions.get('export_action:format') + '&' + urlParams.toString(),
						title: App.object_state.locale.export.do,
						group: 'white',
						width: 'auto',
						onload: function (modal) {
							const channel = new ComponentChannel(modal);
							channel.provide(
								'deliveryMessage',
								() => ctx.delivery === 'email' ? App.i18n.t('send') : App.i18n.t('download')
							);
							resolve(modal);
						},
						onhide: function () {
							reject(new Error("Export format selection was cancelled by user."));
						}
					});
				},
				run({mountedResult, resolve}) {
					mountedResult.addDelegatedEventListener('submit', 'ws-form', function (event) {
						event.preventDefault();
						resolve(event.detail.values);
					});
				},
				unmount() {
					App.modal.hide({immediate: true});
				},
				rollback() {
					App.modal.hide({immediate: true});
				}
			}),
			new Step("recipients", {
				condition({ctx}) {
					return ctx.delivery === 'email';
				},
				mount({ctx, resolve, reject}) {
					App.modal.show({
						url: ctx.url + '?' + App.actions.get('export_action:recipient'),
						width: 450,
						title: App.object_state.locale.export.do_mail,
						group: 'white',
						onload: function (modal) {
							App.mass_operations.init_search();
							resolve(modal);
						},
						onhide: function () {
							reject(new Error("Export format selection was cancelled by user."));
						}
					});
				},
				run({mountedResult, resolve, reject}) {
					const submitBtn = mountedResult.querySelector('ws-btn[data-role="submit"]');
					let user_id;
					mountedResult.addDelegatedEventListener('click', '.menu__item[data-id]', function (event, target) {
						App.controls.menu.item_set_active(target);
						user_id = target.getAttribute('data-id');
						submitBtn.toggle(user_id)
					});
					submitBtn.addEventListener('click', function () {
						resolve([user_id]);
					})
				},
				rollback() {
					App.modal.hide({immediate: true});
				}
			}),
			new Step("send", {
				run({ctx, resolve}) {
					const body = {
						'page': ctx.page,
						...ctx.data,
						'delivery': ctx.delivery,
						'recipients': ctx.recipients,
						'meta': {
							...(ctx.meta || {}),
							'ids': ctx.ids,
						}
					};
					const url = ctx.url + '?' + App.actions.get(`export_action:do`);
					switch (ctx.delivery) {
						case 'download':
							App.core.download_file_with_loader(
								url,
								App.helper.object.toFormUrlEncoded(body)
							);
							break;
						case 'email':
							$.ajax({
								url: url,
								method: 'POST',
								data: body,
								success: function () {
									App.modal.hide({immediate: true});
								}
							});
							break;
					}
					resolve();
				}
			})
		];
	}
}

App.classes.workflows.export_report = ExportReportWorkflow;
